/* eslint-disable typescript-extended-rules/file-naming-convention */
import { HttpResponse, HttpStatusCode } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';

import {
	EMPTY,
	Observable,
	Subject,
	Subscription,
	firstValueFrom,
	of,
	timer,
} from 'rxjs';
import {
	catchError,
	debounceTime,
	distinctUntilChanged,
	finalize,
	switchMap,
	takeUntil,
} from 'rxjs/operators';

import { AppConstants } from '../../../../app.constants';
import { BeneficiaryReconciliationsService } from '../../../../modules/beneficiary-reconciliations/beneficiary-reconciliations.service';
import {
	BeneficiaryReconciliationApplyAction,
	BeneficiaryReconciliationApplyRequest,
	BeneficiaryReconciliationApplyResponse,
	BeneficiaryReconciliationApplyTargetValue,
	BeneficiaryReconciliationActiveProcessingResponse,
	BeneficiaryReconciliationComplementResponse,
	BeneficiaryReconciliationDraft,
	BeneficiaryReconciliationDuplicateReturnResponse,
	BeneficiaryReconciliationField,
	BeneficiaryReconciliationItem,
	BeneficiaryReconciliationItemsResponse,
	BeneficiaryReconciliationItemStatus,
	BeneficiaryReconciliationItemStatusFilter,
	BeneficiaryReconciliationMappingAnalysisResponse,
	BeneficiaryReconciliationMappingConfirmation,
	BeneficiaryReconciliationProcessingStartResponse,
	BeneficiaryReconciliationProcessingType,
	BeneficiaryReconciliationReviewState,
	BeneficiaryReconciliationScopeMode,
	BeneficiaryReconciliationSessionListItem,
	BeneficiaryReconciliationSessionProgressStatus,
	BeneficiaryReconciliationSummary,
	BeneficiaryReconciliationTitularityImportResponse,
	BradescoSpgProductValidationResult,
	EMPTY_BENEFICIARY_RECONCILIATION_SUMMARY,
	GenericBeneficiaryReconciliationImportModel,
	GenericBeneficiaryReconciliationPreflightExportKind,
	GenericBeneficiaryReconciliationPreflightListStatus,
	GenericBeneficiaryReconciliationPreflightRow,
	GenericBeneficiaryReconciliationPreflightSummary,
	GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup,
	GenericBeneficiaryReconciliationUnknownCnpjAlert,
	GenericBeneficiaryReconciliationWalletKind,
} from '../../../../modules/beneficiary-reconciliations/models/beneficiary-reconciliation.model';
import { BeneficiaryReconciliationApplyPreviewResponse } from '../../../../modules/beneficiary-reconciliations/models/beneficiary-reconciliation-apply-preview-response.model';
import { BeneficiaryReconciliationBulkCardActionRequest } from '../../../../modules/beneficiary-reconciliations/models/beneficiary-reconciliation-bulk-card-action-request.model';
import { BeneficiaryReconciliationListItemsArgs } from '../../../../modules/beneficiary-reconciliations/models/beneficiary-reconciliation-list-items-args.model';
import { ReconciliationScopeOption } from '../../../../modules/beneficiary-reconciliations/models/beneficiary-reconciliation-scope-option.model';
import { OrganizationsService } from '../../../../modules/organizations/organizations.service';
import {
	ConfirmationDialogComponent,
	ConfirmationDialogData,
} from '../../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { SnackBarService } from '../../../../shared/components/snack-bar/snack-bar.service';
import { CpfUtils } from '../../../../utils/cpf-utils';

import { BeneficiaryReconciliationApplyPreviewModalComponent } from './beneficiary-reconciliation-apply-preview-modal/beneficiary-reconciliation-apply-preview-modal.component';
import {
	BENEFICIARY_RECONCILIATION_FIELD_METADATA,
	getBeneficiaryReconciliationFieldDescription,
	getBeneficiaryReconciliationFieldLabel,
} from './beneficiary-reconciliation-field-metadata';
import { BeneficiaryReconciliationHistoryDetailModalComponent } from './beneficiary-reconciliation-history-detail-modal/beneficiary-reconciliation-history-detail-modal.component';
import {
	BeneficiaryReconciliationInlineResolutionModalComponent,
	BeneficiaryReconciliationInlineResolutionResult,
} from './beneficiary-reconciliation-inline-resolution-modal/beneficiary-reconciliation-inline-resolution-modal.component';
import { BeneficiaryReconciliationMappingModalComponent } from './beneficiary-reconciliation-mapping-modal/beneficiary-reconciliation-mapping-modal.component';
import {
	BeneficiaryReconciliationPreflightOrganizationModalComponent,
	BeneficiaryReconciliationPreflightOrganizationResult,
} from './beneficiary-reconciliation-preflight-organization-modal/beneficiary-reconciliation-preflight-organization-modal.component';
import {
	BeneficiaryReconciliationPreflightRowModalComponent,
	BeneficiaryReconciliationPreflightRowResult,
} from './beneficiary-reconciliation-preflight-row-modal/beneficiary-reconciliation-preflight-row-modal.component';
import {
	BeneficiaryReconciliationTitularityResolutionModalComponent,
	BeneficiaryReconciliationTitularityResolutionResult,
} from './beneficiary-reconciliation-titularity-resolution-modal/beneficiary-reconciliation-titularity-resolution-modal.component';
import {
	BeneficiaryReconciliationValueResolutionModalComponent,
	BeneficiaryReconciliationValueResolutionResult,
} from './beneficiary-reconciliation-value-resolution-modal/beneficiary-reconciliation-value-resolution-modal.component';

type ItemActionMode = 'AUTO_APPLY' | 'MANUAL_APPLY' | 'REVIEW_ONLY';
type GenericUploadFileField = 'dentalFile' | 'healthFile';
type ReconciliationUploadMode = 'FLEXIBLE' | 'STANDARD';
const NETWORK_ERROR_STATUS = 0;
const TRANSIENT_TRANSPORT_STATUS_CODES = new Set<number>([
	NETWORK_ERROR_STATUS,
	HttpStatusCode.BadGateway,
	HttpStatusCode.ServiceUnavailable,
	HttpStatusCode.GatewayTimeout,
]);
type StandardImportModel = Extract<
	GenericBeneficiaryReconciliationImportModel,
	'AMIL' | 'BRADESCO_PROVIDER' | 'BRADESCO_SPG' | 'SANUS_STANDARD'
>;
type ReconciliationOriginalFile =
	GenericBeneficiaryReconciliationPreflightSummary['files'][number];
interface GenericPreflightCreationResponse {
	preflight: GenericBeneficiaryReconciliationPreflightSummary;
	sessionId: string;
}
type PreflightPendingAction =
	| 'CREATE_SUB'
	| 'RESOLVE_PREFLIGHT_ROW'
	| 'RESUME_PREFLIGHT_ROW'
	| 'TREAT_AS_MATRIX';
type PendingChangeAction =
	| BeneficiaryReconciliationApplyAction
	| PreflightPendingAction;

const RECONCILIATION_DRAFT_SAVE_DEBOUNCE_MS = 500;
const RECONCILIATION_PROCESSING_POLLING_INTERVAL_MS = 5000;
const RECONCILIATION_PROCESSING_POLLING_RECOVERABLE_FAILURE_LIMIT = 3;
const RECONCILIATION_RESOLUTION_MODE_KEEP_PLATFORM = 'KEEP_PLATFORM';
const RECONCILIATION_OPERATIONAL_ACTION_IGNORE = 'IGNORE';

interface QueryState {
	carrier: string;
	comparisonId: string | null;
	field: string;
	limit: number;
	page: number;
	search: string;
	status: BeneficiaryReconciliationItemStatusFilter;
}

interface PendingItemChange {
	action: PendingChangeAction;
	actionLabel?: string;
	field?: BeneficiaryReconciliationField;
	itemId: string;
	preflightKind?: GenericBeneficiaryReconciliationPreflightExportKind;
	targetValue: BeneficiaryReconciliationApplyTargetValue;
}

interface PendingActionSummary {
	action: PendingChangeAction;
	count: number;
	label: string;
}

interface PendingSessionAudit {
	autoSuggestedCount: number;
	manualReviewedCount: number;
	manualUnreviewedCount: number;
	manualUnreviewedItemIds: string[];
	summaries: PendingActionSummary[];
	total: number;
}

interface BulkCardActionAvailability {
	failedCount: number;
	pendingHoldCount: number;
	queuedCount: number;
}

interface ProcessingApplyContext {
	fields?: BeneficiaryReconciliationField[];
	itemIds?: string[];
}

type ReconciliationCardKey =
	| BeneficiaryReconciliationField
	| GenericBeneficiaryReconciliationPreflightExportKind;
type ReconciliationCategoryValue = '' | ReconciliationCardKey;

interface ReconciliationCard {
	count: number;
	description: string;
	field?: BeneficiaryReconciliationField;
	key: ReconciliationCardKey;
	label: string;
	onHoldCount?: number;
	pendingCount?: number;
	preflightKind?: GenericBeneficiaryReconciliationPreflightExportKind;
}

/* eslint-disable perfectionist/sort-classes */
@Component({
	selector: 'app-beneficiary-reconciliation',
	styleUrls: [
		'./beneficiary-reconciliation.component.scss',
		'./beneficiary-reconciliation-table.component.scss',
	],
	templateUrl: './beneficiary-reconciliation.component.html',
})
export class BeneficiaryReconciliationComponent implements OnInit, OnDestroy {
	private static readonly BRADESCO_SPG_PRODUCT_WARNING_MESSAGE =
		'A planilha contém produtos que parecem ser de outro tipo. Se continuar, todos os registros serão importados como o tipo selecionado nesta tela, independentemente do produto informado na planilha. Confira se você selecionou Saúde ou Dental corretamente. Deseja continuar?';

	private readonly applyButtonLabels: Record<
		BeneficiaryReconciliationField,
		string
	> = {
		ACTIVE_MISSING_IN_DB: 'Criar beneficiários',
		CARD_MISMATCH: 'Atualizar carteirinhas',
		DUPLICATE_IN_FILE: 'Processar Duplicidades',
		INACTIVE_MISSING_IN_FILE: 'Tratar beneficiários ausentes na planilha',
		ORGANIZATION_MISMATCH: 'Transferir organizações',
		PLAN_MISMATCH: 'Atualizar planos',
		TITULARITY_MISMATCH: 'Atualizar titularidades',
	};
	private readonly reconciliationCardGuidanceTexts: Partial<
		Record<ReconciliationCardKey, string>
	> = {
		ACTIVE_MISSING_IN_DB:
			'Validar as vidas não encontradas na plataforma para confirmar a necessidade de inclusão dos respectivos cadastros.',
		CARD_MISMATCH:
			'Validar as vidas com divergência de carteirinha para confirmar se o dado deve ser alterado na plataforma ou se a informação atualmente cadastrada deve ser mantida.',
		DUPLICATE_IN_FILE:
			'Verificar se há duplicidade na base importada e validar qual cadastro deve ser considerado correto para permanência na plataforma.',
		INACTIVE_MISSING_IN_FILE:
			'Validar as vidas que não foram identificadas na base do cliente para confirmar a necessidade de exclusão dos respectivos cadastros na plataforma.',
		ORGANIZATION_MISMATCH:
			'Validar as vidas com divergência de CNPJ para confirmar a organização correta e realizar a transferência do cadastro na plataforma, quando aplicável.',
		PLAN_MISMATCH:
			'Validar as vidas com divergência de plano para confirmar a informação correta e realizar a alteração do plano na plataforma.',
		TITULARITY_MISMATCH:
			'Caso a mesma pessoa esteja identificada como titular e dependente na base do cliente, manter sempre o cadastro como titular na plataforma, visto que atualmente não é possível manter 2 CPF ativos na mesa organização.\n\nEm caso de troca de titularidade de um dependente que esteja ativo com dois titulares, validar em qual titular o dependente deverá permanecer na plataforma.',
		UNCOMPARED_LINES:
			'Verificar quais dados estão incompletos na base importada e validar as informações necessárias para atualização do cadastro.',
		UNIDENTIFIED_ORGANIZATIONS:
			'Validar a informação de CNPJ na base importada. Caso o CNPJ não esteja cadastrado na plataforma, confirmar a necessidade de realizar o cadastro da organização.',
	};
	private readonly congenerMissingFields = new Set<string>([
		'carteirinhaCongenere',
		'planoCongenere',
	]);
	private readonly conjugatedMissingFields = new Set<string>([
		'carteirinhaConjugado',
		'planoConjugado',
	]);
	private readonly normalDentalMissingFields = new Set<string>([
		'beneficioOdonto',
		'carteirinhaOdonto',
		'dentalBenefit',
		'dentalCardNumber',
		'dentalPlan',
		'dentalPlanName',
		'hasDentalBenefit',
		'planoOdonto',
	]);
	private readonly normalHealthMissingFields = new Set<string>([
		'beneficioSaude',
		'cardNumber',
		'carteirinhaSaude',
		'hasHealthBenefit',
		'healthBenefit',
		'healthCardNumber',
		'healthPlan',
		'healthPlanName',
		'planName',
		'planoSaude',
	]);
	readonly acceptedReconciliationFileAccept = '.xlsx';
	private readonly bradescoReconciliationFileExtensions = ['.xlsx', '.xls'];
	private readonly sanusStandardReconciliationFileExtensions = ['.xlsx'];
	comparisonId: string | null = null;
	readonly cpfMask = CpfUtils.mask;
	readonly fieldOptions: {
		description?: string;
		label: string;
		value: ReconciliationCategoryValue;
	}[] = [
		{ label: 'Todos os casos', value: '' },
		{
			description: this.getFieldDescription('DUPLICATE_IN_FILE'),
			label: 'Duplicado na base do cliente',
			value: 'DUPLICATE_IN_FILE',
		},
		{
			description:
				'Nome, CPF, nascimento ou operadora necessária ausente ou inválida.',
			label: 'Dados incompletos',
			value: 'UNCOMPARED_LINES',
		},
		{
			description:
				'CNPJ ausente, não cadastrado ou fora do contexto da matriz.',
			label: 'Organizações não identificadas',
			value: 'UNIDENTIFIED_ORGANIZATIONS',
		},
		...(
			Object.entries(BENEFICIARY_RECONCILIATION_FIELD_METADATA) as [
				BeneficiaryReconciliationField,
				(typeof BENEFICIARY_RECONCILIATION_FIELD_METADATA)[BeneficiaryReconciliationField],
			][]
		)
			.map(([value, metadata]) => ({
				description: metadata.description,
				label: metadata.label,
				value,
			}))
			.filter((option) => option.value !== 'DUPLICATE_IN_FILE'),
	];
	filterForm: UntypedFormGroup;
	highlightedManualReviewItemIds = new Set<string>();
	activeProcessingId: string | null = null;
	activeProcessingType: BeneficiaryReconciliationProcessingType | null = null;
	isApplying = false;
	isComparing = false;
	isValidatingBradescoSpgProducts = false;

	isLoadingItems = false;
	isLoadingPreflightRows = false;
	isLoadingUnidentifiedOrganizationGroups = false;
	isLoadingSessions = false;
	isReprocessingPreflight = false;
	isExportingDuplicateInFile = false;
	isExportingPreflightPendencies = false;
	isImportingDuplicateReturn = false;
	isImportingPreflightPendencies = false;
	isImportingTitularityReturn = false;
	isComplementingMissingData = false;
	isExportingActiveMissingInDb = false;
	isExportingOperationalDivergence = false;
	isBulkIgnoring = false;
	isBulkIgnoringAll = false;
	isExportingHistorySessionId: string | null = null;
	isDownloadingOriginalFileId: string | null = null;
	isReplacingBase = false;
	readonly itemStatusOptions: {
		label: string;
		value: BeneficiaryReconciliationItemStatusFilter;
	}[] = [
		{
			label: 'Todos os casos',
			value: BeneficiaryReconciliationItemStatusFilter.ALL,
		},
		{
			label: 'Pendentes',
			value: BeneficiaryReconciliationItemStatusFilter.MANUAL_REVIEW_REQUIRED,
		},
		{
			label: 'Prontos para aplicar',
			value: BeneficiaryReconciliationItemStatusFilter.READY_TO_APPLY,
		},
		{
			label: 'Em espera',
			value: BeneficiaryReconciliationItemStatusFilter.PENDING,
		},
		{
			label: 'Aplicados',
			value: BeneficiaryReconciliationItemStatusFilter.APPLIED,
		},
		{
			label: 'Ignorados',
			value: BeneficiaryReconciliationItemStatusFilter.IGNORED,
		},
		{
			label: 'Falharam',
			value: BeneficiaryReconciliationItemStatusFilter.FAILED,
		},
	];
	items: BeneficiaryReconciliationItem[] = [];
	itemsCount = 0;
	itemsCurrentPage = 1;
	itemsTotalPages = 0;
	lastReportUrl: string | null = null;
	loadedSessionAppliedCount = 0;
	readonly modelTemplates = {
		dental: {
			fileName: 'MODELO DENTAL.xlsx',
			url: 'assets/documents/beneficiary-reconciliation/modelo-dental.xlsx',
		},
		health: {
			fileName: 'MODELO SAUDE.xlsx',
			url: 'assets/documents/beneficiary-reconciliation/modelo-saude.xlsx',
		},
	} as const;
	healthFile: File | null = null;
	readonly limitOptions: number[] = [10, 20, 50, 100];
	dentalFile: File | null = null;
	flexibleFiles: File[] = [];
	isAnalyzingMapping = false;
	isConfirmingMapping = false;
	confirmedMapping: BeneficiaryReconciliationMappingConfirmation | null = null;
	mappingAnalysis: BeneficiaryReconciliationMappingAnalysisResponse | null =
		null;
	organizationId = '';
	selectedOrganizationIsMatriz: boolean | null = null;
	readonly scopeModeOptions: ReconciliationScopeOption[] = [
		{ label: 'Somente matriz', value: 'ORGANIZATION_ONLY' },
		{ label: 'Núcleo inteiro', value: 'FULL_GROUP' },
	];
	scopeMode: BeneficiaryReconciliationScopeMode = 'ORGANIZATION_ONLY';
	private createdSessionScopeModeFallback: {
		scopeMode: BeneficiaryReconciliationScopeMode;
		sessionId: string;
	} | null = null;
	private selectedOrganizationContextRequestId = 0;
	standardImportModel: StandardImportModel = 'SANUS_STANDARD';
	uploadMode: ReconciliationUploadMode = 'STANDARD';
	activeCardKey: ReconciliationCardKey = 'DUPLICATE_IN_FILE';
	expandedGuidanceCardKey: ReconciliationCardKey | null = null;
	pendingChanges = new Map<string, PendingItemChange>();
	bulkCardActionAvailability: BulkCardActionAvailability = {
		failedCount: 0,
		pendingHoldCount: 0,
		queuedCount: 0,
	};
	preflightEligibleRows: GenericBeneficiaryReconciliationPreflightRow[] = [];
	preflightOnHoldRows: GenericBeneficiaryReconciliationPreflightRow[] = [];
	preflightPendingRows: GenericBeneficiaryReconciliationPreflightRow[] = [];
	preflightRowCarrierEdits: Record<string, string> = {};
	preflightRowCpfEdits: Record<string, string> = {};
	preflightSummary: GenericBeneficiaryReconciliationPreflightSummary | null =
		null;
	preflightRowsLoadedSessionId: string | null = null;
	selectedHistorySession: BeneficiaryReconciliationSessionListItem | null =
		null;
	unidentifiedOrganizationGroups: GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup[] =
		[];
	unidentifiedOrganizationGroupsLoadedSessionId: string | null = null;
	preflightUnknownCnpjs: GenericBeneficiaryReconciliationUnknownCnpjAlert[] =
		[];
	recentSessions: BeneficiaryReconciliationSessionListItem[] = [];
	readonly sessionLimit = 5;
	sessionPendingTotal = 0;
	sessionsCount = 0;
	sessionsCurrentPage = 1;
	sessionsTotalPages = 0;
	showRecentSessions = false;
	summary: BeneficiaryReconciliationSummary =
		EMPTY_BENEFICIARY_RECONCILIATION_SUMMARY;
	readonly summaryCards: {
		field: BeneficiaryReconciliationField;
		label: string;
	}[] = (
		Object.keys(
			BENEFICIARY_RECONCILIATION_FIELD_METADATA,
		) as BeneficiaryReconciliationField[]
	).map((field) => ({
		field,
		label: getBeneficiaryReconciliationFieldLabel(field),
	}));

	private latestQuerySignature = '';
	private latestItemsRequestId = 0;
	private latestBulkActionAvailabilityRequestId = 0;
	private itemsRequestSignature: string | null = null;
	private itemsRequestSubscription: Subscription | null = null;
	private bulkActionAvailabilityRequestSignature: string | null = null;
	private bulkActionAvailabilityRequestSubscription: Subscription | null = null;
	private readonly activeApplyProcessingStoragePrefix =
		'beneficiary-reconciliation:active-apply:';
	private readonly consumedTerminalProcessingStoragePrefix =
		'beneficiary-reconciliation:consumed-processing:';
	private readonly hiddenPersistedOriginalFileKeys = new Set<string>();
	private readonly pendingChangesStoragePrefix =
		'beneficiary-reconciliation:pending:';
	private activeApplyContext: ProcessingApplyContext | null = null;
	private unknownApplyContext: ProcessingApplyContext | null = null;
	private unknownApplySessionId: string | null = null;
	private isVerifyingUnknownApplyResult = false;
	private activeProcessingLoadedOrganizationId: string | null = null;
	private readonly remoteDraftSave$ = new Subject<void>();
	private remoteDraftPersistenceInitialized = false;
	private processingPollSubscription: Subscription | null = null;
	private processingPollingConsecutiveFailures = 0;
	processingErrorMessage: string | null = null;
	private readonly unsubscribe$ = new Subject<void>();

	constructor(
		private readonly activatedRoute: ActivatedRoute,
		private readonly beneficiaryReconciliationsService: BeneficiaryReconciliationsService,
		private readonly dialog: MatDialog,
		private readonly fb: UntypedFormBuilder,
		private readonly organizationsService: OrganizationsService,
		private readonly router: Router,
		private readonly snackBarService: SnackBarService,
	) {
		this.filterForm = this.fb.group({
			carrier: [''],
			field: [''],
			limit: [AppConstants.pagination.itemsPerPage],
			search: [''],
			status: [''],
		});
	}

	get autoSuggestedCount(): number {
		return Math.max(this.pendingChangesCount - this.manualReviewedCount, 0);
	}

	get canCompare(): boolean {
		if (this.selectedOrganizationIsMatriz === null) {
			return false;
		}

		if (this.isBaseReplacementMode) {
			return false;
		}

		if (this.isFlexibleUploadMode) {
			return this.flexibleFiles.length > 0;
		}

		if (this.comparisonId || this.preflightSummary) {
			return false;
		}

		return Boolean(this.healthFile || this.dentalFile);
	}

	get hasPendingBaseReplacement(): boolean {
		return Boolean(
			this.healthFile ||
				this.dentalFile ||
				this.hiddenPersistedOriginalFileKeys.size > 0,
		);
	}

	get canReplaceBase(): boolean {
		const hasBaseAfterReplacement = Boolean(
			this.healthFile ||
				this.dentalFile ||
				this.persistedHealthFile ||
				this.persistedDentalFile,
		);

		return (
			Boolean(this.comparisonId && this.preflightSummary) &&
			this.isStandardUploadMode &&
			this.currentAppliedCount === 0 &&
			!this.isProcessingReconciliation &&
			!this.isReplacingBase &&
			this.hasPendingBaseReplacement &&
			hasBaseAfterReplacement
		);
	}

	get isBaseReplacementMode(): boolean {
		return Boolean(
			this.comparisonId && this.preflightSummary && this.isStandardUploadMode,
		);
	}

	get hasAppliedItemsInCurrentSession(): boolean {
		return this.currentAppliedCount > 0;
	}

	get currentAppliedCount(): number {
		const selectedAppliedCount = this.selectedHistorySession?.appliedCount ?? 0;
		const loadedItemsAppliedCount = this.items.filter(
			(item) => item.status === BeneficiaryReconciliationItemStatus.APPLIED,
		).length;

		return Math.max(
			selectedAppliedCount,
			this.loadedSessionAppliedCount,
			loadedItemsAppliedCount,
		);
	}

	get hasPersistedOriginalFiles(): boolean {
		return this.persistedOriginalFiles.length > 0;
	}

	get persistedDentalFile(): ReconciliationOriginalFile | null {
		return this.getPersistedOriginalFileByWalletKind('DENTAL');
	}

	get persistedFlexibleFiles(): ReconciliationOriginalFile[] {
		if (this.preflightSummary?.importModel !== 'ASSISTED_MAPPING') {
			return [];
		}

		return this.persistedOriginalFiles;
	}

	get persistedHealthFile(): ReconciliationOriginalFile | null {
		return this.getPersistedOriginalFileByWalletKind('HEALTH');
	}

	get persistedOriginalFiles(): ReconciliationOriginalFile[] {
		return (this.preflightSummary?.files ?? []).filter(
			(file) =>
				!this.hiddenPersistedOriginalFileKeys.has(
					this.getOriginalFileKey(file),
				),
		);
	}

	get replaceBaseDisabledReason(): string {
		if (this.hasAppliedItemsInCurrentSession) {
			return 'Já existem itens aplicados nesta sessão. Inicie uma nova reconciliação.';
		}

		if (this.isApplyProcessing) {
			return 'Há uma aplicação em andamento nesta sessão. Aguarde a conclusão para substituir a base.';
		}

		if (this.isProcessingReconciliation) {
			return 'Há um processamento em andamento nesta sessão. Aguarde a conclusão para substituir a base.';
		}

		if (this.preflightSummary?.importModel === 'ASSISTED_MAPPING') {
			return 'Substituição de layout flexível exige novo mapeamento.';
		}

		return 'Selecione uma nova base Saúde ou Dental para substituir.';
	}

	get canRunGenericCompare(): boolean {
		return (
			this.selectedOrganizationIsMatriz !== null &&
			Boolean(this.comparisonId && this.preflightSummary) &&
			!this.hasComparisonLoaded &&
			!this.isComparing &&
			!this.isProcessingReconciliation
		);
	}

	get canShowHistoryExportButton(): boolean {
		return Boolean(this.organizationId);
	}

	canExportHistorySession(
		session: BeneficiaryReconciliationSessionListItem,
	): boolean {
		return session.total > 0 && !this.isExportingHistory(session.id);
	}

	get hasPreflightLoaded(): boolean {
		return Boolean(this.preflightSummary);
	}

	get hasComparisonLoaded(): boolean {
		return (
			Boolean(this.comparisonId) &&
			(this.itemsCount > 0 ||
				Object.values(this.summary).some((count) => count > 0))
		);
	}

	get hasReconciliationLoaded(): boolean {
		return Boolean(this.comparisonId);
	}

	get isProcessingReconciliation(): boolean {
		return Boolean(this.activeProcessingId);
	}

	get isApplyProcessing(): boolean {
		return (
			this.isProcessingReconciliation && this.activeProcessingType === 'APPLY'
		);
	}

	get isCompareProcessing(): boolean {
		return (
			this.isProcessingReconciliation && this.activeProcessingType === 'COMPARE'
		);
	}

	get isFlexibleUploadMode(): boolean {
		return this.uploadMode === 'FLEXIBLE';
	}

	get isStandardUploadMode(): boolean {
		return this.uploadMode === 'STANDARD';
	}

	get isScopeModeSelectionLocked(): boolean {
		return Boolean(this.comparisonId || this.preflightSummary);
	}

	get standardUploadFileAccept(): string {
		return this.getAcceptedStandardFileExtensions().join(',');
	}

	get isBradescoProviderStandardImport(): boolean {
		return this.standardImportModel === 'BRADESCO_PROVIDER';
	}

	get isAmilStandardImport(): boolean {
		return this.standardImportModel === 'AMIL';
	}

	get isBradescoSpgStandardImport(): boolean {
		return this.standardImportModel === 'BRADESCO_SPG';
	}

	get isBradescoStandardImport(): boolean {
		return (
			this.isBradescoProviderStandardImport || this.isBradescoSpgStandardImport
		);
	}

	get canSelectScopeMode(): boolean {
		return this.selectedOrganizationIsMatriz === true;
	}

	get effectiveScopeMode(): BeneficiaryReconciliationScopeMode {
		return this.canSelectScopeMode ? this.scopeMode : 'ORGANIZATION_ONLY';
	}

	get isUploadActionProcessing(): boolean {
		return (
			this.isComparing ||
			this.isAnalyzingMapping ||
			this.isConfirmingMapping ||
			this.isValidatingBradescoSpgProducts ||
			this.isReplacingBase
		);
	}

	get isPrimaryUploadActionProcessing(): boolean {
		return (
			this.isComparing ||
			this.isAnalyzingMapping ||
			this.isConfirmingMapping ||
			this.isValidatingBradescoSpgProducts
		);
	}

	get isUploadSelectionBlocked(): boolean {
		return this.isUploadActionProcessing || this.isProcessingReconciliation;
	}

	get canRunUploadAction(): boolean {
		return this.isBaseReplacementMode ? this.canReplaceBase : this.canCompare;
	}

	get uploadActionLabel(): string {
		if (
			this.isFlexibleUploadMode &&
			this.mappingAnalysis &&
			!this.confirmedMapping
		) {
			return 'Revisar mapeamento';
		}

		if (this.isFlexibleUploadMode && this.confirmedMapping) {
			return 'Confirmar mapeamento';
		}

		if (this.isBaseReplacementMode) {
			return this.hasPendingBaseReplacement
				? 'Processar base substituída'
				: 'Processar bases';
		}

		return this.isFlexibleUploadMode ? 'Analisar colunas' : 'Processar bases';
	}

	get uploadActionLoadingLabel(): string {
		if (this.isConfirmingMapping) {
			return 'Confirmando mapeamento';
		}

		if (this.isReplacingBase) {
			return 'Processando base substituída';
		}

		return this.isFlexibleUploadMode
			? 'Analisando colunas'
			: 'Processando bases';
	}

	get isPendingChangeMutationBlocked(): boolean {
		return this.isApplyProcessing || this.isCompareProcessing;
	}

	get processingStateMessage(): string {
		if (this.activeProcessingType === 'COMPARE') {
			return 'Estamos processando a comparação. Isso pode levar alguns minutos.';
		}

		if (this.activeProcessingType === 'APPLY') {
			return 'Estamos aplicando as alterações. Isso pode levar alguns minutos.';
		}

		return 'Estamos processando. Isso pode levar alguns minutos.';
	}

	get isUncomparedLinesActive(): boolean {
		return this.activeCardKey === 'UNCOMPARED_LINES';
	}

	get isUnidentifiedOrganizationsActive(): boolean {
		return this.activeCardKey === 'UNIDENTIFIED_ORGANIZATIONS';
	}

	get isDivergenceCardActive(): boolean {
		return (
			this.activeCardKey !== 'DUPLICATE_IN_FILE' &&
			!this.isUncomparedLinesActive &&
			!this.isUnidentifiedOrganizationsActive
		);
	}

	get isItemTableActive(): boolean {
		return (
			!this.isUncomparedLinesActive && !this.isUnidentifiedOrganizationsActive
		);
	}

	get isTopCardActive(): boolean {
		return (
			this.activeCardKey === 'DUPLICATE_IN_FILE' ||
			this.isUncomparedLinesActive ||
			this.isUnidentifiedOrganizationsActive
		);
	}

	get missingSubs(): GenericBeneficiaryReconciliationPreflightSummary['organizationAlerts']['missingSubs'] {
		return this.preflightSummary?.organizationAlerts?.missingSubs ?? [];
	}

	get preflightEligibleCount(): number {
		return this.preflightSummary?.counts.eligible ?? 0;
	}

	get preflightOnHoldCount(): number {
		return this.preflightSummary?.counts.onHold ?? 0;
	}

	get preflightPendingCount(): number {
		return this.preflightSummary?.counts.pending ?? 0;
	}

	get preflightTotalCount(): number {
		return this.preflightSummary?.counts.total ?? 0;
	}

	get showDentalUnavailableAlert(): boolean {
		return Boolean(
			this.preflightSummary?.organizationAlerts?.dentalPlanNotRegistered,
		);
	}

	get pendingUnknownCnpjs(): GenericBeneficiaryReconciliationUnknownCnpjAlert[] {
		return this.preflightUnknownCnpjs.filter(
			(alert) => alert.status === 'PENDING',
		);
	}

	get allPreflightPendencyRows(): GenericBeneficiaryReconciliationPreflightRow[] {
		return [
			...this.preflightPendingRows,
			...this.preflightOnHoldRows,
			...this.preflightEligibleRows,
		];
	}

	get uncomparedPendingRows(): GenericBeneficiaryReconciliationPreflightRow[] {
		return this.preflightPendingRows.filter((row) =>
			this.isUncomparedLineRow(row),
		);
	}

	get uncomparedOnHoldRows(): GenericBeneficiaryReconciliationPreflightRow[] {
		return this.preflightOnHoldRows.filter((row) =>
			this.isUncomparedLineRow(row),
		);
	}

	get uncomparedPendencyCount(): number {
		return this.preflightSummary?.pendencyCounts?.uncomparedLines ?? 0;
	}

	get uncomparedCaseCount(): number {
		return this.uncomparedPendencyCount;
	}

	get uncomparedOnHoldCount(): number {
		return this.uncomparedOnHoldRows.length;
	}

	get uncomparedPendingCount(): number {
		return this.uncomparedPendingRows.length;
	}

	get filteredUncomparedRows(): GenericBeneficiaryReconciliationPreflightRow[] {
		const rows = this.hasCompletePreflightSummaryRows()
			? (this.preflightSummary?.rows ?? []).filter((row) =>
					this.isUncomparedLineRow(row),
				)
			: [
					...this.uncomparedPendingRows,
					...this.uncomparedOnHoldRows,
					...this.preflightEligibleRows.filter((row) =>
						this.isUncomparedLineRow(row),
					),
				];

		return this.filterPreflightRows(rows);
	}

	get unidentifiedOrganizationPendingRows(): GenericBeneficiaryReconciliationPreflightRow[] {
		return this.preflightPendingRows.filter((row) =>
			this.isUnidentifiedOrganizationRow(row),
		);
	}

	get unidentifiedOrganizationOnHoldRows(): GenericBeneficiaryReconciliationPreflightRow[] {
		return this.preflightOnHoldRows.filter((row) =>
			this.isUnidentifiedOrganizationRow(row),
		);
	}

	get unidentifiedOrganizationPendencyCount(): number {
		const groupedCount = this.unidentifiedOrganizationGroups.reduce(
			(total, group) => total + group.pendingCount + group.onHoldCount,
			0,
		);

		return groupedCount > 0
			? groupedCount
			: this.unidentifiedOrganizationPendingRows.length +
					this.unidentifiedOrganizationOnHoldRows.length;
	}

	get unidentifiedOrganizationCaseCount(): number {
		return this.unidentifiedOrganizationGroups.reduce(
			(total, group) => total + group.rowCount,
			0,
		);
	}

	get filteredUnidentifiedOrganizationGroups(): GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup[] {
		const status = this.normalizeStatusFilter(
			this.getStringValue(this.filterForm.get('status')?.value),
		);
		const carrier = this.getStringValue(
			this.filterForm.get('carrier')?.value,
		).toLowerCase();
		const search = this.getStringValue(this.filterForm.get('search')?.value);
		const normalizedSearch = this.normalizeSearchText(search);
		const searchDigits = this.onlyDigits(search);

		return this.unidentifiedOrganizationGroups.filter((group) => {
			if (
				status === BeneficiaryReconciliationItemStatusFilter.APPLIED &&
				(group.status !== 'ELIGIBLE' ||
					(group.appliedCount ?? 0) <= (group.ignoredCount ?? 0))
			) {
				return false;
			}

			if (
				status === BeneficiaryReconciliationItemStatusFilter.IGNORED &&
				(group.ignoredCount ?? 0) === 0
			) {
				return false;
			}

			if (status === BeneficiaryReconciliationItemStatusFilter.FAILED) {
				return false;
			}

			if (
				status ===
					BeneficiaryReconciliationItemStatusFilter.MANUAL_REVIEW_REQUIRED &&
				(group.pendingCount === 0 || this.pendingChanges.has(group.key))
			) {
				return false;
			}

			if (
				status === BeneficiaryReconciliationItemStatusFilter.PENDING &&
				group.pendingCount > 0
			) {
				return false;
			}

			if (
				status === BeneficiaryReconciliationItemStatusFilter.READY_TO_APPLY &&
				!this.pendingChanges.has(group.key)
			) {
				return false;
			}

			if (carrier) {
				return false;
			}

			if (normalizedSearch) {
				const identifierLabel =
					this.getUnidentifiedOrganizationIdentifierLabel(group);
				const textHaystack = this.normalizeSearchText(
					[
						group.cnpj ?? '',
						group.subinvoiceNumber ?? '',
						group.normalizedSubinvoiceNumber ?? '',
						identifierLabel,
						this.getPreflightIssueCodeLabels(group.issueCodes),
					].join(' '),
				);
				const digitHaystack = this.onlyDigits(
					[
						group.cnpj ?? '',
						group.subinvoiceNumber ?? '',
						group.normalizedSubinvoiceNumber ?? '',
						identifierLabel,
					].join(' '),
				);

				return (
					textHaystack.includes(normalizedSearch) ||
					Boolean(searchDigits && digitHaystack.includes(searchDigits))
				);
			}

			return true;
		});
	}

	get paginatedUncomparedRows(): GenericBeneficiaryReconciliationPreflightRow[] {
		return this.filteredUncomparedRows;
	}

	get paginatedUnidentifiedOrganizationGroups(): GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup[] {
		return this.paginateLocalItems(this.filteredUnidentifiedOrganizationGroups);
	}

	get localTotalPages(): number {
		if (this.isUncomparedLinesActive) {
			return this.itemsTotalPages;
		}

		const total = this.isUncomparedLinesActive
			? this.filteredUncomparedRows.length
			: this.filteredUnidentifiedOrganizationGroups.length;

		return Math.ceil(total / this.getLimit());
	}

	get duplicatePendencyCount(): number {
		return this.summary.DUPLICATE_IN_FILE ?? 0;
	}

	get firstRowCards(): ReconciliationCard[] {
		return [
			{
				count: this.duplicatePendencyCount,
				description: this.getFieldDescription('DUPLICATE_IN_FILE'),
				field: 'DUPLICATE_IN_FILE',
				key: 'DUPLICATE_IN_FILE',
				label: 'Duplicado na base do cliente',
			},
			{
				count: this.uncomparedCaseCount,
				description:
					'Nome, CPF, nascimento ou operadora necessária ausente ou inválida.',
				key: 'UNCOMPARED_LINES',
				label: 'Dados incompletos',
				onHoldCount: this.uncomparedOnHoldCount,
				pendingCount: this.uncomparedPendingCount,
				preflightKind: 'UNCOMPARED_LINES',
			},
			{
				count: this.unidentifiedOrganizationCaseCount,
				description:
					'CNPJ ausente, não cadastrado ou fora do contexto da matriz.',
				key: 'UNIDENTIFIED_ORGANIZATIONS',
				label: 'Organizações não identificadas',
				onHoldCount: this.unidentifiedOrganizationGroups.reduce(
					(total, group) => total + group.onHoldCount,
					0,
				),
				pendingCount: this.unidentifiedOrganizationGroups.reduce(
					(total, group) => total + group.pendingCount,
					0,
				),
				preflightKind: 'UNIDENTIFIED_ORGANIZATIONS',
			},
			{
				count: this.getFieldCount('ORGANIZATION_MISMATCH'),
				description: this.getFieldDescription('ORGANIZATION_MISMATCH'),
				field: 'ORGANIZATION_MISMATCH',
				key: 'ORGANIZATION_MISMATCH',
				label: this.getFieldLabel('ORGANIZATION_MISMATCH'),
			},
		];
	}

	get secondRowCards(): ReconciliationCard[] {
		return this.summaryCards
			.filter(
				(card) =>
					!['DUPLICATE_IN_FILE', 'ORGANIZATION_MISMATCH'].includes(card.field),
			)
			.map((card) => ({
				count: this.getFieldCount(card.field),
				description: this.getFieldDescription(card.field),
				field: card.field,
				key: card.field,
				label: card.label,
			}));
	}

	get pendingComparisonPendencyCount(): number {
		return (
			this.uncomparedPendencyCount +
			this.unidentifiedOrganizationPendencyCount +
			this.duplicatePendencyCount
		);
	}

	get canOpenApplyPreview(): boolean {
		return (
			Boolean(this.currentApplyField) &&
			(this.sessionPendingTotal > 0 || this.pendingChangesCount > 0) &&
			!this.isApplying &&
			!this.hasUnknownApplyResult &&
			!this.isProcessingReconciliation
		);
	}

	get hasUnknownApplyResult(): boolean {
		return Boolean(this.unknownApplySessionId && this.unknownApplyContext);
	}

	get currentApplyField(): BeneficiaryReconciliationField | null {
		return this.parseReconciliationField(this.filterForm.get('field')?.value);
	}

	get applySectionButtonLabel(): string {
		const field = this.currentApplyField;

		return field ? this.applyButtonLabels[field] : 'Selecione uma seção';
	}

	get isDuplicateInFileTab(): boolean {
		return (
			this.getStringValue(this.filterForm.get('field')?.value) ===
			'DUPLICATE_IN_FILE'
		);
	}

	get canShowDuplicateExportButton(): boolean {
		return (
			this.hasComparisonLoaded &&
			this.isDuplicateInFileTab &&
			(this.summary.DUPLICATE_IN_FILE ?? 0) > 0
		);
	}

	get canShowDuplicateReturnImportButton(): boolean {
		return this.canShowDuplicateExportButton;
	}

	get activeTopCardApplyLabel(): string {
		if (this.isUncomparedLinesActive) {
			return 'Aplicar comparações';
		}

		if (this.isUnidentifiedOrganizationsActive) {
			return 'Aplicar organizações';
		}

		return 'Processar duplicidades';
	}

	get activeTopCardExportLabel(): string {
		if (this.isUncomparedLinesActive) {
			return 'Exportar linhas não comparadas';
		}

		if (this.isUnidentifiedOrganizationsActive) {
			return 'Exportar organizações não identificadas';
		}

		return 'Exportar duplicidades';
	}

	get activeTopCardImportLabel(): string {
		if (this.isUncomparedLinesActive) {
			return 'Importar linhas resolvidas';
		}

		return 'Importar duplicidades';
	}

	get activeTopCardPendingCount(): number {
		if (this.isUncomparedLinesActive) {
			return this.filteredUncomparedRows.filter((row) =>
				this.isPreflightRowActionable(row),
			).length;
		}

		if (this.isUnidentifiedOrganizationsActive) {
			return this.filteredUnidentifiedOrganizationGroups.filter((group) =>
				this.isUnidentifiedOrganizationGroupActionable(group),
			).length;
		}

		return this.summary.DUPLICATE_IN_FILE ?? 0;
	}

	get canShowTopCardActions(): boolean {
		return this.hasReconciliationLoaded && this.isTopCardActive;
	}

	get canShowTopCardImportButton(): boolean {
		return !this.isUnidentifiedOrganizationsActive;
	}

	get canApplyTopCard(): boolean {
		if (
			!this.comparisonId ||
			this.isApplying ||
			this.hasUnknownApplyResult ||
			this.isProcessingReconciliation
		) {
			return false;
		}

		if (this.isUncomparedLinesActive) {
			return (
				this.getPendingChangesByPreflightKind('UNCOMPARED_LINES').length > 0
			);
		}

		if (this.isUnidentifiedOrganizationsActive) {
			return (
				this.getPendingChangesByPreflightKind('UNIDENTIFIED_ORGANIZATIONS')
					.length > 0
			);
		}

		if (this.activeCardKey === 'DUPLICATE_IN_FILE') {
			return (
				this.bulkCardActionAvailability.pendingHoldCount > 0 ||
				Array.from(this.pendingChanges.values()).some(
					(change) => change.field === 'DUPLICATE_IN_FILE',
				)
			);
		}

		return this.canOpenApplyPreview;
	}

	get isActiveMissingInDbTab(): boolean {
		return (
			this.getStringValue(this.filterForm.get('field')?.value) ===
			'ACTIVE_MISSING_IN_DB'
		);
	}

	get canShowActiveMissingExportButton(): boolean {
		return (
			this.hasComparisonLoaded &&
			this.isActiveMissingInDbTab &&
			(this.summary.ACTIVE_MISSING_IN_DB ?? 0) > 0
		);
	}

	get canShowOperationalDivergenceExportButton(): boolean {
		const field = this.currentApplyField;

		return (
			this.hasComparisonLoaded &&
			this.isOperationalDivergenceExportField(field) &&
			(this.summary[field] ?? 0) > 0
		);
	}

	get canShowTitularityReturnImportButton(): boolean {
		return (
			this.hasComparisonLoaded &&
			this.currentApplyField === 'TITULARITY_MISMATCH' &&
			(this.summary.TITULARITY_MISMATCH ?? 0) > 0
		);
	}

	get canShowBulkIgnoreButton(): boolean {
		const field = this.currentApplyField;

		if (!field) {
			return false;
		}

		return this.hasComparisonLoaded && (this.summary[field] ?? 0) > 0;
	}

	get isBulkCardActionRunning(): boolean {
		return (
			this.isBulkIgnoring ||
			this.isBulkIgnoringAll ||
			this.isApplying ||
			this.isProcessingReconciliation
		);
	}

	get canShowSelectedCardMassActions(): boolean {
		if (!this.hasReconciliationLoaded) {
			return false;
		}

		if (this.activeCardKey === 'DUPLICATE_IN_FILE') {
			return this.canShowBulkIgnoreButton;
		}

		if (this.isTopCardActive) {
			return this.activeTopCardPendingCount > 0;
		}

		return this.canShowBulkIgnoreButton;
	}

	get canShowIgnoreAllCasesAction(): boolean {
		return this.isTopCardActive || Boolean(this.currentApplyField);
	}

	get isCreationCardActive(): boolean {
		return this.currentApplyField === 'ACTIVE_MISSING_IN_DB';
	}

	get canHoldPendingInSelectedCard(): boolean {
		if (this.activeCardKey === 'DUPLICATE_IN_FILE') {
			return this.bulkCardActionAvailability.pendingHoldCount > 0;
		}

		if (this.isTopCardActive) {
			return this.activeTopCardPendingCount > 0;
		}

		return this.bulkCardActionAvailability.pendingHoldCount > 0;
	}

	get canIgnoreAllCasesInSelectedCard(): boolean {
		if (this.activeCardKey === 'DUPLICATE_IN_FILE') {
			return this.hasBulkCardActionEligibleItems;
		}

		if (this.isTopCardActive) {
			return this.activeTopCardPendingCount > 0;
		}

		if (!this.currentApplyField) {
			return false;
		}

		return (
			this.bulkCardActionAvailability.pendingHoldCount +
				this.bulkCardActionAvailability.failedCount +
				this.bulkCardActionAvailability.queuedCount >
			0
		);
	}

	get hasBulkCardActionEligibleItems(): boolean {
		return (
			this.bulkCardActionAvailability.pendingHoldCount +
				this.bulkCardActionAvailability.failedCount +
				this.bulkCardActionAvailability.queuedCount >
			0
		);
	}

	get holdPendingButtonLabel(): string {
		switch (this.currentApplyField) {
			case 'ACTIVE_MISSING_IN_DB':
				return 'Colocar pendências de criação em espera';
			case 'DUPLICATE_IN_FILE':
				return 'Colocar duplicidades pendentes em espera';
			default:
				return 'Colocar pendentes em espera';
		}
	}

	get manualReviewedCount(): number {
		return this.items.filter(
			(item) =>
				this.requiresManualReview(item) && this.pendingChanges.has(item.id),
		).length;
	}

	get pendingActionSummaries(): PendingActionSummary[] {
		const grouped = Array.from(this.pendingChanges.values()).reduce<
			Map<string, PendingActionSummary>
		>((accumulator, change) => {
			const label =
				this.getStringValue(change.actionLabel) ||
				this.getPendingActionLabel(change.action);
			const key = `${change.action}:${label}`;
			const current = accumulator.get(key);

			if (current) {
				current.count += 1;

				return accumulator;
			}

			accumulator.set(key, {
				action: change.action,
				count: 1,
				label,
			});

			return accumulator;
		}, new Map());

		return Array.from(grouped.values()).sort(
			(current, next) => next.count - current.count,
		);
	}

	get pendingChangesCount(): number {
		return this.pendingChanges.size;
	}

	get pendingDisplayTotal(): number {
		return Math.max(this.sessionPendingTotal, this.pendingChangesCount);
	}

	get pendingManualReviewCount(): number {
		return this.items.filter(
			(item) =>
				this.requiresManualReview(item) && !this.pendingChanges.has(item.id),
		).length;
	}

	addFiles(fieldName: GenericUploadFileField, files: FileList | null): void {
		if (!files?.length) {
			return;
		}

		const selectedFiles = this.getAcceptedStandardReconciliationFiles(files);
		const file = selectedFiles[0];

		if (!file) {
			return;
		}

		if (fieldName === 'healthFile') {
			this.healthFile = file;

			return;
		}

		this.dentalFile = file;
	}

	addFlexibleFiles(files: FileList | null): void {
		if (!files?.length) {
			return;
		}

		const selectedFiles = this.getAcceptedReconciliationFiles(
			files,
			this.sanusStandardReconciliationFileExtensions,
		);

		if (selectedFiles.length === 0) {
			return;
		}

		this.flexibleFiles = [...this.flexibleFiles, ...selectedFiles];
		this.confirmedMapping = null;
		this.mappingAnalysis = null;
	}

	downloadModelTemplate(type: keyof typeof this.modelTemplates): void {
		const template = this.modelTemplates[type];
		const link = document.createElement('a');

		link.setAttribute('href', template.url);
		link.setAttribute('download', template.fileName);
		document.body.appendChild(link);
		link.click();
		link.remove();
	}

	downloadOriginalFile(file: ReconciliationOriginalFile): void {
		if (
			!this.organizationId ||
			!this.comparisonId ||
			!file.id ||
			this.isDownloadingOriginalFileId
		) {
			return;
		}

		this.isDownloadingOriginalFileId = file.id;
		this.beneficiaryReconciliationsService
			.downloadGenericOriginalFile({
				fileId: file.id,
				organizationId: this.organizationId,
				sessionId: this.comparisonId,
			})
			.pipe(
				finalize(() => {
					this.isDownloadingOriginalFileId = null;
				}),
				takeUntil(this.unsubscribe$),
			)
			.subscribe({
				error: (error) => {
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Não foi possível baixar o arquivo original.',
						),
						'error',
					);
				},
				next: (response) => {
					this.saveBlobLocally(
						response.body,
						this.getDownloadFileName(response, file.fileName),
					);
				},
			});
	}

	downloadLastReport(): void {
		if (!this.lastReportUrl) {
			return;
		}

		const link = document.createElement('a');

		link.setAttribute('href', this.lastReportUrl);
		link.setAttribute('target', '_blank');
		document.body.appendChild(link);
		link.click();
		link.remove();
	}

	formatOriginalFileSize(fileSize?: number | null): string {
		if (typeof fileSize !== 'number' || !Number.isFinite(fileSize)) {
			return 'Tamanho não informado';
		}

		return `${(fileSize / 1024 / 1024).toFixed(2)} MB`;
	}

	getUploadTriggerLabel(fieldName: GenericUploadFileField): string {
		const walletLabel = fieldName === 'healthFile' ? 'Saúde' : 'Dental';

		return this.hasUploadFileForField(fieldName)
			? `Substituir base ${walletLabel}`
			: `Selecionar base ${walletLabel}`;
	}

	getUploadTriggerTitle(fieldName: GenericUploadFileField): string | null {
		if (!this.isUploadTriggerBlocked(fieldName)) {
			return null;
		}

		return this.isBaseReplacementMode
			? this.replaceBaseDisabledReason
			: 'Aguarde o processamento atual para selecionar uma base.';
	}

	formatCpf(value?: string | null): string {
		if (!value) {
			return '-';
		}

		const digits = value.replace(/\D/g, '');

		if (digits.length !== 11) {
			return value;
		}

		return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(
			6,
			9,
		)}-${digits.slice(9, 11)}`;
	}

	getActionMode(item: BeneficiaryReconciliationItem): ItemActionMode {
		const context = (item.context ?? {}) as Record<string, unknown>;
		const manualReviewRequired = this.resolveManualReviewRequired(context);
		const isResolved = this.resolveResolved(context);
		const suggestedAction =
			this.getStringValue(context.suggestedAction) ||
			this.getStringValue(item.suggestedAction);
		const resolutionOptions = Array.isArray(context.resolutionOptions)
			? context.resolutionOptions
			: [];

		if (manualReviewRequired) {
			return 'MANUAL_APPLY';
		}

		if (this.isDuplicateOnHold(item)) {
			return 'AUTO_APPLY';
		}

		if (isResolved) {
			return 'AUTO_APPLY';
		}

		if (suggestedAction) {
			return 'AUTO_APPLY';
		}

		if (resolutionOptions.length > 0 || item.resolvable) {
			return 'MANUAL_APPLY';
		}

		return 'REVIEW_ONLY';
	}

	getBenefitVariantLabel(item: BeneficiaryReconciliationItem): string {
		const context = item.context ?? {};
		const benefitVariant = this.getStringValue(
			context.benefitVariant,
		).toUpperCase();
		const benefitVariantLabel = this.getStringValue(
			context.benefitVariantLabel,
		).toUpperCase();
		const walletType = this.getStringValue(context.walletType).toUpperCase();

		if (
			benefitVariant === 'CONGENER' ||
			benefitVariantLabel === 'CONGÊNERE' ||
			walletType === 'CONGENER'
		) {
			return 'Congênere';
		}

		if (
			benefitVariant === 'CONJUGATED' ||
			benefitVariantLabel === 'CONJUGADO' ||
			walletType === 'CONJUGATED'
		) {
			return 'Conjugado';
		}

		return '';
	}

	getFieldCount(field: BeneficiaryReconciliationField): number {
		return this.summary[field] ?? 0;
	}

	getFieldDescription(field: BeneficiaryReconciliationField): string {
		return getBeneficiaryReconciliationFieldDescription(field);
	}

	getFieldLabel(field: BeneficiaryReconciliationField): string {
		return getBeneficiaryReconciliationFieldLabel(field);
	}

	getReconciliationCardGuidance(card: ReconciliationCard): string {
		return this.reconciliationCardGuidanceTexts[card.key] ?? card.description;
	}

	getReconciliationCardGuidanceAriaLabel(card: ReconciliationCard): string {
		return this.isReconciliationCardGuidanceExpanded(card)
			? `Ocultar orientação de ${card.label}`
			: `Ver orientação de ${card.label}`;
	}

	getReconciliationCardGuidanceTooltip(card: ReconciliationCard): string {
		return this.isReconciliationCardGuidanceExpanded(card)
			? 'Ocultar orientação'
			: 'Ver orientação';
	}

	isReconciliationCardGuidanceExpanded(card: ReconciliationCard): boolean {
		return this.expandedGuidanceCardKey === card.key;
	}

	getSessionProgressStatus(
		session: BeneficiaryReconciliationSessionListItem,
	): BeneficiaryReconciliationSessionProgressStatus {
		const storedPendingSummary = this.getStoredPendingChangesSummary(
			session.id,
		);

		if (storedPendingSummary.actionableCount > 0) {
			return 'IN_PROGRESS';
		}

		if (
			storedPendingSummary.onHoldCount > 0 &&
			(session.appliedCount ?? 0) + storedPendingSummary.onHoldCount >=
				session.total
		) {
			return 'CRITICIZED';
		}

		return session.progressStatus ?? 'COMPARED_ONLY';
	}

	getSessionProgressStatusLabel(
		session: BeneficiaryReconciliationSessionListItem,
	): string {
		switch (this.getSessionProgressStatus(session)) {
			case 'COMPLETED':
				return 'Concluída';
			case 'CRITICIZED':
				return 'Criticado';
			case 'IN_PROGRESS':
				return 'Em andamento';
			case 'COMPARED_ONLY':
			default:
				return 'Apenas comparada';
		}
	}

	getSessionProgressStatusClass(
		session: BeneficiaryReconciliationSessionListItem,
	): string {
		return `history-${this.getSessionProgressStatus(session)
			.toLowerCase()
			.replace(/_/g, '-')}`;
	}

	getSessionScopeLabel(
		session: BeneficiaryReconciliationSessionListItem,
	): string {
		switch (this.getSessionPreflight(session)?.scope?.kind) {
			case 'SUB_ONLY':
				return 'Sub';
			case 'MATRIX_ONLY':
				return 'Somente matriz';
			case 'FULL_GROUP':
				return 'Núcleo inteiro';
			case 'LEGACY_FULL_GROUP':
			default:
				return 'Legado';
		}
	}

	getInteractionButtonLabel(item: BeneficiaryReconciliationItem): string {
		if (this.isManualReviewRequired(item)) {
			return 'Revisar';
		}

		if (this.getActionMode(item) === 'REVIEW_ONLY') {
			return 'Ver';
		}

		return 'Editar';
	}

	getMissingDataNote(item: BeneficiaryReconciliationItem): string {
		if (!this.isCreateBeneficiaryCard(item)) {
			return '';
		}

		const essentialMissingFields = this.getEssentialMissingFields(item);

		if (
			this.isManualReviewRequired(item) &&
			essentialMissingFields.length > 0
		) {
			return `Dados essenciais faltantes: ${essentialMissingFields.join(', ')}`;
		}

		const importantMissingFields = this.getImportantMissingFields(item);

		if (importantMissingFields.length > 0) {
			return `Dados faltantes: ${importantMissingFields.join(', ')}`;
		}

		return '';
	}

	getItemStatusLabel(item: BeneficiaryReconciliationItem): string {
		if (this.isPending(item) && !this.isItemOnHold(item)) {
			return 'Pronto para aplicar';
		}

		if (this.isItemDefinitivelyIgnored(item)) {
			return 'Ignorado';
		}

		if (this.isItemOnHold(item)) {
			return 'Em espera';
		}

		if (item.status === BeneficiaryReconciliationItemStatus.APPLIED) {
			return 'Aplicado';
		}

		if (item.status === BeneficiaryReconciliationItemStatus.FAILED) {
			return 'Falhou';
		}

		if (this.isManualReviewRequired(item)) {
			return 'Pendente';
		}

		return 'Pendente';
	}

	getItemStatusTone(
		item: BeneficiaryReconciliationItem,
	): '' | 'error' | 'queued' | 'review' | 'success' | 'waiting' {
		if (this.isPending(item) && !this.isItemOnHold(item)) {
			return 'queued';
		}

		if (this.isItemDefinitivelyIgnored(item)) {
			return 'success';
		}

		if (this.isItemOnHold(item)) {
			return 'waiting';
		}

		if (item.status === BeneficiaryReconciliationItemStatus.APPLIED) {
			return 'success';
		}

		if (item.status === BeneficiaryReconciliationItemStatus.FAILED) {
			return 'error';
		}

		if (this.isManualReviewRequired(item)) {
			return 'review';
		}

		return '';
	}

	getItemWarningLabels(item: BeneficiaryReconciliationItem): string {
		if (!this.isCreateBeneficiaryCard(item)) {
			return '';
		}

		const context = (item.context ?? {}) as Record<string, unknown>;
		const warnings = Array.isArray(context.warnings) ? context.warnings : [];
		const labels = warnings
			.map((warning) =>
				typeof warning === 'string' ? this.getPreflightIssueLabel(warning) : '',
			)
			.filter(Boolean);

		return labels.length > 0 ? labels.join(', ') : '';
	}

	getItemWalletKindLabel(item: BeneficiaryReconciliationItem): string {
		const context = (item.context ?? {}) as Record<string, unknown>;
		const walletKind = this.parseWalletKind(context.walletKind);

		return this.getWalletKindLabel(walletKind);
	}

	getManualActionLabel(item: BeneficiaryReconciliationItem): string {
		switch (item.field) {
			case 'ACTIVE_MISSING_IN_DB':
				return 'Resolver inline';
			case 'DUPLICATE_IN_FILE':
				return 'Revisar duplicidade';
			case 'INACTIVE_MISSING_IN_FILE':
				return 'Definir ação';
			case 'ORGANIZATION_MISMATCH':
				return 'Transferir ou ignorar';
			case 'TITULARITY_MISMATCH':
				return 'Revisar titularidade';
			default:
				return 'Resolver';
		}
	}

	getPendingPreview(item: BeneficiaryReconciliationItem): string {
		const pendingItem = this.pendingChanges.get(item.id);

		if (!pendingItem) {
			return '';
		}

		const valueParts: string[] = [];

		if (pendingItem.targetValue.planName) {
			valueParts.push(`Plano ${pendingItem.targetValue.planName}`);
		}

		if (pendingItem.targetValue.cardNumber) {
			valueParts.push(`Carteirinha ${pendingItem.targetValue.cardNumber}`);
		}

		if (pendingItem.targetValue.typeKinship) {
			valueParts.push(`Titularidade ${pendingItem.targetValue.typeKinship}`);
		}

		if (pendingItem.targetValue.name) {
			valueParts.push(`Nome ${pendingItem.targetValue.name}`);
		}

		if (pendingItem.targetValue.rowNumber) {
			valueParts.push(`Linha ${pendingItem.targetValue.rowNumber}`);
		}

		if (this.isPendingChangeOnHold(pendingItem)) {
			return 'Em espera';
		}

		if (this.isKeepPlatformPendingChange(pendingItem)) {
			return (
				this.getStringValue(pendingItem.actionLabel) || 'Plataforma mantida'
			);
		}

		if (
			pendingItem.action === 'IGNORE_ITEM' &&
			item.field === 'PLAN_MISMATCH'
		) {
			return 'Manter plano atual da plataforma';
		}

		if (
			pendingItem.action === 'IGNORE_ITEM' &&
			item.field === 'CARD_MISMATCH'
		) {
			return 'Manter carteirinha atual da plataforma';
		}

		if (
			pendingItem.action === 'IGNORE_ITEM' &&
			item.field === 'DUPLICATE_IN_FILE'
		) {
			return 'Em espera';
		}

		if (
			pendingItem.action === 'SELECT_DUPLICATE_ROW' &&
			item.field === 'DUPLICATE_IN_FILE'
		) {
			return valueParts.length > 0
				? `Linha selecionada - ${valueParts.join(' - ')}`
				: 'Linha selecionada';
		}

		const actionLabel =
			this.getStringValue(pendingItem.actionLabel) ||
			this.getPendingActionLabel(pendingItem.action);

		return [actionLabel, ...valueParts].filter(Boolean).join(' - ');
	}

	getResolutionNote(item: BeneficiaryReconciliationItem): string {
		return this.getStringValue(item.context?.resolutionNote);
	}

	private getEssentialMissingFields(
		item: BeneficiaryReconciliationItem,
	): string[] {
		const context = (item.context ?? {}) as Record<string, unknown>;
		const rawValue = context.missingRequiredFields;

		if (!Array.isArray(rawValue)) {
			return [];
		}

		return rawValue
			.filter((value): value is string => typeof value === 'string')
			.map((value) => this.getEssentialFieldLabel(value))
			.filter(Boolean);
	}

	private getEssentialFieldLabel(field: string): string {
		switch (field) {
			case 'organizationId':
				return 'Organização';
			case 'organizationCnpj':
				return 'CNPJ';
			case 'name':
				return 'Nome';
			case 'cpf':
				return 'CPF';
			case 'birthDay':
				return 'Data de nascimento';
			case 'typeKinship':
				return 'Titularidade';
			default:
				return field;
		}
	}

	private getImportantMissingFields(
		item: BeneficiaryReconciliationItem,
	): string[] {
		const context = (item.context ?? {}) as Record<string, unknown>;
		const rawValue = context.missingFields;

		if (!Array.isArray(rawValue)) {
			return [];
		}

		return rawValue
			.filter((value): value is string => typeof value === 'string')
			.filter((value) =>
				this.shouldDisplayImportantMissingField(value, context),
			)
			.map((value) => this.getImportantFieldLabel(value))
			.filter(Boolean);
	}

	private getImportantFieldLabel(field: string): string {
		switch (field) {
			case 'genero':
			case 'gender':
				return 'Gênero';
			case 'telefone':
			case 'phone':
				return 'Telefone';
			case 'planoSaude':
				return 'Plano saúde';
			case 'carteirinhaSaude':
			case 'healthCardNumber':
				return 'Carteirinha saúde';
			case 'planoCongenere':
				return 'Plano congênere';
			case 'carteirinhaCongenere':
				return 'Carteirinha congênere';
			case 'planoConjugado':
				return 'Plano conjugado';
			case 'carteirinhaConjugado':
				return 'Carteirinha conjugado';
			case 'planoOdonto':
				return 'Plano odonto';
			case 'carteirinhaOdonto':
			case 'dentalCardNumber':
				return 'Carteirinha odonto';
			default:
				return field;
		}
	}

	private hasImportantIncompleteData(
		item: BeneficiaryReconciliationItem,
	): boolean {
		const context = (item.context ?? {}) as Record<string, unknown>;

		if (this.getBooleanValue(context.incompleteData)) {
			return true;
		}

		return this.getImportantMissingFields(item).length > 0;
	}

	private isConjugatedBenefitContext(
		context: Record<string, unknown>,
	): boolean {
		return [
			context.benefitVariant,
			context.benefitVariantLabel,
			context.walletType,
		].some((value) =>
			['CONJUGADO', 'CONJUGATED'].includes(
				this.normalizeBenefitContextValue(value),
			),
		);
	}

	private isCongenerBenefitContext(context: Record<string, unknown>): boolean {
		return [
			context.benefitVariant,
			context.benefitVariantLabel,
			context.walletType,
		].some((value) =>
			['CONGENER', 'CONGENERE'].includes(
				this.normalizeBenefitContextValue(value),
			),
		);
	}

	private isCongenerMissingField(field: string): boolean {
		return this.congenerMissingFields.has(field);
	}

	private isConjugatedMissingField(field: string): boolean {
		return this.conjugatedMissingFields.has(field);
	}

	private isDentalWalletContext(context: Record<string, unknown>): boolean {
		return this.normalizeBenefitContextValue(context.walletKind) === 'DENTAL';
	}

	private isNormalDentalMissingField(field: string): boolean {
		return this.normalDentalMissingFields.has(field);
	}

	private isNormalHealthMissingField(field: string): boolean {
		return this.normalHealthMissingFields.has(field);
	}

	private isNormalBenefitMissingField(field: string): boolean {
		return (
			this.isNormalHealthMissingField(field) ||
			this.isNormalDentalMissingField(field)
		);
	}

	private normalizeBenefitContextValue(value: unknown): string {
		return this.getStringValue(value)
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.toUpperCase();
	}

	private shouldDisplayImportantMissingField(
		field: string,
		context: Record<string, unknown>,
	): boolean {
		if (this.isCongenerBenefitContext(context)) {
			return (
				!this.isNormalBenefitMissingField(field) &&
				!this.isConjugatedMissingField(field)
			);
		}

		if (this.isConjugatedBenefitContext(context)) {
			return (
				!this.isNormalBenefitMissingField(field) &&
				!this.isCongenerMissingField(field)
			);
		}

		if (this.isDentalWalletContext(context)) {
			return !this.isNormalHealthMissingField(field);
		}

		return true;
	}

	private isCreateBeneficiaryCard(
		item: BeneficiaryReconciliationItem,
	): boolean {
		return item.field === 'ACTIVE_MISSING_IN_DB';
	}

	private isCreateBeneficiaryQueued(
		item: BeneficiaryReconciliationItem,
	): boolean {
		if (!this.isCreateBeneficiaryCard(item) || !this.isPending(item)) {
			return false;
		}

		return (
			this.pendingChanges.get(item.id)?.action === 'CREATE_BENEFICIARY' ||
			this.getSuggestedPendingChange(item)?.action === 'CREATE_BENEFICIARY'
		);
	}

	getRowCategoryDescription(item: BeneficiaryReconciliationItem): string {
		if (item.field === 'INACTIVE_MISSING_IN_FILE') {
			return 'Ativo na plataforma, ausente na base do cliente';
		}

		if (item.field === 'ORGANIZATION_MISMATCH') {
			return 'Beneficiário está em unidade diferente da base do cliente';
		}

		if (item.field === 'TITULARITY_MISMATCH') {
			const context = (item.context ?? {}) as Record<string, unknown>;
			const message = this.getStringValue(context.message);

			return (
				message ||
				'Diferença de titularidade entre plataforma e base do cliente'
			);
		}

		if (item.field === 'DUPLICATE_IN_FILE') {
			return 'Beneficiário aparece mais de uma vez na base do cliente';
		}

		return '';
	}

	getRowSuggestedActionText(item: BeneficiaryReconciliationItem): string {
		if (this.isPending(item)) {
			return '';
		}

		const context = (item.context ?? {}) as Record<string, unknown>;
		const suggestedAction = this.parseApplyAction(
			context.suggestedAction ?? item.suggestedAction,
		);

		if (!suggestedAction || item.field !== 'ORGANIZATION_MISMATCH') {
			return '';
		}

		if (suggestedAction === 'TRANSFER_ORGANIZATION') {
			return 'Sugestão: Transferir de unidade';
		}

		if (suggestedAction === 'IGNORE_ITEM') {
			return 'Sugestão: Manter como está';
		}

		return '';
	}

	getSourceLabel(item: BeneficiaryReconciliationItem): string {
		const parts = [
			item.sourceFileName ?? '',
			item.sheetName ? `Aba ${item.sheetName}` : '',
			item.rowNumber ? `Linha ${item.rowNumber}` : '',
		].filter(Boolean);

		return parts.length > 0 ? parts.join(' - ') : '-';
	}

	getStructuredValueLabel(
		item: BeneficiaryReconciliationItem,
		source: 'dbValue' | 'externalValue',
	): string {
		const value = item[source] as Record<string, unknown> | null | undefined;

		if (!value) {
			return source === 'dbValue' ? 'Não encontrado' : 'Não enviado';
		}

		switch (item.field) {
			case 'PLAN_MISMATCH':
				return this.getStringValue(value.planName);
			case 'CARD_MISMATCH':
				return this.getStringValue(value.cardNumber);
			case 'TITULARITY_MISMATCH': {
				const titularity = this.getStringValue(value.titularity);
				const holderCpf = this.getStringValue(value.holderCpf);
				const parentesco = this.getStringValue(value.parentesco);
				const details = [
					titularity ? `Titularidade: ${titularity}` : '',
					holderCpf ? `Titular: ${this.formatCpf(holderCpf)}` : '',
					parentesco ? `Parentesco: ${parentesco}` : '',
				].filter(Boolean);

				return details.length > 0 ? details.join(' - ') : '-';
			}
			case 'ORGANIZATION_MISMATCH':
				return (
					this.getStringValue(value.organizationName) ||
					this.getStringValue(value.currentOrganizationName) ||
					this.getStringValue(value.expectedOrganizationName) ||
					'-'
				);
			case 'ACTIVE_MISSING_IN_DB': {
				if (source === 'externalValue') {
					const context = (item.context ?? {}) as Record<string, unknown>;
					const externalValue =
						context.externalValue && typeof context.externalValue === 'object'
							? (context.externalValue as Record<string, unknown>)
							: value;
					const message = this.getStringValue(externalValue.message);
					const details = [
						this.getStringValue(externalValue.planName)
							? `Plano: ${this.getStringValue(externalValue.planName)}`
							: '',
						this.getStringValue(externalValue.cardNumber)
							? `Carteirinha: ${this.getStringValue(externalValue.cardNumber)}`
							: '',
						this.getStringValue(externalValue.titularity)
							? `Titularidade: ${this.getStringValue(externalValue.titularity)}`
							: '',
					].filter(Boolean);

					return (
						message ||
						(details.length > 0
							? details.join(' - ')
							: 'Encontrado na base do cliente')
					);
				}

				const details = [
					this.getStringValue(value.planName)
						? `Plano: ${this.getStringValue(value.planName)}`
						: '',
					this.getStringValue(value.cardNumber)
						? `Carteirinha: ${this.getStringValue(value.cardNumber)}`
						: '',
					this.getStringValue(value.titularity)
						? `Titularidade: ${this.getStringValue(value.titularity)}`
						: '',
				].filter(Boolean);

				return details.length > 0 ? details.join(' - ') : '-';
			}

			case 'INACTIVE_MISSING_IN_FILE': {
				if (source === 'externalValue') {
					const context = (item.context ?? {}) as Record<string, unknown>;
					const externalValue =
						context.externalValue && typeof context.externalValue === 'object'
							? (context.externalValue as Record<string, unknown>)
							: value;
					const message = this.getStringValue(externalValue.message);

					return message || 'Beneficiário não encontrado na base do cliente';
				}

				const details = [
					this.getStringValue(value.planName)
						? `Plano: ${this.getStringValue(value.planName)}`
						: '',
					this.getStringValue(value.cardNumber)
						? `Carteirinha: ${this.getStringValue(value.cardNumber)}`
						: '',
					this.getStringValue(value.titularity)
						? `Titularidade: ${this.getStringValue(value.titularity)}`
						: '',
					typeof value.active === 'boolean'
						? `Status: ${value.active ? 'Ativo' : 'Inativo'}`
						: '',
				].filter(Boolean);

				return details.length > 0 ? details.join(' - ') : '-';
			}
			case 'DUPLICATE_IN_FILE':
				return source === 'externalValue'
					? 'Há múltiplas linhas para este beneficiário'
					: 'Sem valor único na plataforma';
			default:
				return '-';
		}
	}

	getSubjectBeneficiaryLabel(item: BeneficiaryReconciliationItem): string {
		if (
			item.field !== 'TITULARITY_MISMATCH' &&
			item.field !== 'DUPLICATE_IN_FILE'
		) {
			return '';
		}

		const subject = this.getSubjectBeneficiary(item);
		const name =
			this.getStringValue(subject.name) || item.beneficiaryName || '-';
		const cpf = this.getStringValue(subject.cpf) || item.beneficiaryCpf || '';
		const formattedCpf = cpf ? this.formatCpf(cpf) : '-';

		return `Beneficiário analisado: ${name} - CPF: ${formattedCpf}`;
	}

	getSuggestedActionLabel(item: BeneficiaryReconciliationItem): string {
		const pendingItem = this.pendingChanges.get(item.id);

		if (pendingItem) {
			return this.getPendingPreview(item);
		}

		const context = (item.context ?? {}) as Record<string, unknown>;
		const resolvedAction = this.parseApplyAction(
			(item.context?.resolution as { action?: unknown } | undefined)?.action,
		);

		if (this.isDuplicateResolvedByClientReturn(item)) {
			return 'Resolvido via layout - aguardando processamento';
		}

		if (
			item.status === BeneficiaryReconciliationItemStatus.APPLIED &&
			resolvedAction === 'IGNORE_ITEM'
		) {
			return this.isItemDefinitivelyIgnored(item)
				? 'Ignorado'
				: this.getStringValue(context.actionLabel) || 'Plataforma mantida';
		}

		if (resolvedAction) {
			return this.resolvePendingActionLabel(item, resolvedAction);
		}

		if (item.field === 'DUPLICATE_IN_FILE') {
			if (this.isDuplicateOnHold(item)) {
				return 'Em espera';
			}

			if (this.resolveResolved(context)) {
				return 'Linha selecionada';
			}
		}

		const suggestedAction = this.parseApplyAction(
			context.suggestedAction ?? item.suggestedAction,
		);

		if (suggestedAction) {
			const actionLabel = this.getStringValue(context.actionLabel);

			return actionLabel || this.getApplyActionLabel(suggestedAction);
		}

		if (this.isManualReviewRequired(item)) {
			return 'Aguardando revisão obrigatória';
		}

		if (item.resolvable) {
			return 'Aguardando decisão';
		}

		return '-';
	}

	getSuggestedActionTone(
		item: BeneficiaryReconciliationItem,
	): 'manual' | 'neutral' | 'queued' {
		if (this.isItemOnHold(item)) {
			return 'queued';
		}

		if (this.isPending(item)) {
			return 'queued';
		}

		if (this.isManualReviewRequired(item)) {
			return 'manual';
		}

		return 'neutral';
	}

	isManualReviewHighlighted(itemId: string): boolean {
		return this.highlightedManualReviewItemIds.has(itemId);
	}

	isManualReviewRequired(item: BeneficiaryReconciliationItem): boolean {
		return this.requiresManualReview(item) && !this.isPending(item);
	}

	isPending(item: BeneficiaryReconciliationItem): boolean {
		return (
			this.pendingChanges.has(item.id) ||
			Boolean(this.getSuggestedPendingChange(item))
		);
	}

	private hasTerminalReconciliationStatus(
		item: BeneficiaryReconciliationItem,
	): boolean {
		return (
			item.status === BeneficiaryReconciliationItemStatus.APPLIED ||
			item.status === BeneficiaryReconciliationItemStatus.FAILED
		);
	}

	isHistoryAvailable(item: BeneficiaryReconciliationItem): boolean {
		return this.hasTerminalReconciliationStatus(item);
	}

	isHistoryClickable(item: BeneficiaryReconciliationItem): boolean {
		return !this.isPending(item) && this.hasTerminalReconciliationStatus(item);
	}

	loadRecentSession(session: BeneficiaryReconciliationSessionListItem): void {
		this.pendingChanges.clear();
		this.lastReportUrl = null;
		this.loadedSessionAppliedCount = session.appliedCount ?? 0;
		this.selectedHistorySession = session;
		this.sessionPendingTotal = 0;
		const preflight = this.getSessionPreflight(session);

		if (preflight && this.isPreflightOnlySession(session)) {
			this.comparisonId = session.id;
			this.items = [];
			this.itemsCount = 0;
			this.itemsTotalPages = 0;
			this.summary = EMPTY_BENEFICIARY_RECONCILIATION_SUMMARY;
			this.updatePreflightAlerts(preflight);
			this.loadPreflightRows();
		} else {
			this.preflightSummary = null;
			this.preflightEligibleRows = [];
			this.preflightPendingRows = [];
			this.preflightOnHoldRows = [];
			this.preflightRowsLoadedSessionId = null;
			this.unidentifiedOrganizationGroups = [];
			this.unidentifiedOrganizationGroupsLoadedSessionId = null;
			this.preflightUnknownCnpjs = [];
		}
		this.syncQueryParams({
			comparisonId: session.id,
			page: 1,
		});
	}

	isPreflightOnlySession(
		session: BeneficiaryReconciliationSessionListItem,
	): boolean {
		return Boolean(this.getSessionPreflight(session) && session.total === 0);
	}

	ngOnDestroy(): void {
		this.itemsRequestSubscription?.unsubscribe();
		this.bulkActionAvailabilityRequestSubscription?.unsubscribe();
		this.stopProcessingPolling();
		this.remoteDraftSave$.complete();
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	ngOnInit(): void {
		this.initializeRemoteDraftPersistence();

		this.activatedRoute.params
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((params) => {
				const organizationId = params['organization_id'];

				if (!organizationId) {
					return;
				}

				if (this.organizationId && this.organizationId !== organizationId) {
					this.stopProcessingPolling();
					this.activeProcessingLoadedOrganizationId = null;
				}

				this.organizationId = organizationId;
				this.loadSelectedOrganizationContext(organizationId);

				if (this.ensureAllowedOrganization()) {
					this.loadActiveProcessingForOrganization(organizationId);
				}
			});

		this.activatedRoute.queryParams
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((params) => {
				if (!this.organizationId || !this.ensureAllowedOrganization()) {
					return;
				}

				this.applyQueryState(params);

				if (
					this.getStringValue(params.reprocessPreflight) === 'true' &&
					this.comparisonId
				) {
					this.onReprocessPreflightAfterSubCreation();
				}
			});

		this.filterForm.valueChanges
			.pipe(
				debounceTime(400),
				distinctUntilChanged(
					(current, previous) =>
						JSON.stringify(current) === JSON.stringify(previous),
				),
				takeUntil(this.unsubscribe$),
			)
			.subscribe(() => {
				if (!this.comparisonId) {
					return;
				}

				this.syncQueryParams({
					page: 1,
				});
			});
	}

	onApplyFilters(): void {
		if (!this.comparisonId || !this.organizationId) {
			return;
		}

		this.syncQueryParams({
			page: 1,
		});
	}

	onChangeItemsPage(page: number): void {
		if (!this.comparisonId || page === this.itemsCurrentPage) {
			return;
		}

		this.syncQueryParams({ page });
	}

	onApplyTopCard(): void {
		if (this.isProcessingReconciliation || this.hasUnknownApplyResult) {
			return;
		}

		if (this.isUncomparedLinesActive) {
			void this.applyPreflightPendingChanges('UNCOMPARED_LINES');

			return;
		}

		if (this.isUnidentifiedOrganizationsActive) {
			void this.applyPreflightPendingChanges('UNIDENTIFIED_ORGANIZATIONS');

			return;
		}

		this.onOpenApplyPreview();
	}

	onVerifyUnknownApplyResult(): void {
		if (
			!this.unknownApplySessionId ||
			!this.unknownApplyContext ||
			this.isVerifyingUnknownApplyResult
		) {
			return;
		}

		this.verifyUnknownApplyResult(
			this.unknownApplySessionId,
			this.unknownApplyContext,
		);
	}

	onExportActiveTopCard(): void {
		if (this.isUncomparedLinesActive) {
			this.onExportPreflightPendencies('UNCOMPARED_LINES');

			return;
		}

		if (this.isUnidentifiedOrganizationsActive) {
			this.onExportPreflightPendencies('UNIDENTIFIED_ORGANIZATIONS');

			return;
		}

		this.onExportDuplicateInFile();
	}

	onImportActiveTopCard(files: FileList | null): void {
		if (
			this.isUnidentifiedOrganizationsActive ||
			this.isProcessingReconciliation
		) {
			return;
		}

		if (this.isUncomparedLinesActive) {
			this.onImportPreflightPendencies(files);

			return;
		}

		this.onImportDuplicateReturn(files);
	}

	async onIgnoreAllActiveTopCard(): Promise<void> {
		if (
			!this.comparisonId ||
			!this.organizationId ||
			this.isBulkCardActionRunning ||
			this.isPendingChangeMutationBlocked
		) {
			return;
		}

		this.isBulkIgnoring = true;
		const pendingChangesSnapshot = this.buildPendingChangesSnapshot();

		try {
			if (this.isUncomparedLinesActive) {
				const rows = this.filteredUncomparedRows.filter(
					(row) => row.status === 'PENDING' && !this.pendingChanges.has(row.id),
				);

				for (const row of rows) {
					this.queuePreflightRowIgnore(row);
				}

				this.snackBarService.showSnack(
					`${rows.length} linha(s) colocada(s) em espera.`,
					'success',
				);
				void this.refreshBulkActionAvailability();

				return;
			}

			if (this.isUnidentifiedOrganizationsActive) {
				const groups = this.unidentifiedOrganizationGroups.filter((group) =>
					this.isUnidentifiedOrganizationGroupActionable(group),
				);

				for (const group of groups) {
					this.queueUnidentifiedOrganizationGroupIgnore(group);
				}

				this.snackBarService.showSnack(
					`${groups.length} organização(ões) colocada(s) em espera.`,
					'success',
				);
				void this.refreshBulkActionAvailability();

				return;
			}

			const response = await firstValueFrom(
				this.beneficiaryReconciliationsService.getGenericBulkCardHoldEligibleItems(
					{
						organizationId: this.organizationId,
						payload: this.buildBulkCardActionRequest('DUPLICATE_IN_FILE'),
						sessionId: this.comparisonId,
					},
				),
			);
			const eligibleItemIds = response.itemIds.filter((itemId) =>
				this.isPendingChangeUnchanged(pendingChangesSnapshot, itemId),
			);

			if (eligibleItemIds.length === 0) {
				this.snackBarService.showSnack(
					'Não há duplicidades elegíveis para colocar em espera.',
					'info',
				);

				return;
			}

			for (const itemId of eligibleItemIds) {
				this.setPendingChange({
					action: 'IGNORE_ITEM',
					actionLabel: 'Em espera',
					field: 'DUPLICATE_IN_FILE',
					itemId,
					targetValue: {},
				});
			}

			this.snackBarService.showSnack(
				`${eligibleItemIds.length} duplicidade(s) colocada(s) em espera.`,
				'success',
			);
			void this.refreshBulkActionAvailability();
		} catch (error) {
			this.snackBarService.showSnack(
				this.getNormalizedBackendMessage(
					error,
					'Erro ao colocar itens na fila de espera.',
				),
				'error',
			);
		} finally {
			this.isBulkIgnoring = false;
		}
	}

	async applyPreflightPendingChanges(
		kind: GenericBeneficiaryReconciliationPreflightExportKind,
	): Promise<void> {
		if (
			!this.comparisonId ||
			!this.organizationId ||
			this.isApplying ||
			this.isProcessingReconciliation
		) {
			return;
		}

		const changes = this.getPendingChangesByPreflightKind(kind);

		if (changes.length === 0) {
			this.snackBarService.showSnack(
				'Não há alterações na fila para aplicar neste card.',
				'info',
			);

			return;
		}

		this.isApplying = true;

		try {
			let processed = 0;
			let failed = 0;

			for (const change of changes) {
				try {
					const shouldContinue = await this.applyPreflightPendingChange(change);

					if (!shouldContinue) {
						return;
					}

					this.pendingChanges.delete(change.itemId);
					processed += 1;
				} catch (error) {
					failed += 1;
				}
			}

			this.persistPendingChanges();
			this.refreshReconciliationData();
			this.snackBarService.showSnack(
				failed > 0
					? `${processed} alteração(ões) aplicada(s) e ${failed} falharam.`
					: `${processed} alteração(ões) aplicada(s) com sucesso.`,
				failed > 0 ? 'info' : 'success',
			);
		} finally {
			this.isApplying = false;
		}
	}

	onChangeSessionsPage(page: number): void {
		if (page === this.sessionsCurrentPage) {
			return;
		}

		this.loadSessions(page);
	}

	onComplementMissingData(files: FileList | null): void {
		if (
			!this.comparisonId ||
			!this.organizationId ||
			!files?.length ||
			this.isComplementingMissingData ||
			this.isProcessingReconciliation
		) {
			return;
		}

		const file = files[0];

		if (!file.name.toLowerCase().endsWith('.xlsx')) {
			this.snackBarService.showSnack(
				'Apenas arquivos .xlsx são aceitos.',
				'error',
			);

			return;
		}

		this.isComplementingMissingData = true;
		this.beneficiaryReconciliationsService
			.complementGenericMissingData({
				file,
				organizationId: this.organizationId,
				sessionId: this.comparisonId,
			})
			.pipe(
				takeUntil(this.unsubscribe$),
				finalize(() => {
					this.isComplementingMissingData = false;
				}),
			)
			.subscribe({
				error: (error) => {
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Erro ao processar o complemento de dados.',
						),
						'error',
					);
				},
				next: (response: BeneficiaryReconciliationComplementResponse) => {
					const parts: string[] = [];

					if (response.created > 0) {
						parts.push(`${response.created} criado(s)`);
					}

					if (response.complemented > 0) {
						parts.push(`${response.complemented} complementado(s)`);
					}

					if (response.stillIncomplete > 0) {
						parts.push(`${response.stillIncomplete} ainda incompleto(s)`);
					}

					if (response.failed > 0) {
						parts.push(`${response.failed} falhou`);
					}

					if (response.skipped > 0) {
						parts.push(`${response.skipped} ignorado(s)`);
					}

					const message =
						parts.length > 0
							? `Complemento processado: ${parts.join(', ')}.`
							: 'Complemento processado sem alterações.';

					this.snackBarService.showSnack(
						message,
						response.failed > 0 ? 'info' : 'success',
					);
					this.loadItems({ refreshPendingTotal: true });
				},
			});
	}

	onImportDuplicateReturn(files: FileList | null): void {
		if (
			!this.comparisonId ||
			!this.organizationId ||
			!files?.length ||
			this.isImportingDuplicateReturn ||
			this.isProcessingReconciliation
		) {
			return;
		}

		const file = files[0];

		if (!file.name.toLowerCase().endsWith('.xlsx')) {
			this.snackBarService.showSnack(
				'Apenas arquivos .xlsx são aceitos.',
				'error',
			);

			return;
		}

		this.isImportingDuplicateReturn = true;
		this.beneficiaryReconciliationsService
			.importGenericDuplicateReturn({
				file,
				organizationId: this.organizationId,
				sessionId: this.comparisonId,
			})
			.pipe(
				takeUntil(this.unsubscribe$),
				finalize(() => {
					this.isImportingDuplicateReturn = false;
				}),
			)
			.subscribe({
				error: (error) => {
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Erro ao importar duplicidades resolvidas.',
						),
						'error',
					);
				},
				next: (response: BeneficiaryReconciliationDuplicateReturnResponse) => {
					const parts: string[] = [];

					if (response.resolved > 0) {
						parts.push(`${response.resolved} preparada(s)`);
					}

					if (response.ignored > 0) {
						parts.push(`${response.ignored} pendente(s) sem retorno`);
					}

					if (response.skippedAlreadyResolved > 0) {
						parts.push(`${response.skippedAlreadyResolved} ja resolvida(s)`);
					}

					if (response.errors.length > 0) {
						parts.push(`${response.errors.length} erro(s)`);
					}

					const prefix =
						parts.length > 0
							? `Retorno importado: ${parts.join(', ')}.`
							: 'Retorno importado sem alterações.';

					this.snackBarService.showSnack(
						`${prefix} Nenhum cadastro foi alterado. Clique em Aplicar alterações para gerar as divergências reais.`,
						response.errors.length > 0 ? 'info' : 'success',
					);
					this.loadItems({ refreshPendingTotal: true });
				},
			});
	}

	onImportTitularityReturn(files: FileList | null): void {
		if (
			!this.comparisonId ||
			!this.organizationId ||
			!files?.length ||
			this.isImportingTitularityReturn ||
			this.isProcessingReconciliation
		) {
			return;
		}

		const file = files[0];

		if (!file.name.toLowerCase().endsWith('.xlsx')) {
			this.snackBarService.showSnack(
				'Apenas arquivos .xlsx sao aceitos.',
				'error',
			);

			return;
		}

		this.isImportingTitularityReturn = true;
		this.beneficiaryReconciliationsService
			.importGenericTitularityReturn({
				file,
				organizationId: this.organizationId,
				sessionId: this.comparisonId,
			})
			.pipe(
				takeUntil(this.unsubscribe$),
				finalize(() => {
					this.isImportingTitularityReturn = false;
				}),
			)
			.subscribe({
				error: (error) => {
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Erro ao importar retorno de titularidade.',
						),
						'error',
					);
				},
				next: (response: BeneficiaryReconciliationTitularityImportResponse) => {
					const results = Array.isArray(response.results)
						? response.results
						: [];
					const succeededItemIds = results
						.filter((result) => result.status === 'SUCCESS')
						.map((result) => this.getStringValue(result.itemId))
						.filter((itemId) => Boolean(itemId));

					for (const itemId of succeededItemIds) {
						this.removePendingChange(itemId);
					}

					const message = `Retorno de titularidade importado: ${response.succeeded} pronto(s) para aplicar, ${response.failed} erro(s). Nenhum cadastro foi alterado.`;

					this.snackBarService.showSnack(
						message,
						response.failed > 0 ? 'info' : 'success',
					);
					this.loadItems({ refreshPendingTotal: true });
				},
			});
	}

	onReprocessPreflightAfterSubCreation(): void {
		if (
			!this.organizationId ||
			!this.comparisonId ||
			this.isReprocessingPreflight
		) {
			return;
		}

		this.isReprocessingPreflight = true;
		this.beneficiaryReconciliationsService
			.reprocessGenericPreflight({
				organizationId: this.organizationId,
				sessionId: this.comparisonId,
			})
			.pipe(
				takeUntil(this.unsubscribe$),
				finalize(() => {
					this.isReprocessingPreflight = false;
				}),
			)
			.subscribe({
				error: (error) => {
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Erro ao reprocessar a sessão de reconciliação.',
						),
						'error',
					);
				},
				next: (preflight) => {
					this.updatePreflightAlerts(preflight);
					this.loadPreflightRows();
					this.loadItems({ refreshPendingTotal: true });
					this.snackBarService.showSnack(
						'Sessão reprocessada com sucesso.',
						'success',
					);
					void this.router.navigate([], {
						queryParams: { reprocessPreflight: null },
						queryParamsHandling: 'merge',
						relativeTo: this.activatedRoute,
						replaceUrl: true,
					});
				},
			});
	}

	onClearFilters(): void {
		this.filterForm.patchValue(
			{
				carrier: '',
				field: '',
				search: '',
				status: '',
			},
			{ emitEvent: false },
		);

		if (!this.comparisonId || !this.organizationId) {
			return;
		}

		this.syncQueryParams({
			page: 1,
		});
	}

	async onCompare(): Promise<void> {
		if (
			!this.canCompare ||
			!this.organizationId ||
			this.isComparing ||
			this.isUploadActionProcessing ||
			this.isProcessingReconciliation
		) {
			return;
		}

		if (this.isFlexibleUploadMode) {
			if (this.confirmedMapping) {
				this.confirmFlexibleMapping(this.confirmedMapping);

				return;
			}

			if (this.mappingAnalysis) {
				this.openMappingModal(this.mappingAnalysis);

				return;
			}

			this.analyzeFlexibleMapping();

			return;
		}

		if (this.comparisonId || this.preflightSummary) {
			return;
		}

		if (
			this.isBradescoSpgStandardImport &&
			!(await this.confirmBradescoSpgProductValidationWithLock())
		) {
			return;
		}

		this.isComparing = true;
		this.resetGenericPreflightStateForNewSession();

		this.createStandardPreflight()
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: (error) => {
					this.isComparing = false;
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Erro ao classificar as pendências de comparação.',
						),
						'error',
					);
				},
				next: (response) => {
					this.handleGenericPreflightCreated(response);
					this.clearStandardUploadState();
				},
			});
	}

	async onUploadAction(): Promise<void> {
		if (this.isBaseReplacementMode) {
			await this.onReplaceBase();

			return;
		}

		await this.onCompare();
	}

	async onReplaceBase(): Promise<void> {
		if (this.isValidatingBradescoSpgProducts) {
			return;
		}

		if (!this.canReplaceBase || !this.organizationId || !this.comparisonId) {
			this.snackBarService.showSnack(this.replaceBaseDisabledReason, 'info');

			return;
		}

		if (
			this.isBradescoSpgStandardImport &&
			!(await this.confirmBradescoSpgProductValidationWithLock())
		) {
			return;
		}

		this.isReplacingBase = true;
		this.beneficiaryReconciliationsService
			.replaceGenericPreflightBase({
				dentalFile: this.dentalFile,
				healthFile: this.healthFile,
				importModel: this.standardImportModel,
				organizationId: this.organizationId,
				removedWalletKinds: this.getRemovedPersistedWalletKinds(),
				scopeMode: this.effectiveScopeMode,
				sessionId: this.comparisonId,
			})
			.pipe(
				finalize(() => {
					this.isReplacingBase = false;
				}),
				takeUntil(this.unsubscribe$),
			)
			.subscribe({
				error: (error) => {
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Não foi possível substituir a base desta sessão.',
						),
						'error',
					);
				},
				next: (response) => {
					this.selectedHistorySession = null;
					this.handleGenericPreflightCreated(response);
					this.clearStandardUploadState();
					this.snackBarService.showSnack(
						'Base substituída. A comparação foi reiniciada.',
						'success',
					);
				},
			});
	}

	onUploadTriggerClick(event: Event, fieldName: GenericUploadFileField): void {
		if (!this.isUploadTriggerBlocked(fieldName)) {
			if (event instanceof KeyboardEvent) {
				event.preventDefault();
				document
					.getElementById(
						fieldName === 'healthFile' ? 'health-upload' : 'dental-upload',
					)
					?.click();
			}

			return;
		}

		event.preventDefault();
		this.snackBarService.showSnack(
			this.getUploadTriggerTitle(fieldName) ?? this.replaceBaseDisabledReason,
			'info',
		);
	}

	onStandardImportModelChange(model: StandardImportModel): void {
		if (
			model === this.standardImportModel ||
			this.isUploadActionProcessing ||
			this.isProcessingReconciliation
		) {
			return;
		}

		this.standardImportModel = model;
		this.removeInvalidStandardFilesForSelectedModel();
	}

	onScopeModeChange(scopeMode: BeneficiaryReconciliationScopeMode): void {
		if (
			scopeMode === this.scopeMode ||
			this.isUploadActionProcessing ||
			this.isProcessingReconciliation ||
			this.isScopeModeSelectionLocked
		) {
			return;
		}

		this.scopeMode = scopeMode;
	}

	onUploadModeChange(mode: ReconciliationUploadMode): void {
		if (
			mode === this.uploadMode ||
			this.isUploadActionProcessing ||
			this.isProcessingReconciliation
		) {
			return;
		}

		this.uploadMode = mode;

		if (this.isStandardUploadMode) {
			this.clearFlexibleUploadState();

			return;
		}

		this.clearStandardUploadState();
	}

	private handleGenericPreflightCreated(
		response: GenericPreflightCreationResponse,
	): void {
		const createdSessionScopeMode = this.effectiveScopeMode;

		this.resetGenericPreflightStateForNewSession();
		this.createdSessionScopeModeFallback = {
			scopeMode: createdSessionScopeMode,
			sessionId: response.sessionId,
		};
		this.items = [];
		this.comparisonId = response.sessionId;
		this.itemsCount = 0;
		this.itemsCurrentPage = 1;
		this.itemsTotalPages = 0;
		this.sessionPendingTotal = 0;
		this.summary = EMPTY_BENEFICIARY_RECONCILIATION_SUMMARY;
		this.filterForm.patchValue(
			{
				carrier: '',
				field: '',
				limit: this.getLimit(),
				search: '',
				status: '',
			},
			{ emitEvent: false },
		);
		this.latestQuerySignature = this.buildQuerySignature({
			carrier: '',
			comparisonId: response.sessionId,
			field: '',
			limit: this.getLimit(),
			page: 1,
			search: '',
			status: BeneficiaryReconciliationItemStatusFilter.ALL,
		});
		this.updatePreflightAlerts(response.preflight);
		this.runGenericCompareForCurrentSession(response.preflight);
	}

	onRunGenericCompare(): void {
		if (
			!this.comparisonId ||
			!this.organizationId ||
			this.selectedOrganizationIsMatriz === null ||
			this.isComparing ||
			this.isProcessingReconciliation
		) {
			return;
		}

		this.runGenericCompareForCurrentSession();
	}

	private runGenericCompareForCurrentSession(
		preflightAfterCompare?: GenericBeneficiaryReconciliationPreflightSummary,
	): void {
		if (
			!this.comparisonId ||
			!this.organizationId ||
			this.selectedOrganizationIsMatriz === null
		) {
			return;
		}

		const sessionId = this.comparisonId;

		this.isComparing = true;
		this.pendingChanges.clear();
		this.persistPendingChanges();
		this.lastReportUrl = null;
		this.beneficiaryReconciliationsService
			.compareGenericSession({
				organizationId: this.organizationId,
				...this.getSessionScopeModeRequest(preflightAfterCompare),
				sessionId,
			})
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: (error) => {
					this.isComparing = false;
					const message = this.getNormalizedBackendMessage(
						error,
						'Erro ao comparar a sessão de reconciliação.',
					);

					this.snackBarService.showSnack(
						message.toLowerCase().includes('já possui itens de comparação')
							? 'Esta sessão já possui itens de comparação gerados. Para comparar novamente, crie uma nova sessão.'
							: message,
						'error',
					);
				},
				next: (
					response:
						| BeneficiaryReconciliationItemsResponse
						| BeneficiaryReconciliationProcessingStartResponse,
				) => {
					if ('processingId' in response) {
						if (preflightAfterCompare) {
							this.updatePreflightAlerts(preflightAfterCompare);
						}

						this.syncQueryParams({
							comparisonId: sessionId,
							page: 1,
						});
						this.startProcessingPolling({
							processingId: response.processingId,
							sessionId,
							type: 'COMPARE',
						});
						this.snackBarService.showSnack(
							'Estamos processando. Isso pode levar alguns minutos.',
							'info',
						);

						return;
					}

					this.isComparing = false;
					if (preflightAfterCompare) {
						this.updatePreflightAlerts(preflightAfterCompare);
					}
					this.loadPreflightRows();
					this.items = response.items;
					this.comparisonId = response.comparisonId;
					this.primePendingChangesFromItems(response.items);
					this.itemsCount = response.total;
					this.itemsCurrentPage = response.page;
					this.itemsTotalPages = response.totalPages;
					this.summary = response.summary;
					this.refreshCurrentSessionSummary();
					void this.refreshSessionPendingTotal();
					this.snackBarService.showSnack(
						'Comparação concluída com sucesso.',
						'success',
					);
					if (this.showRecentSessions) {
						this.loadSessions(1);
					}
					this.syncQueryParams({
						comparisonId: response.comparisonId,
						page: response.page,
					});
				},
			});
	}

	onCreateSubFromUnknownCnpj(cnpj: string): void {
		if (!this.organizationId || !this.comparisonId) {
			return;
		}

		const normalizedCnpj = cnpj.replace(/\D/g, '');

		if (!normalizedCnpj) {
			this.snackBarService.showSnack(
				'Esta organização não possui CNPJ para criar uma sub.',
				'error',
			);

			return;
		}

		this.beneficiaryReconciliationsService
			.getGenericCreateSubPrefill({
				cnpj: normalizedCnpj,
				organizationId: this.organizationId,
				sessionId: this.comparisonId,
			})
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: (error) => {
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Erro ao preparar o cadastro da nova sub.',
						),
						'error',
					);
				},
				next: (prefill) => {
					void this.router.navigate(['/organizacoes', 'cadastro'], {
						queryParams: {
							reconciliationCnpj: prefill.cnpj || normalizedCnpj,
							reconciliationMatrizOrganizationId: prefill.matrizOrganizationId,
							reconciliationReturnOrganizationId: this.organizationId,
							reconciliationSessionId: this.comparisonId,
						},
					});
				},
			});
	}

	// eslint-disable-next-line sonarjs/no-identical-functions
	onLimitChange(): void {
		if (!this.comparisonId) {
			return;
		}

		this.syncQueryParams({
			page: 1,
		});
	}

	onOpenApplyPreview(): void {
		const currentField = this.currentApplyField;

		if (
			!this.comparisonId ||
			!currentField ||
			this.isApplying ||
			this.hasUnknownApplyResult ||
			this.isProcessingReconciliation
		) {
			return;
		}

		void this.openApplyPreviewWithAudit(this.comparisonId, [currentField]);
	}

	async onBulkIgnoreCurrentSection(): Promise<void> {
		const field = this.currentApplyField;

		if (
			!this.comparisonId ||
			!this.organizationId ||
			this.isBulkCardActionRunning ||
			this.isPendingChangeMutationBlocked ||
			!field
		) {
			return;
		}

		this.isBulkIgnoring = true;
		const pendingChangesSnapshot = this.buildPendingChangesSnapshot();

		try {
			const response = await firstValueFrom(
				this.beneficiaryReconciliationsService.getGenericBulkCardHoldEligibleItems(
					{
						organizationId: this.organizationId,
						payload: this.buildBulkCardActionRequest(field),
						sessionId: this.comparisonId,
					},
				),
			);
			const eligibleItemIds = response.itemIds.filter((itemId) =>
				this.isPendingChangeUnchanged(pendingChangesSnapshot, itemId),
			);

			if (eligibleItemIds.length === 0) {
				this.snackBarService.showSnack(
					'Não há itens elegíveis para colocar em espera nesta seção.',
					'info',
				);

				return;
			}

			for (const itemId of eligibleItemIds) {
				this.setPendingChange({
					action: 'IGNORE_ITEM',
					actionLabel: 'Em espera',
					field,
					itemId,
					targetValue: {},
				});
			}

			this.snackBarService.showSnack(
				field === 'DUPLICATE_IN_FILE'
					? `${eligibleItemIds.length} duplicidade(s) colocada(s) em espera.`
					: `${eligibleItemIds.length} item(ns) colocado(s) em espera.`,
				'success',
			);
			void this.refreshBulkActionAvailability();
			this.loadItems();
		} catch (error) {
			this.snackBarService.showSnack(
				this.getNormalizedBackendMessage(
					error,
					'Erro ao colocar itens em espera.',
				),
				'error',
			);
		} finally {
			this.isBulkIgnoring = false;
		}
	}

	async onIgnoreAllCasesInSelectedCard(): Promise<void> {
		if (this.activeCardKey === 'DUPLICATE_IN_FILE') {
			await this.ignoreAllItemsByFieldDefinitively('DUPLICATE_IN_FILE');

			return;
		}

		if (this.isTopCardActive) {
			await this.ignoreAllActiveTopCardDefinitively();

			return;
		}

		const field = this.currentApplyField;

		await this.ignoreAllItemsByFieldDefinitively(field);
	}

	private async ignoreAllItemsByFieldDefinitively(
		field: BeneficiaryReconciliationField | null,
	): Promise<void> {
		if (
			!this.comparisonId ||
			!this.organizationId ||
			this.isBulkCardActionRunning ||
			!field
		) {
			return;
		}

		const confirmed = await this.confirmBulkReconciliationAction({
			confirmButtonText: 'Ignorar casos',
			message:
				'Todos os casos pendentes, em fila e com falha deste card serão marcados como ignorados neste bate. Nenhuma alteração será aplicada na plataforma. Deseja continuar?',
			title: 'Ignorar todos os casos',
		});

		if (!confirmed) {
			return;
		}

		this.isBulkIgnoringAll = true;

		try {
			const response = await firstValueFrom(
				this.beneficiaryReconciliationsService.ignoreGenericBulkCardEligibleItems(
					{
						organizationId: this.organizationId,
						payload: this.buildBulkCardActionRequest(field),
						sessionId: this.comparisonId,
					},
				),
			);

			if (
				response.status === 'empty' ||
				response.itemIds.length === 0 ||
				!response.processingId
			) {
				this.snackBarService.showSnack(
					'Não há casos ignoráveis neste card.',
					'info',
				);
				this.isBulkIgnoringAll = false;

				return;
			}

			this.isBulkIgnoringAll = false;
			this.startProcessingPolling({
				applyContext: {
					itemIds: response.itemIds,
				},
				processingId: response.processingId,
				sessionId: this.comparisonId,
				type: 'APPLY',
			});
			this.snackBarService.showSnack(
				'Estamos processando. Isso pode levar alguns minutos.',
				'info',
			);
		} catch (error) {
			this.isBulkIgnoringAll = false;
			this.snackBarService.showSnack(
				this.getNormalizedBackendMessage(
					error,
					'Erro ao ignorar os casos deste card.',
				),
				'error',
			);
		}
	}

	private applyDefinitiveIgnoreItems(
		items: BeneficiaryReconciliationItem[],
	): void {
		if (!this.comparisonId || !this.organizationId || items.length === 0) {
			this.isBulkIgnoringAll = false;

			return;
		}

		const sessionId = this.comparisonId;
		const itemIds = Array.from(new Set(items.map((item) => item.id)));

		this.beneficiaryReconciliationsService
			.applyGenericSession({
				organizationId: this.organizationId,
				payload: {
					items: itemIds.map((itemId) => ({
						action: 'IGNORE_ITEM',
						itemId,
						targetValue: {
							operationalAction: RECONCILIATION_OPERATIONAL_ACTION_IGNORE,
							resolutionMode: RECONCILIATION_RESOLUTION_MODE_KEEP_PLATFORM,
						},
					})),
					...this.getSessionScopeModeRequest(),
				},
				sessionId,
			})
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: (error) => {
					this.isBulkIgnoringAll = false;

					if (this.isRecoverableProcessingPollingError(error)) {
						this.recoverUnknownApplyResult(sessionId, { itemIds });

						return;
					}

					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Erro ao ignorar os itens selecionados.',
						),
						'error',
					);
				},
				next: (response) => {
					this.isBulkIgnoringAll = false;
					this.pruneAppliedPendingChanges({ itemIds });
					this.startProcessingPolling({
						applyContext: {
							itemIds,
						},
						processingId: response.processingId,
						sessionId,
						type: 'APPLY',
					});
					this.snackBarService.showSnack(
						'Estamos processando. Isso pode levar alguns minutos.',
						'info',
					);
				},
			});
	}

	private async confirmBulkReconciliationAction(
		data: ConfirmationDialogData,
	): Promise<boolean> {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			data,
			disableClose: true,
			width: '440px',
		});

		return Boolean(await firstValueFrom(dialogRef.afterClosed()));
	}

	private async confirmBradescoSpgProductValidationWithLock(): Promise<boolean> {
		if (this.isValidatingBradescoSpgProducts) {
			return false;
		}

		this.isValidatingBradescoSpgProducts = true;

		try {
			return await this.confirmBradescoSpgProductValidation();
		} finally {
			this.isValidatingBradescoSpgProducts = false;
		}
	}

	private async confirmBradescoSpgProductValidation(): Promise<boolean> {
		if (!this.isBradescoSpgStandardImport || !this.organizationId) {
			return true;
		}

		try {
			const response = await firstValueFrom(
				this.beneficiaryReconciliationsService.validateBradescoSpgProducts({
					dentalFile: this.dentalFile,
					healthFile: this.healthFile,
					organizationId: this.organizationId,
					scopeMode: this.effectiveScopeMode,
				}),
			);
			const alert = response.results.find((result) =>
				BeneficiaryReconciliationComponent.hasBradescoSpgProductWarning(result),
			);

			if (!alert) {
				return true;
			}

			return await this.confirmBulkReconciliationAction({
				confirmButtonText: 'Continuar',
				message:
					BeneficiaryReconciliationComponent.getBradescoSpgProductWarningMessage(
						alert,
					),
				title: 'Conferir produto da base',
			});
		} catch (error) {
			this.snackBarService.showSnack(
				this.getNormalizedBackendMessage(
					error,
					'Não foi possível validar os produtos da base Bradesco SPG.',
				),
				'error',
			);

			return false;
		}
	}

	private static hasBradescoSpgProductWarning(
		result: BradescoSpgProductValidationResult,
	): boolean {
		switch (result.status) {
			case 'ONLY_HEALTH_IN_DENTAL_FLOW':
			case 'ONLY_DENTAL_IN_HEALTH_FLOW':
			case 'MIXED_HEALTH_DENTAL':
				return true;
			default:
				return false;
		}
	}

	private static getBradescoSpgProductWarningMessage(
		result: BradescoSpgProductValidationResult,
	): string {
		switch (result.status) {
			case 'ONLY_HEALTH_IN_DENTAL_FLOW':
			case 'ONLY_DENTAL_IN_HEALTH_FLOW':
			case 'MIXED_HEALTH_DENTAL':
				return BeneficiaryReconciliationComponent.BRADESCO_SPG_PRODUCT_WARNING_MESSAGE;
			case 'NONE':
				return '';
			default:
				return '';
		}
	}

	private async confirmSingleDefinitiveIgnore(): Promise<boolean> {
		return this.confirmBulkReconciliationAction({
			confirmButtonText: 'Ignorar caso',
			message:
				'Este caso será marcado como ignorado neste bate. Nenhuma alteração será aplicada na plataforma. Deseja continuar?',
			title: 'Ignorar caso',
		});
	}

	private async confirmAndIgnoreItemDefinitively(
		item: BeneficiaryReconciliationItem,
	): Promise<void> {
		if (
			!this.comparisonId ||
			!this.organizationId ||
			this.isPendingChangeMutationBlocked ||
			this.isBulkCardActionRunning
		) {
			return;
		}

		const confirmed = await this.confirmSingleDefinitiveIgnore();

		if (!confirmed) {
			return;
		}

		this.isBulkIgnoringAll = true;
		this.applyDefinitiveIgnoreItems([item]);
	}

	private async confirmAndIgnorePreflightRow(
		row: GenericBeneficiaryReconciliationPreflightRow,
	): Promise<void> {
		if (!this.comparisonId || !this.organizationId) {
			return;
		}

		const confirmed = await this.confirmSingleDefinitiveIgnore();

		if (confirmed) {
			this.onIgnorePreflightRow(row);
		}
	}

	private async confirmAndIgnoreUnidentifiedOrganizationGroup(
		group: GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup,
	): Promise<void> {
		if (!this.comparisonId || !this.organizationId) {
			return;
		}

		const confirmed = await this.confirmSingleDefinitiveIgnore();

		if (confirmed) {
			this.onIgnoreUnidentifiedOrganizationGroup(group);
		}
	}

	private async ignoreAllActiveTopCardDefinitively(): Promise<void> {
		if (
			!this.comparisonId ||
			!this.organizationId ||
			this.isBulkCardActionRunning
		) {
			return;
		}

		const confirmed = await this.confirmBulkReconciliationAction({
			confirmButtonText: 'Ignorar casos',
			message:
				'Todos os casos pendentes, em fila e com falha deste card serão marcados como ignorados neste bate. Nenhuma alteração será aplicada na plataforma. Deseja continuar?',
			title: 'Ignorar todos os casos',
		});

		if (!confirmed) {
			return;
		}

		this.isBulkIgnoringAll = true;

		try {
			const pendingRowIds = await this.getActiveTopCardDefinitiveIgnoreRowIds();

			if (pendingRowIds.length === 0) {
				this.snackBarService.showSnack(
					'Não há casos ignoráveis neste card.',
					'info',
				);

				return;
			}

			const updatedRows = await Promise.all(
				pendingRowIds.map(async (rowId) =>
					firstValueFrom(
						this.beneficiaryReconciliationsService.ignoreGenericPreflightPendency(
							{
								organizationId: this.organizationId,
								rowId,
								sessionId: this.comparisonId as string,
							},
						),
					),
				),
			);

			this.updatePreflightRows(updatedRows);

			for (const rowId of pendingRowIds) {
				this.pendingChanges.delete(rowId);
			}

			if (this.isUnidentifiedOrganizationsActive) {
				const ignoredRowIds = new Set(pendingRowIds);
				this.filteredUnidentifiedOrganizationGroups
					.filter((group) =>
						group.pendingRowIds.some((rowId) => ignoredRowIds.has(rowId)),
					)
					.forEach((group) => this.pendingChanges.delete(group.key));
			}

			this.persistPendingChanges();
			this.loadPreflightRows();
			this.loadItems({ refreshPendingTotal: true });
			this.snackBarService.showSnack(
				`${pendingRowIds.length} caso(s) ignorado(s).`,
				'success',
			);
		} catch (error) {
			this.snackBarService.showSnack(
				this.getNormalizedBackendMessage(
					error,
					'Erro ao ignorar os casos deste card.',
				),
				'error',
			);
		} finally {
			this.isBulkIgnoringAll = false;
		}
	}

	private async getActiveTopCardDefinitiveIgnoreRowIds(): Promise<string[]> {
		if (this.isUncomparedLinesActive) {
			const rows =
				await this.listAllPendingPreflightRowsByKind('UNCOMPARED_LINES');

			return Array.from(
				new Set(
					rows.filter((row) => row.status === 'PENDING').map((row) => row.id),
				),
			);
		}

		if (this.isUnidentifiedOrganizationsActive) {
			return Array.from(
				new Set(
					this.filteredUnidentifiedOrganizationGroups.reduce<string[]>(
						(rowIds, group) => [...rowIds, ...group.pendingRowIds],
						[],
					),
				),
			);
		}

		return [];
	}

	private async listAllPendingPreflightRowsByKind(
		kind: GenericBeneficiaryReconciliationPreflightExportKind,
	): Promise<GenericBeneficiaryReconciliationPreflightRow[]> {
		if (!this.organizationId || !this.comparisonId) {
			return [];
		}

		const limit = 100;
		const firstPage = await firstValueFrom(
			this.beneficiaryReconciliationsService.listGenericPreflightPendencies({
				kind,
				limit,
				organizationId: this.organizationId,
				page: 1,
				sessionId: this.comparisonId,
				status: 'PENDING',
			}),
		);
		const rows = [...firstPage.items];

		for (let page = 2; page <= firstPage.totalPages; page += 1) {
			const response = await firstValueFrom(
				this.beneficiaryReconciliationsService.listGenericPreflightPendencies({
					kind,
					limit,
					organizationId: this.organizationId,
					page,
					sessionId: this.comparisonId,
					status: 'PENDING',
				}),
			);

			rows.push(...response.items);
		}

		return rows;
	}

	onExportDuplicateInFile(): void {
		if (
			!this.comparisonId ||
			!this.organizationId ||
			!this.canShowDuplicateExportButton ||
			this.isExportingDuplicateInFile
		) {
			return;
		}

		this.isExportingDuplicateInFile = true;
		this.beneficiaryReconciliationsService
			.exportGenericDuplicateInFile({
				organizationId: this.organizationId,
				sessionId: this.comparisonId,
			})
			.pipe(
				finalize(() => {
					this.isExportingDuplicateInFile = false;
				}),
				takeUntil(this.unsubscribe$),
			)
			.subscribe({
				error: (error) => {
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Não foi possível exportar as duplicidades não resolvidas.',
						),
						'error',
					);
				},
				next: (response) => {
					const url = this.getStringValue(response?.url);

					if (!url) {
						this.snackBarService.showSnack(
							'Não foi possível obter o link do arquivo de duplicidades.',
							'error',
						);

						return;
					}

					const link = document.createElement('a');

					link.setAttribute('href', url);
					link.setAttribute('download', 'duplicidades-nao-resolvidas.xlsx');
					document.body.appendChild(link);
					link.click();
					link.remove();
				},
			});
	}

	onExportActiveMissingInDb(): void {
		if (
			!this.comparisonId ||
			!this.organizationId ||
			!this.canShowActiveMissingExportButton ||
			this.isExportingActiveMissingInDb
		) {
			return;
		}

		this.isExportingActiveMissingInDb = true;
		this.beneficiaryReconciliationsService
			.exportGenericActiveMissingInDb({
				organizationId: this.organizationId,
				sessionId: this.comparisonId,
			})
			.pipe(
				finalize(() => {
					this.isExportingActiveMissingInDb = false;
				}),
				takeUntil(this.unsubscribe$),
			)
			.subscribe({
				error: (error) => {
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Não foi possível exportar os dados faltantes.',
						),
						'error',
					);
				},
				next: (response) => {
					const url = this.getStringValue(response?.url);

					if (!url) {
						this.snackBarService.showSnack(
							'Não foi possível obter o link do arquivo de dados faltantes.',
							'error',
						);

						return;
					}

					const link = document.createElement('a');

					link.setAttribute('href', url);
					link.setAttribute('download', 'layout-dados-faltantes.xlsx');
					document.body.appendChild(link);
					link.click();
					link.remove();
				},
			});
	}

	onExportOperationalDivergence(): void {
		const field = this.currentApplyField;

		if (
			!this.comparisonId ||
			!this.organizationId ||
			!this.canShowOperationalDivergenceExportButton ||
			!this.isOperationalDivergenceExportField(field) ||
			this.isExportingOperationalDivergence
		) {
			return;
		}

		this.isExportingOperationalDivergence = true;
		this.beneficiaryReconciliationsService
			.exportGenericOperationalDivergence({
				field,
				organizationId: this.organizationId,
				sessionId: this.comparisonId,
			})
			.pipe(
				finalize(() => {
					this.isExportingOperationalDivergence = false;
				}),
				takeUntil(this.unsubscribe$),
			)
			.subscribe({
				error: (error) => {
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Não foi possível exportar o relatório operacional.',
						),
						'error',
					);
				},
				next: (response) => {
					const url = this.getStringValue(response?.url);

					if (!url) {
						this.snackBarService.showSnack(
							'Não foi possível obter o link do relatório operacional.',
							'error',
						);

						return;
					}

					const link = document.createElement('a');

					link.setAttribute('href', url);
					link.setAttribute(
						'download',
						this.getOperationalDivergenceExportFileName(field),
					);
					document.body.appendChild(link);
					link.click();
					link.remove();
				},
			});
	}

	isExportingHistory(sessionId: string): boolean {
		return this.isExportingHistorySessionId === sessionId;
	}

	onExportHistorySession(
		session: BeneficiaryReconciliationSessionListItem,
		event: Event,
	): void {
		event.stopPropagation();
		event.preventDefault();

		if (
			!session?.id ||
			!this.organizationId ||
			this.isExportingHistorySessionId ||
			!this.canExportHistorySession(session)
		) {
			return;
		}

		this.isExportingHistorySessionId = session.id;
		this.beneficiaryReconciliationsService
			.exportGenericHistory({
				organizationId: this.organizationId,
				sessionId: session.id,
			})
			.pipe(
				finalize(() => {
					this.isExportingHistorySessionId = null;
				}),
				takeUntil(this.unsubscribe$),
			)
			.subscribe({
				error: (error) => {
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Não foi possível exportar o histórico da sessão.',
						),
						'error',
					);
				},
				next: (response) => {
					const url = this.getStringValue(response?.url);

					if (!url) {
						this.snackBarService.showSnack(
							'Não foi possível obter o link do histórico.',
							'error',
						);

						return;
					}

					const link = document.createElement('a');

					link.setAttribute('href', url);
					link.setAttribute('download', 'layout-historico-reconciliacao.xlsx');
					document.body.appendChild(link);
					link.click();
					link.remove();
				},
			});
	}

	onOpenResolution(item: BeneficiaryReconciliationItem): void {
		if (this.isPendingChangeMutationBlocked) {
			return;
		}

		switch (item.field) {
			case 'PLAN_MISMATCH':
			case 'CARD_MISMATCH':
				this.openValueResolution(item);

				return;
			case 'TITULARITY_MISMATCH':
				this.openTitularityResolution(item);

				return;
			case 'ACTIVE_MISSING_IN_DB':
			case 'INACTIVE_MISSING_IN_FILE':
			case 'DUPLICATE_IN_FILE':
			case 'ORGANIZATION_MISMATCH':
				this.openInlineResolution(item);

				break;
			default:
		}
	}

	canApplySingleItem(item: BeneficiaryReconciliationItem): boolean {
		const change = this.getEffectivePendingChange(item);
		const request = change ? this.buildApplyRequestFromChange(change) : null;

		return Boolean(
			this.comparisonId &&
				this.organizationId &&
				!this.isApplying &&
				!this.isProcessingReconciliation &&
				!this.isItemOnHold(item) &&
				request &&
				request.action !== 'IGNORE_ITEM',
		);
	}

	canIgnoreItem(item: BeneficiaryReconciliationItem): boolean {
		return (
			item.status === BeneficiaryReconciliationItemStatus.PENDING &&
			!this.isPendingChangeMutationBlocked &&
			!this.isItemOnHold(item)
		);
	}

	onApplySingleItem(item: BeneficiaryReconciliationItem): void {
		if (
			!this.comparisonId ||
			!this.organizationId ||
			this.isApplying ||
			this.isProcessingReconciliation
		) {
			return;
		}

		const sessionId = this.comparisonId;
		const change = this.getEffectivePendingChange(item);
		const request = change ? this.buildApplyRequestFromChange(change) : null;

		if (
			!request ||
			(request.action === 'IGNORE_ITEM' &&
				!this.isKeepPlatformPendingChange(change ?? undefined))
		) {
			this.snackBarService.showSnack(
				'Não há alteração válida para aplicar neste item.',
				'info',
			);

			return;
		}

		this.isApplying = true;
		this.beneficiaryReconciliationsService
			.applyGenericSession({
				organizationId: this.organizationId,
				payload: {
					items: [request],
					...this.getSessionScopeModeRequest(),
				},
				sessionId,
			})
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: (error) => {
					if (this.isRecoverableProcessingPollingError(error)) {
						this.recoverUnknownApplyResult(sessionId, {
							itemIds: [item.id],
						});

						return;
					}

					this.isApplying = false;
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Erro ao aplicar o item da reconciliação.',
						),
						'error',
					);
				},
				next: (
					response:
						| BeneficiaryReconciliationApplyResponse
						| BeneficiaryReconciliationProcessingStartResponse,
				) => {
					if ('processingId' in response) {
						this.startProcessingPolling({
							applyContext: {
								itemIds: [item.id],
							},
							processingId: response.processingId,
							sessionId,
							type: 'APPLY',
						});
						this.snackBarService.showSnack(
							'Estamos processando. Isso pode levar alguns minutos.',
							'info',
						);

						return;
					}

					this.isApplying = false;
					const failedItem = response.items.find(
						(result) => result.itemId === item.id,
					);
					const pendingChange = this.pendingChanges.get(item.id);

					if (
						failedItem?.status === BeneficiaryReconciliationItemStatus.FAILED &&
						pendingChange
					) {
						this.pendingChanges.set(item.id, pendingChange);
					} else {
						this.pendingChanges.delete(item.id);
					}

					this.persistPendingChanges();
					this.lastReportUrl = response.reportUrl;
					this.snackBarService.showSnack(
						response.failed > 0
							? 'O item foi processado com falha.'
							: 'Item aplicado com sucesso.',
						response.failed > 0 ? 'info' : 'success',
					);
					if (this.showRecentSessions) {
						this.loadSessions(this.sessionsCurrentPage);
					}
					this.loadItems({ refreshPendingTotal: true });
				},
			});
	}

	onIgnoreItem(item: BeneficiaryReconciliationItem): void {
		if (!this.canIgnoreItem(item)) {
			return;
		}

		this.setPendingChange({
			action: 'IGNORE_ITEM',
			actionLabel: this.resolvePendingActionLabel(item, 'IGNORE_ITEM'),
			field: item.field,
			itemId: item.id,
			targetValue: {},
		});
		this.snackBarService.showSnack('Item colocado em espera.', 'success');
		void this.refreshBulkActionAvailability();
	}

	onOpenHistory(item: BeneficiaryReconciliationItem): void {
		if (!this.isHistoryAvailable(item)) {
			return;
		}

		this.dialog.open(BeneficiaryReconciliationHistoryDetailModalComponent, {
			data: { item },
			maxWidth: '96vw',
			width: '760px',
		});
	}

	onRemovePendingChange(itemId: string): void {
		this.removePendingChange(itemId);
	}

	onToggleRecentSessions(): void {
		this.showRecentSessions = !this.showRecentSessions;

		if (
			this.showRecentSessions &&
			!this.isLoadingSessions &&
			this.recentSessions.length === 0
		) {
			this.loadSessions(1);
		}
	}

	removeSelectedFile(fieldName: GenericUploadFileField): void {
		if (fieldName === 'healthFile') {
			this.healthFile = null;

			return;
		}

		this.dentalFile = null;
	}

	removePersistedOriginalFile(file: ReconciliationOriginalFile): void {
		this.hiddenPersistedOriginalFileKeys.add(this.getOriginalFileKey(file));
	}

	removeFlexibleFile(index: number): void {
		this.flexibleFiles = this.flexibleFiles.filter(
			(file, fileIndex) => fileIndex !== index,
		);
		this.confirmedMapping = null;
		this.mappingAnalysis = null;
	}

	isUploadTriggerBlocked(fieldName: GenericUploadFileField): boolean {
		if (!this.isBaseReplacementMode) {
			return this.isUploadSelectionBlocked;
		}

		const isStandardFileField =
			fieldName === 'healthFile' || fieldName === 'dentalFile';

		return (
			(isStandardFileField && this.isUploadSelectionBlocked) ||
			this.hasAppliedItemsInCurrentSession ||
			this.preflightSummary?.importModel === 'ASSISTED_MAPPING'
		);
	}

	onToggleReconciliationCardGuidance(
		card: ReconciliationCard,
		event: Event,
	): void {
		event.stopPropagation();
		const wasExpanded = this.expandedGuidanceCardKey === card.key;

		this.onSelectReconciliationCard(card);
		this.expandedGuidanceCardKey = wasExpanded ? null : card.key;
	}

	onSelectReconciliationCard(card: ReconciliationCard): void {
		this.activeCardKey = card.key;
		this.expandedGuidanceCardKey =
			this.expandedGuidanceCardKey === card.key
				? this.expandedGuidanceCardKey
				: null;
		this.itemsCurrentPage = 1;

		if (card.field) {
			this.selectSummaryCard(card.field);

			return;
		}

		this.filterForm.patchValue(
			{
				field: card.key,
			},
			{ emitEvent: false },
		);
		this.syncQueryParams({
			page: 1,
		});

		if (
			card.preflightKind === 'UNIDENTIFIED_ORGANIZATIONS' &&
			this.unidentifiedOrganizationGroupsLoadedSessionId !== this.comparisonId
		) {
			this.loadUnidentifiedOrganizationGroups();
		}

		if (
			card.preflightKind === 'UNCOMPARED_LINES' &&
			this.preflightRowsLoadedSessionId !== this.comparisonId
		) {
			this.loadPreflightRows();
		}
	}

	onExportPreflightPendencies(
		kind: GenericBeneficiaryReconciliationPreflightExportKind,
	): void {
		if (
			!this.comparisonId ||
			!this.organizationId ||
			this.isExportingPreflightPendencies
		) {
			return;
		}

		this.isExportingPreflightPendencies = true;
		this.beneficiaryReconciliationsService
			.exportGenericPreflightPendencies({
				kind,
				organizationId: this.organizationId,
				sessionId: this.comparisonId,
			})
			.pipe(
				finalize(() => {
					this.isExportingPreflightPendencies = false;
				}),
				takeUntil(this.unsubscribe$),
			)
			.subscribe({
				error: (error) => {
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Não foi possível exportar as pendências de comparação.',
						),
						'error',
					);
				},
				next: (response) => {
					const url = this.getStringValue(response?.url);

					if (!url) {
						this.snackBarService.showSnack(
							'Não foi possível obter o link do arquivo de pendências.',
							'error',
						);

						return;
					}

					const link = document.createElement('a');
					const fileName =
						kind === 'UNCOMPARED_LINES'
							? 'linhas-nao-comparadas.xlsx'
							: 'organizacoes-nao-identificadas.xlsx';

					link.setAttribute('href', url);
					link.setAttribute('download', fileName);
					document.body.appendChild(link);
					link.click();
					link.remove();
				},
			});
	}

	onImportPreflightPendencies(files: FileList | null): void {
		if (
			!this.comparisonId ||
			!this.organizationId ||
			!files?.length ||
			this.isImportingPreflightPendencies
		) {
			return;
		}

		const file = files[0];

		if (!file.name.toLowerCase().endsWith('.xlsx')) {
			this.snackBarService.showSnack(
				'Apenas arquivos .xlsx são aceitos.',
				'error',
			);

			return;
		}

		this.isImportingPreflightPendencies = true;
		this.beneficiaryReconciliationsService
			.importGenericPreflightPendencies({
				file,
				organizationId: this.organizationId,
				sessionId: this.comparisonId,
			})
			.pipe(
				finalize(() => {
					this.isImportingPreflightPendencies = false;
				}),
				takeUntil(this.unsubscribe$),
			)
			.subscribe({
				error: (error) => {
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Erro ao importar correções das pendências de comparação.',
						),
						'error',
					);
				},
				next: (response) => {
					const message = `Correções importadas: ${response.succeeded} sucesso(s), ${response.failed} erro(s).`;

					this.snackBarService.showSnack(
						message,
						response.failed > 0 ? 'info' : 'success',
					);
					this.loadPreflightRows();
					this.loadItems({ refreshPendingTotal: true });
				},
			});
	}

	onReviewPreflightRow(
		row: GenericBeneficiaryReconciliationPreflightRow,
	): void {
		if (this.isPendingChangeMutationBlocked) {
			return;
		}

		const dialogRef = this.dialog.open(
			BeneficiaryReconciliationPreflightRowModalComponent,
			{
				data: {
					pendingChange: this.pendingChanges.get(row.id) ?? null,
					row,
				},
				maxWidth: '96vw',
				width: '620px',
			},
		);

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(
				(result: BeneficiaryReconciliationPreflightRowResult | undefined) => {
					if (!result) {
						return;
					}

					if ('removePending' in result) {
						this.removePendingChange(row.id);

						return;
					}

					if (result.action === 'IGNORE_ITEM') {
						if (result.onHold) {
							this.queuePreflightRowIgnore(row);
						} else {
							void this.confirmAndIgnorePreflightRow(row);
						}

						return;
					}

					this.setPendingChange({
						action: 'RESOLVE_PREFLIGHT_ROW',
						actionLabel: 'Correção informada',
						itemId: row.id,
						preflightKind: 'UNCOMPARED_LINES',
						targetValue: {
							carrier: result.carrier,
							cpf: result.cpf,
						},
					});
				},
			);
	}

	onReviewUnidentifiedOrganizationGroup(
		group: GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup,
	): void {
		if (this.isPendingChangeMutationBlocked) {
			return;
		}

		const dialogRef = this.dialog.open(
			BeneficiaryReconciliationPreflightOrganizationModalComponent,
			{
				data: {
					group,
					pendingChange: this.pendingChanges.get(group.key) ?? null,
				},
				maxWidth: '96vw',
				width: '620px',
			},
		);

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(
				(
					result:
						| BeneficiaryReconciliationPreflightOrganizationResult
						| undefined,
				) => {
					if (!result) {
						return;
					}

					if ('removePending' in result) {
						this.removePendingChange(group.key);

						return;
					}

					switch (result.action) {
						case 'IGNORE_ITEM':
							if (result.onHold) {
								this.queueUnidentifiedOrganizationGroupIgnore(group);
							} else {
								void this.confirmAndIgnoreUnidentifiedOrganizationGroup(group);
							}
							break;
						case 'TREAT_AS_MATRIX':
							this.setPendingChange({
								action: 'TREAT_AS_MATRIX',
								actionLabel: 'Tratar como matriz',
								itemId: group.key,
								preflightKind: 'UNIDENTIFIED_ORGANIZATIONS',
								targetValue: {
									cnpj: group.cnpj ?? undefined,
									rowIds: group.pendingRowIds,
								},
							});
							break;
						case 'CREATE_SUB':
							{
								const cnpj = result.cnpj ?? group.cnpj;

								if (!cnpj) {
									this.snackBarService.showSnack(
										'Esta organização não possui CNPJ para criar uma sub.',
										'error',
									);

									return;
								}

								this.removePendingChange(group.key);
								this.persistPendingChanges();
								this.onCreateSubFromUnknownCnpj(cnpj);
							}
							break;
						case 'RESUME_PREFLIGHT_ROW':
							this.setPendingChange({
								action: 'RESUME_PREFLIGHT_ROW',
								actionLabel: 'Retomar',
								itemId: group.key,
								preflightKind: 'UNIDENTIFIED_ORGANIZATIONS',
								targetValue: {
									cnpj: group.cnpj ?? undefined,
									rowIds: group.onHoldRowIds,
								},
							});
							break;
						default:
					}
				},
			);
	}

	onHoldPreflightRow(row: GenericBeneficiaryReconciliationPreflightRow): void {
		if (!this.organizationId || !this.comparisonId) {
			return;
		}

		this.beneficiaryReconciliationsService
			.holdGenericPreflightPendency({
				organizationId: this.organizationId,
				rowId: row.id,
				sessionId: this.comparisonId,
			})
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: (error) => {
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Erro ao colocar a linha em espera.',
						),
						'error',
					);
				},
				next: (updatedRow) => {
					this.updatePreflightRow(updatedRow);
					this.snackBarService.showSnack(
						'Linha colocada em espera.',
						'success',
					);
				},
			});
	}

	onIgnorePreflightRow(
		row: GenericBeneficiaryReconciliationPreflightRow,
	): void {
		if (!this.organizationId || !this.comparisonId) {
			return;
		}

		this.beneficiaryReconciliationsService
			.ignoreGenericPreflightPendency({
				organizationId: this.organizationId,
				rowId: row.id,
				sessionId: this.comparisonId,
			})
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: (error) => {
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Erro ao ignorar a pendencia.',
						),
						'error',
					);
				},
				next: (updatedRow) => {
					this.updatePreflightRow(updatedRow);
					this.loadItems({ refreshPendingTotal: true });
					this.snackBarService.showSnack('Pendencia ignorada.', 'success');
				},
			});
	}

	onResolvePreflightRow(
		row: GenericBeneficiaryReconciliationPreflightRow,
	): void {
		if (!this.organizationId || !this.comparisonId) {
			return;
		}

		const cpf = this.getStringValue(this.preflightRowCpfEdits[row.id]);
		const carrier = this.getStringValue(this.preflightRowCarrierEdits[row.id]);

		if (!cpf && !carrier) {
			this.snackBarService.showSnack(
				'Informe CPF ou operadora para resolver a linha.',
				'error',
			);

			return;
		}

		this.beneficiaryReconciliationsService
			.resolveGenericPreflightPendency({
				carrier: carrier || undefined,
				cpf: cpf || undefined,
				organizationId: this.organizationId,
				rowId: row.id,
				sessionId: this.comparisonId,
			})
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: (error) => {
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Erro ao resolver a linha não comparada.',
						),
						'error',
					);
				},
				next: (updatedRow) => {
					this.updatePreflightRow(updatedRow);
					this.loadItems({ refreshPendingTotal: true });
					this.snackBarService.showSnack(
						'Linha reprocessada com sucesso.',
						'success',
					);
				},
			});
	}

	onResolvePreflightCpf(
		row: GenericBeneficiaryReconciliationPreflightRow,
	): void {
		this.onResolvePreflightRow(row);
	}

	onResumePreflightRow(
		row: GenericBeneficiaryReconciliationPreflightRow,
	): void {
		if (!this.organizationId || !this.comparisonId) {
			return;
		}

		this.beneficiaryReconciliationsService
			.resumeGenericPreflightPendency({
				organizationId: this.organizationId,
				rowId: row.id,
				sessionId: this.comparisonId,
			})
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: (error) => {
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Erro ao retomar a linha em espera.',
						),
						'error',
					);
				},
				next: (updatedRow) => {
					this.updatePreflightRow(updatedRow);
					this.loadItems({ refreshPendingTotal: true });
					this.snackBarService.showSnack('Linha retomada.', 'success');
				},
			});
	}

	onTreatPreflightRowAsMatrix(
		row: GenericBeneficiaryReconciliationPreflightRow,
	): void {
		if (!this.organizationId || !this.comparisonId) {
			return;
		}

		this.beneficiaryReconciliationsService
			.resolveGenericPreflightPendency({
				organizationId: this.organizationId,
				rowId: row.id,
				sessionId: this.comparisonId,
				treatAsMatrix: true,
			})
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: (error) => {
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Erro ao tratar a organização como matriz.',
						),
						'error',
					);
				},
				next: (updatedRow) => {
					this.updatePreflightRow(updatedRow);
					this.loadItems({ refreshPendingTotal: true });
					this.snackBarService.showSnack(
						'Organização tratada como matriz.',
						'success',
					);
				},
			});
	}

	onCreateSubFromPreflightRow(
		row: GenericBeneficiaryReconciliationPreflightRow,
	): void {
		const cnpj = this.getStringValue(row.data?.sourceOrganizationCnpj);

		if (!cnpj) {
			this.snackBarService.showSnack(
				'Esta linha não possui CNPJ para criar uma sub.',
				'info',
			);

			return;
		}

		this.onCreateSubFromUnknownCnpj(cnpj);
	}

	onCreateSubFromUnidentifiedOrganizationGroup(
		group: GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup,
	): void {
		if (!group.canCreateSub || !group.cnpj) {
			return;
		}

		this.onCreateSubFromUnknownCnpj(group.cnpj);
	}

	onTreatUnidentifiedOrganizationGroupAsMatrix(
		group: GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup,
	): void {
		if (!group.canTreatAsMatrix) {
			return;
		}

		if (group.cnpj && group.issueCodes.includes('CNPJ_NOT_REGISTERED')) {
			this.onTreatUnknownCnpjAsMatrix(group.cnpj);

			return;
		}

		void this.runPreflightGroupAction(
			group.pendingRowIds,
			(rowId) =>
				this.beneficiaryReconciliationsService.resolveGenericPreflightPendency({
					organizationId: this.organizationId,
					rowId,
					sessionId: this.comparisonId as string,
					treatAsMatrix: true,
				}),
			'Organização tratada como matriz.',
			'Erro ao tratar a organização como matriz.',
		);
	}

	onHoldUnidentifiedOrganizationGroup(
		group: GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup,
	): void {
		void this.runPreflightGroupAction(
			group.pendingRowIds,
			(rowId) =>
				this.beneficiaryReconciliationsService.holdGenericPreflightPendency({
					organizationId: this.organizationId,
					rowId,
					sessionId: this.comparisonId as string,
				}),
			'Organização colocada em espera.',
			'Erro ao colocar a organização em espera.',
		);
	}

	onIgnoreUnidentifiedOrganizationGroup(
		group: GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup,
	): void {
		void this.runPreflightGroupAction(
			group.pendingRowIds,
			(rowId) =>
				this.beneficiaryReconciliationsService.ignoreGenericPreflightPendency({
					organizationId: this.organizationId,
					rowId,
					sessionId: this.comparisonId as string,
				}),
			'Organização ignorada.',
			'Erro ao ignorar a organização.',
		);
	}

	onResumeUnidentifiedOrganizationGroup(
		group: GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup,
	): void {
		void this.runPreflightGroupAction(
			group.onHoldRowIds,
			(rowId) =>
				this.beneficiaryReconciliationsService.resumeGenericPreflightPendency({
					organizationId: this.organizationId,
					rowId,
					sessionId: this.comparisonId as string,
				}),
			'Organização retomada.',
			'Erro ao retomar a organização.',
		);
	}

	onTreatUnknownCnpjAsMatrix(cnpj: string): void {
		if (!this.organizationId || !this.comparisonId) {
			return;
		}

		this.beneficiaryReconciliationsService
			.treatUnknownCnpjAsMatrix({
				cnpj,
				organizationId: this.organizationId,
				sessionId: this.comparisonId,
			})
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: (error) => {
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Erro ao tratar o CNPJ como matriz.',
						),
						'error',
					);
				},
				next: () => {
					this.snackBarService.showSnack(
						'CNPJ tratado como matriz.',
						'success',
					);
					this.onReprocessPreflightAfterSubCreation();
				},
			});
	}

	setPreflightCpfEdit(rowId: string, value: string): void {
		this.preflightRowCpfEdits[rowId] = value;
	}

	setPreflightCarrierEdit(rowId: string, value: string): void {
		this.preflightRowCarrierEdits[rowId] = value;
	}

	canCreateSubFromPreflightRow(
		row: GenericBeneficiaryReconciliationPreflightRow,
	): boolean {
		return (
			this.hasAnyPreflightIssue(row, ['CNPJ_NOT_REGISTERED', 'UNKNOWN_CNPJ']) &&
			Boolean(this.getStringValue(row.data?.sourceOrganizationCnpj)) &&
			!this.hasAnyPreflightIssue(row, ['CNPJ_OUTSIDE_CONTEXT'])
		);
	}

	canTreatPreflightRowAsMatrix(
		row: GenericBeneficiaryReconciliationPreflightRow,
	): boolean {
		return !this.hasAnyPreflightIssue(row, [
			'CNPJ_OUTSIDE_CONTEXT',
			'SUBINVOICE_NOT_FOUND',
		]);
	}

	selectSummaryCard(field: BeneficiaryReconciliationField): void {
		if (!this.comparisonId) {
			return;
		}

		this.activeCardKey = field;
		this.filterForm.patchValue(
			{
				field,
			},
			{ emitEvent: false },
		);
		this.syncQueryParams({
			page: 1,
		});
	}

	trackByField(
		index: number,
		card: { field: BeneficiaryReconciliationField; label: string },
	): string {
		return card.field;
	}

	trackByReconciliationCard(index: number, card: ReconciliationCard): string {
		return card.key;
	}

	trackByFile(index: number, file: File): string {
		return `${file.name}-${index}`;
	}

	trackByOriginalFile(index: number, file: ReconciliationOriginalFile): string {
		return file.id ?? `${file.fileName}-${index}`;
	}

	trackByItem(index: number, item: BeneficiaryReconciliationItem): string {
		return item.id;
	}

	trackByPendingSummary(index: number, summary: PendingActionSummary): string {
		return `${summary.action}:${summary.label}`;
	}

	trackByPreflightRow(
		index: number,
		row: GenericBeneficiaryReconciliationPreflightRow,
	): string {
		return row.id;
	}

	trackByUnidentifiedOrganizationGroup(
		index: number,
		group: GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup,
	): string {
		return group.key;
	}

	trackBySession(
		index: number,
		session: BeneficiaryReconciliationSessionListItem,
	): string {
		return session.id;
	}

	trackByScopeModeOption(
		index: number,
		option: ReconciliationScopeOption,
	): string {
		return option.value;
	}

	getPreflightIssueLabels(
		row: GenericBeneficiaryReconciliationPreflightRow,
	): string {
		const labels = this.getPreflightIssueCodeLabels(
			this.getVisiblePreflightIssueCodes(
				[...row.issues, ...row.warnings].map((issue) => issue.code),
			),
		);

		return labels || '-';
	}

	getPreflightIssueCodeLabels(codes: string[]): string {
		const labels = Array.from(
			new Set(
				codes.map((code) => this.getPreflightIssueLabel(code)).filter(Boolean),
			),
		);

		return labels.length > 0 ? labels.join(', ') : '-';
	}

	getUnidentifiedOrganizationIdentifierLabel(
		group: GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup,
	): string {
		if (this.isSubinvoiceNotFoundGroup(group)) {
			const subinvoiceNumber =
				this.getStringValue(group.subinvoiceNumber) ||
				this.getStringValue(group.normalizedSubinvoiceNumber);

			return subinvoiceNumber ? `Subfatura ${subinvoiceNumber}` : 'Subfatura -';
		}

		return group.cnpj || '-';
	}

	getPreflightRowSourceLabel(
		row: GenericBeneficiaryReconciliationPreflightRow,
	): string {
		const source = row.data?.source;

		if (!source) {
			return '-';
		}

		return `${source.fileName} - linha ${source.rowNumber}`;
	}

	getPreflightRowStatusLabel(
		row: GenericBeneficiaryReconciliationPreflightRow,
	): string {
		const pendingChange = this.pendingChanges.get(row.id);

		if (pendingChange) {
			return this.isPendingChangeOnHold(pendingChange)
				? 'Em espera'
				: 'Na fila';
		}

		if (this.isIgnoredPreflightRow(row)) {
			return 'Ignorado';
		}

		if (row.status === 'ELIGIBLE') {
			return 'Aplicado';
		}

		return row.status === 'ON_HOLD' ? 'Em espera' : 'Pendente';
	}

	getPreflightRowStatusTone(
		row: GenericBeneficiaryReconciliationPreflightRow,
	): 'queued' | 'review' | 'success' | 'waiting' {
		const pendingChange = this.pendingChanges.get(row.id);

		if (pendingChange) {
			return this.isPendingChangeOnHold(pendingChange) ? 'waiting' : 'queued';
		}

		if (this.isIgnoredPreflightRow(row)) {
			return 'success';
		}

		if (row.status === 'ELIGIBLE') {
			return 'success';
		}

		return row.status === 'ON_HOLD' ? 'waiting' : 'review';
	}

	isPreflightRowCompleted(
		row: GenericBeneficiaryReconciliationPreflightRow,
	): boolean {
		return row.status === 'ELIGIBLE' || this.isIgnoredPreflightRow(row);
	}

	getUnidentifiedOrganizationStatusLabel(
		group: GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup,
	): string {
		const pendingChange =
			this.getActiveUnidentifiedOrganizationPendingChange(group);

		if (pendingChange) {
			return this.isPendingChangeOnHold(pendingChange)
				? 'Em espera'
				: 'Na fila';
		}

		if (group.status === 'ELIGIBLE') {
			return 'Aplicado';
		}

		if (this.isUnidentifiedOrganizationGroupFullyIgnored(group)) {
			return 'Ignorado';
		}

		return group.pendingCount > 0 ? 'Pendente' : 'Em espera';
	}

	private isUnidentifiedOrganizationGroupFullyIgnored(
		group: GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup,
	): boolean {
		return (
			group.status !== 'ELIGIBLE' &&
			(group.ignoredCount ?? 0) > 0 &&
			group.pendingCount === 0 &&
			group.onHoldCount === 0
		);
	}

	private isSubinvoiceNotFoundGroup(
		group: GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup,
	): boolean {
		return group.issueCodes.includes('SUBINVOICE_NOT_FOUND');
	}

	getUnidentifiedOrganizationStatusTone(
		group: GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup,
	): 'queued' | 'review' | 'success' | 'waiting' {
		const pendingChange =
			this.getActiveUnidentifiedOrganizationPendingChange(group);

		if (pendingChange) {
			return this.isPendingChangeOnHold(pendingChange) ? 'waiting' : 'queued';
		}

		if (group.status === 'ELIGIBLE') {
			return 'success';
		}

		if (this.isUnidentifiedOrganizationGroupFullyIgnored(group)) {
			return 'success';
		}

		return group.pendingCount > 0 ? 'review' : 'waiting';
	}

	getWalletKindLabel(
		value?: GenericBeneficiaryReconciliationWalletKind | null,
	): string {
		switch (value) {
			case 'DENTAL':
				return 'Dental';
			case 'HEALTH':
				return 'Saúde';
			default:
				return '-';
		}
	}

	getItemErrorMessage(item: BeneficiaryReconciliationItem): string {
		return this.normalizeBackendText(
			item.errorMessage ||
				this.getStringValue(item.context?.resolutionError) ||
				'',
		);
	}

	hasItemErrorMessage(item: BeneficiaryReconciliationItem): boolean {
		return Boolean(this.getItemErrorMessage(item));
	}

	private appendSummary(
		grouped: Map<string, PendingActionSummary>,
		action: PendingChangeAction,
		actionLabel?: string,
	): void {
		const label =
			this.getStringValue(actionLabel) || this.getPendingActionLabel(action);
		const key = `${action}:${label}`;
		const current = grouped.get(key);

		if (current) {
			current.count += 1;

			return;
		}

		grouped.set(key, {
			action,
			count: 1,
			label,
		});
	}

	private buildApplyRequestFromChange(
		change: PendingItemChange,
	):
		| NonNullable<BeneficiaryReconciliationApplyRequest['items']>[number]
		| null {
		const action = this.parseApplyAction(change.action);

		if (!action) {
			return null;
		}

		const targetValue = this.sanitizeApplyTargetValue(change.targetValue);

		return {
			action,
			itemId: change.itemId,
			targetValue:
				action === 'IGNORE_ITEM' &&
				targetValue.resolutionMode !==
					RECONCILIATION_RESOLUTION_MODE_KEEP_PLATFORM
					? {}
					: targetValue,
		};
	}

	private buildPendingChangesSnapshot(): Map<string, string> {
		return new Map(
			Array.from(this.pendingChanges.entries()).map(([itemId, change]) => [
				itemId,
				this.buildPendingChangeSignature(change),
			]),
		);
	}

	private buildPendingChangeSignature(change: PendingItemChange): string {
		return JSON.stringify({
			action: change.action,
			actionLabel: change.actionLabel,
			field: change.field,
			itemId: change.itemId,
			preflightKind: change.preflightKind,
			targetValue: this.sanitizeApplyTargetValue(change.targetValue),
		});
	}

	private isPendingChangeUnchanged(
		snapshot: Map<string, string>,
		itemId: string,
	): boolean {
		const currentChange = this.pendingChanges.get(itemId);

		return (
			snapshot.get(itemId) ===
			(currentChange
				? this.buildPendingChangeSignature(currentChange)
				: undefined)
		);
	}

	private getEffectivePendingChange(
		item: BeneficiaryReconciliationItem,
	): PendingItemChange | null {
		return (
			this.pendingChanges.get(item.id) ?? this.getSuggestedPendingChange(item)
		);
	}

	private initializeRemoteDraftPersistence(): void {
		if (this.remoteDraftPersistenceInitialized) {
			return;
		}

		this.remoteDraftPersistenceInitialized = true;
		this.remoteDraftSave$
			.pipe(
				debounceTime(RECONCILIATION_DRAFT_SAVE_DEBOUNCE_MS),
				switchMap(() => this.savePendingChangesDraftRemote()),
				takeUntil(this.unsubscribe$),
			)
			.subscribe();
	}

	private savePendingChangesDraftRemote(): Observable<unknown> {
		if (
			!this.organizationId ||
			!this.comparisonId ||
			(this.pendingChanges.size === 0 &&
				!this.getCurrentActiveApplyProcessingDraft())
		) {
			return EMPTY;
		}

		return this.beneficiaryReconciliationsService
			.saveGenericDraft({
				draft: this.buildPendingChangesDraft(),
				organizationId: this.organizationId,
				sessionId: this.comparisonId,
			})
			.pipe(catchError(() => EMPTY));
	}

	private persistActiveApplyProcessingContext(
		sessionId: string,
		processingId: string,
		context?: ProcessingApplyContext,
	): void {
		const activeApplyProcessing = this.buildActiveApplyProcessingDraft(
			processingId,
			context,
		);

		if (!activeApplyProcessing) {
			return;
		}

		sessionStorage.setItem(
			this.getActiveApplyProcessingStorageKey(sessionId),
			JSON.stringify(activeApplyProcessing),
		);

		if (!this.organizationId || !this.comparisonId) {
			return;
		}

		this.beneficiaryReconciliationsService
			.saveGenericDraft({
				draft: this.buildPendingChangesDraft(),
				organizationId: this.organizationId,
				sessionId,
			})
			.pipe(
				catchError(() => EMPTY),
				takeUntil(this.unsubscribe$),
			)
			.subscribe();
	}

	private clearActiveApplyProcessingContext(sessionId: string): void {
		sessionStorage.removeItem(
			this.getActiveApplyProcessingStorageKey(sessionId),
		);
	}

	private deletePendingChangesDraftRemote(sessionId: string): void {
		if (!this.organizationId) {
			return;
		}

		this.beneficiaryReconciliationsService
			.deleteGenericDraft({
				organizationId: this.organizationId,
				sessionId,
			})
			.pipe(
				catchError(() => EMPTY),
				takeUntil(this.unsubscribe$),
			)
			.subscribe();
	}

	private buildPendingChangesDraft(): BeneficiaryReconciliationDraft {
		const activeApplyProcessing = this.getCurrentActiveApplyProcessingDraft();

		return {
			...(activeApplyProcessing ? { activeApplyProcessing } : {}),
			pendingChanges: this.serializePendingChanges(),
		};
	}

	private serializePendingChanges(): NonNullable<
		BeneficiaryReconciliationDraft['pendingChanges']
	> {
		return Array.from(this.pendingChanges.values()).map((change) => ({
			...change,
			targetValue: this.sanitizeApplyTargetValue(change.targetValue),
		}));
	}

	private buildActiveApplyProcessingDraft(
		processingId: string,
		context?: ProcessingApplyContext,
	): NonNullable<
		BeneficiaryReconciliationDraft['activeApplyProcessing']
	> | null {
		const fields = context?.fields?.length ? [...context.fields] : undefined;
		const itemIds = context?.itemIds?.length ? [...context.itemIds] : undefined;

		if (!fields && !itemIds) {
			return null;
		}

		return {
			...(fields ? { fields } : {}),
			...(itemIds ? { itemIds } : {}),
			processingId,
		};
	}

	private getCurrentActiveApplyProcessingDraft(): NonNullable<
		BeneficiaryReconciliationDraft['activeApplyProcessing']
	> | null {
		if (
			this.activeProcessingType !== 'APPLY' ||
			!this.activeProcessingId ||
			!this.activeApplyContext
		) {
			return null;
		}

		return this.buildActiveApplyProcessingDraft(
			this.activeProcessingId,
			this.activeApplyContext,
		);
	}

	private loadActiveProcessingForOrganization(
		organizationId: string,
		options?: { force?: boolean },
	): void {
		if (
			!options?.force &&
			this.activeProcessingLoadedOrganizationId === organizationId
		) {
			return;
		}

		this.activeProcessingLoadedOrganizationId = organizationId;
		this.beneficiaryReconciliationsService
			.getGenericActiveProcessing({ organizationId })
			.pipe(
				catchError(() => EMPTY),
				takeUntil(this.unsubscribe$),
			)
			.subscribe({
				next: (activeProcessing) => {
					if (activeProcessing?.type === 'APPLY') {
						this.restoreActiveApplyProcessing(organizationId, activeProcessing);

						return;
					}

					this.restoreActiveProcessing(activeProcessing);
				},
			});
	}

	private restoreActiveProcessing(
		activeProcessing: BeneficiaryReconciliationActiveProcessingResponse,
		draft?: BeneficiaryReconciliationDraft | null,
	): void {
		if (!activeProcessing) {
			return;
		}

		if (
			activeProcessing.status !== 'processing' &&
			this.hasConsumedTerminalProcessing(activeProcessing.processingId)
		) {
			return;
		}

		this.restoreProcessingSession(activeProcessing.sessionId, draft);
		const applyContext =
			activeProcessing.type === 'APPLY'
				? this.resolveActiveApplyProcessingContext(activeProcessing, draft)
				: undefined;

		if (activeProcessing.status === 'failed') {
			this.activeProcessingType = activeProcessing.type;
			this.activeApplyContext = applyContext ?? null;
			this.handleProcessingFailed(
				activeProcessing.error ??
					'O processamento da reconciliação falhou. Tente novamente.',
			);
			this.markTerminalProcessingAsConsumed(activeProcessing.processingId);

			return;
		}

		if (activeProcessing.status === 'finished') {
			this.activeProcessingType = activeProcessing.type;
			this.activeApplyContext = applyContext ?? null;
			this.handleProcessingFinished(
				activeProcessing.type,
				activeProcessing.sessionId,
				activeProcessing.data,
			);
			this.markTerminalProcessingAsConsumed(activeProcessing.processingId);

			return;
		}

		this.startProcessingPolling({
			applyContext,
			processingId: activeProcessing.processingId,
			sessionId: activeProcessing.sessionId,
			type: activeProcessing.type,
		});
	}

	private restoreActiveApplyProcessing(
		organizationId: string,
		activeProcessing: Exclude<
			BeneficiaryReconciliationActiveProcessingResponse,
			null
		>,
	): void {
		this.beneficiaryReconciliationsService
			.getGenericDraft({
				organizationId,
				sessionId: activeProcessing.sessionId,
			})
			.pipe(
				catchError(() => of(null)),
				takeUntil(this.unsubscribe$),
			)
			.subscribe({
				next: (draft) => {
					this.restoreActiveProcessing(activeProcessing, draft);
				},
			});
	}

	private restoreProcessingSession(
		sessionId: string,
		draft?: BeneficiaryReconciliationDraft | null,
	): void {
		this.comparisonId = sessionId;
		this.hydratePendingChangesForRestoredSession(sessionId, draft);
		this.syncQueryParams({
			comparisonId: sessionId,
			page: this.itemsCurrentPage,
		});
	}

	private startProcessingPolling(args: {
		applyContext?: ProcessingApplyContext;
		processingId: string;
		sessionId: string;
		type: BeneficiaryReconciliationProcessingType;
	}): void {
		if (!this.organizationId) {
			return;
		}

		this.stopProcessingPolling();
		this.activeProcessingId = args.processingId;
		this.activeProcessingType = args.type;
		this.activeApplyContext = args.applyContext ?? null;
		this.processingErrorMessage = null;

		this.isComparing = args.type === 'COMPARE';
		this.isApplying = args.type === 'APPLY';

		if (args.type === 'APPLY') {
			this.persistActiveApplyProcessingContext(
				args.sessionId,
				args.processingId,
				args.applyContext,
			);
		}

		this.processingPollSubscription = timer(
			RECONCILIATION_PROCESSING_POLLING_INTERVAL_MS,
			RECONCILIATION_PROCESSING_POLLING_INTERVAL_MS,
		)
			.pipe(
				switchMap(() =>
					this.beneficiaryReconciliationsService
						.getGenericProcessingStatus({
							organizationId: this.organizationId,
							processingId: args.processingId,
						})
						.pipe(
							catchError((error: unknown) =>
								this.handleProcessingPollingRequestError(error),
							),
						),
				),
				takeUntil(this.unsubscribe$),
			)
			.subscribe({
				error: (error) => {
					this.handleProcessingFailed(
						this.getNormalizedBackendMessage(
							error,
							'Erro ao acompanhar o processamento da reconciliação.',
						),
					);
				},
				next: (response) => {
					this.resetProcessingPollingFailures();

					if (response.status === 'processing') {
						return;
					}

					if (response.status === 'failed') {
						this.handleProcessingFailed(
							response.error ??
								'O processamento da reconciliação falhou. Tente novamente.',
						);
						this.markTerminalProcessingAsConsumed(args.processingId);

						return;
					}

					this.handleProcessingFinished(
						args.type,
						args.sessionId,
						response.data,
					);
					this.markTerminalProcessingAsConsumed(args.processingId);
				},
			});
	}

	private stopProcessingPolling(): void {
		this.processingPollSubscription?.unsubscribe();
		this.processingPollSubscription = null;
		this.resetProcessingPollingFailures();
		this.activeProcessingId = null;
		this.activeProcessingType = null;
		this.activeApplyContext = null;
		this.isApplying = false;
		this.isComparing = false;
	}

	private handleProcessingPollingRequestError(
		error: unknown,
	): Observable<never> {
		if (!this.isRecoverableProcessingPollingError(error)) {
			throw error;
		}

		this.processingPollingConsecutiveFailures += 1;

		if (
			this.processingPollingConsecutiveFailures <=
			RECONCILIATION_PROCESSING_POLLING_RECOVERABLE_FAILURE_LIMIT
		) {
			return EMPTY;
		}

		throw error;
	}

	private isRecoverableProcessingPollingError(error: unknown): boolean {
		const status = this.getProcessingPollingHttpStatus(error);

		return status === 0 || status === 502 || status === 503 || status === 504;
	}

	private getProcessingPollingHttpStatus(error: unknown): number | null {
		if (!error || typeof error !== 'object' || !('status' in error)) {
			return null;
		}

		const status = (error as { status?: unknown }).status;

		return typeof status === 'number' ? status : null;
	}

	private resetProcessingPollingFailures(): void {
		this.processingPollingConsecutiveFailures = 0;
	}

	private handleProcessingFailed(message: string): void {
		const shouldClearActiveApplyContext =
			this.activeProcessingType === 'APPLY' && Boolean(this.comparisonId);
		const sessionId = this.comparisonId;

		this.stopProcessingPolling();

		if (shouldClearActiveApplyContext && sessionId) {
			this.clearActiveApplyProcessingContext(sessionId);
			this.persistPendingChanges();
		}

		this.processingErrorMessage = this.normalizeBackendText(message);
		this.snackBarService.showSnack(this.processingErrorMessage, 'error');
	}

	private handleProcessingFinished(
		type: BeneficiaryReconciliationProcessingType,
		sessionId: string,
		data?: Record<string, unknown> | null,
	): void {
		const applyContext = this.activeApplyContext;

		this.stopProcessingPolling();
		this.processingErrorMessage = null;

		if (type === 'APPLY') {
			this.clearActiveApplyProcessingContext(sessionId);
			const didPersistPendingChanges = this.pruneAppliedPendingChanges(
				applyContext,
				data,
			);

			if (!didPersistPendingChanges) {
				this.persistPendingChanges();
			}

			this.setReportUrlFromProcessingData(data);

			if (applyContext?.fields?.length) {
				this.resetStatusFilterToAll();
			}
		}

		if (this.comparisonId !== sessionId) {
			this.comparisonId = sessionId;
			this.syncQueryParams({
				comparisonId: sessionId,
				page: this.itemsCurrentPage,
			});
		}

		this.loadPreflightRows();
		this.loadItems({ refreshPendingTotal: true });
		this.refreshCurrentSessionSummary();

		if (this.showRecentSessions) {
			this.loadSessions(this.sessionsCurrentPage);
		}

		this.snackBarService.showSnack(
			this.getProcessingFinishedMessage(type, data),
			this.hasProcessingFailures(data) ? 'info' : 'success',
		);
	}

	private getProcessingFinishedMessage(
		type: BeneficiaryReconciliationProcessingType,
		data?: Record<string, unknown> | null,
	): string {
		if (type === 'COMPARE') {
			return 'Comparação concluída com sucesso.';
		}

		const applied = this.getProcessingNumber(data?.applied);
		const failed = this.getProcessingNumber(data?.failed);

		if (failed && failed > 0) {
			return applied !== null
				? `${applied} alteração(ões) aplicada(s) e ${failed} falharam.`
				: `${failed} alteração(ões) falharam durante a aplicação.`;
		}

		return applied !== null
			? `${applied} alteração(ões) aplicadas com sucesso.`
			: 'Aplicação concluída com sucesso.';
	}

	private getProcessingNumber(value: unknown): number | null {
		const numericValue =
			typeof value === 'number'
				? value
				: typeof value === 'string'
					? Number(value)
					: Number.NaN;

		return Number.isFinite(numericValue) ? numericValue : null;
	}

	private hasProcessingFailures(data?: { failed?: unknown } | null): boolean {
		const failed = this.getProcessingNumber(data?.failed);

		return failed !== null && failed > 0;
	}

	private setReportUrlFromProcessingData(
		data?: Record<string, unknown> | null,
	): void {
		const reportUrl = this.getStringValue(data?.reportUrl);

		if (reportUrl) {
			this.lastReportUrl = reportUrl;
		}
	}

	private pruneAppliedPendingChanges(
		context: ProcessingApplyContext | null,
		data?: Record<string, unknown> | null,
	): boolean {
		if (!context) {
			return false;
		}

		const appliedResultItemIds = this.getAppliedResultItemIds(data);

		if (this.hasProcessingFailures(data) && !appliedResultItemIds) {
			return false;
		}

		const itemIds = new Set(context.itemIds ?? []);
		const fields = new Set(context.fields ?? []);
		let hasChanged = false;

		for (const change of Array.from(this.pendingChanges.values())) {
			const shouldRemoveByItem = appliedResultItemIds
				? appliedResultItemIds.has(change.itemId)
				: itemIds.has(change.itemId);
			const shouldRemoveByField =
				!appliedResultItemIds &&
				fields.size > 0 &&
				Boolean(change.field) &&
				fields.has(change.field as BeneficiaryReconciliationField) &&
				!change.preflightKind &&
				!this.isPendingChangeOnHold(change);

			if (shouldRemoveByItem || shouldRemoveByField) {
				this.pendingChanges.delete(change.itemId);
				hasChanged = true;
			}
		}

		if (hasChanged) {
			this.persistPendingChanges();
		}

		return hasChanged;
	}

	private getAppliedResultItemIds(
		data?: { items?: unknown } | null,
	): Set<string> | null {
		const items = data?.items;

		if (!Array.isArray(items)) {
			return null;
		}

		const resultItems = items.filter(
			(item): item is Record<string, unknown> =>
				Boolean(item) && typeof item === 'object',
		);

		if (resultItems.length === 0) {
			return null;
		}

		return resultItems.reduce<Set<string>>((accumulator, itemRecord) => {
			const itemId = this.getStringValue(itemRecord.itemId);
			const status = this.getStringValue(itemRecord.status);

			if (itemId && status === BeneficiaryReconciliationItemStatus.APPLIED) {
				accumulator.add(itemId);
			}

			return accumulator;
		}, new Set<string>());
	}

	private applyPendingChanges(fields?: BeneficiaryReconciliationField[]): void {
		if (
			!this.comparisonId ||
			!this.organizationId ||
			this.isApplying ||
			this.isProcessingReconciliation
		) {
			return;
		}

		const sessionId = this.comparisonId;
		const fieldSet = fields?.length ? new Set(fields) : null;
		const items: NonNullable<BeneficiaryReconciliationApplyRequest['items']> =
			[];

		for (const change of Array.from(this.pendingChanges.values())) {
			const isOutsideSelectedField =
				fieldSet && (!change.field || !fieldSet.has(change.field));
			const request = this.buildApplyRequestFromChange(change);

			if (!isOutsideSelectedField && request) {
				items.push(request);
			}
		}

		if (items.length === 0) {
			this.snackBarService.showSnack(
				'Não há alterações válidas na fila para confirmar.',
				'info',
			);

			return;
		}

		this.isApplying = true;
		this.beneficiaryReconciliationsService
			.applyGenericSessionConfirmSuggested({
				fields,
				items,
				organizationId: this.organizationId,
				...this.getSessionScopeModeRequest(),
				sessionId,
			})
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: (error) => {
					if (this.isRecoverableProcessingPollingError(error)) {
						this.recoverUnknownApplyResult(sessionId, { fields });

						return;
					}

					this.isApplying = false;
					const manualReviewItemIds =
						this.extractManualReviewItemIdsFromError(error);

					if (manualReviewItemIds.length > 0) {
						this.focusManualReviewItems(manualReviewItemIds);
					}

					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Erro ao aplicar alterações da reconciliação.',
						),
						'error',
					);
				},
				next: (
					response:
						| BeneficiaryReconciliationApplyResponse
						| BeneficiaryReconciliationProcessingStartResponse,
				) => {
					if ('processingId' in response) {
						this.startProcessingPolling({
							applyContext: {
								fields,
							},
							processingId: response.processingId,
							sessionId,
							type: 'APPLY',
						});
						this.snackBarService.showSnack(
							'Estamos processando. Isso pode levar alguns minutos.',
							'info',
						);

						return;
					}

					this.isApplying = false;
					const nextPendingChanges = new Map<string, PendingItemChange>();
					const appliedResultItemIds = this.getAppliedResultItemIds(response);
					const hasFailedItems = response.failed > 0;

					for (const change of Array.from(this.pendingChanges.values())) {
						const isOutsideSelectedField =
							fields?.length &&
							(!change.field || !fields.includes(change.field));

						if (
							isOutsideSelectedField ||
							change.preflightKind ||
							this.isPendingChangeOnHold(change) ||
							(hasFailedItems && !appliedResultItemIds?.has(change.itemId))
						) {
							nextPendingChanges.set(change.itemId, change);
						}
					}

					this.pendingChanges = nextPendingChanges;
					this.persistPendingChanges();
					this.lastReportUrl = response.reportUrl;
					this.snackBarService.showSnack(
						response.failed > 0
							? `${response.applied} alteração(ões) aplicadas e ${response.failed} falharam.`
							: `${response.applied} alteração(ões) aplicadas com sucesso.`,
						response.failed > 0 ? 'info' : 'success',
					);
					if (this.showRecentSessions) {
						this.loadSessions(this.sessionsCurrentPage);
					}
					this.resetStatusFilterToAll();
					this.loadItems({ refreshPendingTotal: true });
				},
			});
	}

	private applyQueryState(params: Params): void {
		const queryState = this.parseQueryState(params);
		const signature = this.buildQuerySignature(queryState);

		if (signature === this.latestQuerySignature) {
			return;
		}

		this.latestQuerySignature = signature;
		this.filterForm.patchValue(
			{
				carrier: queryState.carrier,
				field: queryState.field,
				limit: queryState.limit,
				search: queryState.search,
				status: queryState.status,
			},
			{ emitEvent: false },
		);
		const previousComparisonId = this.comparisonId;
		this.comparisonId = queryState.comparisonId;
		this.itemsCurrentPage = queryState.page;
		this.activeCardKey =
			this.parseReconciliationCategory(queryState.field) ?? 'DUPLICATE_IN_FILE';

		if (previousComparisonId !== queryState.comparisonId) {
			const shouldReloadActiveProcessing = Boolean(
				this.organizationId && this.activeProcessingId,
			);

			this.hiddenPersistedOriginalFileKeys.clear();
			this.loadedSessionAppliedCount = 0;
			this.stopProcessingPolling();
			this.activeProcessingLoadedOrganizationId = null;
			this.hydratePendingChanges(queryState.comparisonId);

			if (shouldReloadActiveProcessing) {
				this.loadActiveProcessingForOrganization(this.organizationId, {
					force: true,
				});
			}
		}

		if (!queryState.comparisonId) {
			this.items = [];
			this.itemsCount = 0;
			this.itemsTotalPages = 0;
			this.highlightedManualReviewItemIds.clear();
			this.pendingChanges.clear();
			this.preflightSummary = null;
			this.preflightEligibleRows = [];
			this.preflightPendingRows = [];
			this.preflightOnHoldRows = [];
			this.preflightRowsLoadedSessionId = null;
			this.unidentifiedOrganizationGroups = [];
			this.unidentifiedOrganizationGroupsLoadedSessionId = null;
			this.loadedSessionAppliedCount = 0;
			this.preflightUnknownCnpjs = [];
			this.selectedHistorySession = null;
			this.sessionPendingTotal = 0;
			this.summary = EMPTY_BENEFICIARY_RECONCILIATION_SUMMARY;

			return;
		}

		if (this.preflightSummary) {
			if (
				this.isUncomparedLinesActive ||
				previousComparisonId !== queryState.comparisonId ||
				this.preflightRowsLoadedSessionId !== queryState.comparisonId
			) {
				this.loadPreflightRows();
			}

			if (this.isItemTableActive) {
				this.loadItems();
			}

			this.selectedHistorySession = null;

			return;
		}

		const selectedSession =
			this.selectedHistorySession?.id === queryState.comparisonId
				? this.selectedHistorySession
				: null;

		this.selectedHistorySession = null;
		this.loadSessionStateOrItems(selectedSession);
	}

	private updatePreflightAlerts(
		preflight: GenericBeneficiaryReconciliationPreflightSummary,
	): void {
		this.syncUploadSelectionFromPreflight(preflight);
		this.syncScopeModeFromPreflight(preflight);
		this.preflightSummary = preflight;
		this.preflightUnknownCnpjs =
			preflight.organizationAlerts?.unknownCnpjs ?? [];
	}

	private syncUploadSelectionFromPreflight(
		preflight: GenericBeneficiaryReconciliationPreflightSummary,
	): void {
		switch (preflight.importModel) {
			case 'AMIL':
				this.uploadMode = 'STANDARD';
				this.standardImportModel = 'AMIL';

				return;
			case 'ASSISTED_MAPPING':
				this.uploadMode = 'FLEXIBLE';
				this.standardImportModel = 'SANUS_STANDARD';

				return;
			case 'BRADESCO_PROVIDER':
				this.uploadMode = 'STANDARD';
				this.standardImportModel = 'BRADESCO_PROVIDER';

				return;
			case 'BRADESCO_SPG':
				this.uploadMode = 'STANDARD';
				this.standardImportModel = 'BRADESCO_SPG';

				return;
			case 'SANUS_STANDARD':
			default:
				this.uploadMode = 'STANDARD';
				this.standardImportModel = 'SANUS_STANDARD';
		}
	}

	private syncScopeModeFromPreflight(
		preflight: GenericBeneficiaryReconciliationPreflightSummary,
	): void {
		if (preflight.scope?.mode) {
			this.scopeMode = preflight.scope.mode;
		}
	}

	private getPreflightIssueLabel(code: string): string {
		switch (code) {
			case 'INVALID_BIRTH_DAY':
				return 'Data de nascimento inválida';
			case 'INVALID_VALIDITY_END_DATE':
				return 'Data fim da vigência inválida';
			case 'MISSING_BIRTH_DAY':
				return 'Data de nascimento ausente';
			case 'MISSING_NAME':
				return 'Nome ausente';
			case 'INVALID_CPF':
				return 'CPF inválido';
			case 'MISSING_CPF':
				return 'CPF ausente';
			case 'CNPJ_NOT_REGISTERED':
			case 'UNKNOWN_CNPJ':
				return 'CNPJ não cadastrado';
			case 'MISSING_CNPJ':
				return 'CNPJ ausente';
			case 'SUBINVOICE_NOT_FOUND':
				return 'Número da subfatura não encontrado';
			case 'CNPJ_OUTSIDE_CONTEXT':
				return 'CNPJ fora do contexto';
			case 'AMBIGUOUS_CARRIER':
			case 'CARRIER_AMBIGUOUS':
				return 'Operadora ambígua';
			case 'MISSING_CARRIER':
				return 'Operadora ausente';
			case 'INVALID_HOLDER_CPF':
				return 'CPF do titular inválido';
			case 'MISSING_HOLDER_CPF':
				return 'CPF do titular ausente';
			case 'HOLDER_NOT_FOUND':
			case 'MISSING_HOLDER':
				return 'Titular não localizado';
			case 'TITULARITY_NOT_IDENTIFIED':
				return 'Titularidade não identificada';
			case 'MISSING_SUB':
				return 'Sub ausente';
			case 'DENTAL_PLAN_NOT_REGISTERED':
				return 'Empresa sem Dental cadastrado';
			default:
				return code;
		}
	}

	private getVisiblePreflightIssueCodes(codes: string[]): string[] {
		if (this.isUncomparedLinesActive) {
			return codes.filter((code) => this.isUncomparedLineIssueCode(code));
		}

		if (this.isUnidentifiedOrganizationsActive) {
			return codes.filter((code) => this.isOrganizationIssueCode(code));
		}

		return codes;
	}

	private getSessionPreflight(
		session: BeneficiaryReconciliationSessionListItem,
	): GenericBeneficiaryReconciliationPreflightSummary | null {
		return session.summary?.preflight ?? null;
	}

	private getSessionScopeModeRequest(
		preflight?: GenericBeneficiaryReconciliationPreflightSummary,
	): { scopeMode?: BeneficiaryReconciliationScopeMode } {
		const scopeMode =
			preflight?.scope?.mode ??
			this.preflightSummary?.scope?.mode ??
			this.getCreatedSessionScopeModeFallback();

		return scopeMode ? { scopeMode } : {};
	}

	private getCreatedSessionScopeModeFallback(): BeneficiaryReconciliationScopeMode | null {
		if (
			!this.createdSessionScopeModeFallback ||
			this.createdSessionScopeModeFallback.sessionId !== this.comparisonId
		) {
			return null;
		}

		return this.createdSessionScopeModeFallback.scopeMode;
	}

	private parseWalletKind(
		value: unknown,
	): GenericBeneficiaryReconciliationWalletKind | null {
		const walletKind = this.getStringValue(value);

		switch (walletKind) {
			case 'DENTAL':
			case 'HEALTH':
				return walletKind;
			default:
				return null;
		}
	}

	private hasAnyPreflightIssue(
		row: GenericBeneficiaryReconciliationPreflightRow,
		codes: string[],
	): boolean {
		const codeSet = new Set(codes);

		return [...row.issues, ...row.warnings].some((issue) =>
			codeSet.has(issue.code),
		);
	}

	private hasAnyPreflightBlockingIssue(
		row: GenericBeneficiaryReconciliationPreflightRow,
		codes: string[],
	): boolean {
		const codeSet = new Set(codes);

		return row.issues.some((issue) => codeSet.has(issue.code));
	}

	private getPreflightCounts(
		rows: GenericBeneficiaryReconciliationPreflightRow[],
	): GenericBeneficiaryReconciliationPreflightSummary['counts'] {
		return {
			eligible: rows.filter((row) => row.status === 'ELIGIBLE').length,
			onHold: rows.filter(
				(row) => row.status === 'ON_HOLD' && !this.isIgnoredPreflightRow(row),
			).length,
			pending: rows.filter((row) => row.status === 'PENDING').length,
			total: rows.length,
		};
	}

	private isIgnoredPreflightRow(
		row: GenericBeneficiaryReconciliationPreflightRow,
	): boolean {
		return row.status === 'ON_HOLD' && row.lastAction === 'IGNORED';
	}

	private isOrganizationIssueCode(code: string): boolean {
		return [
			'CNPJ_NOT_REGISTERED',
			'CNPJ_OUTSIDE_CONTEXT',
			'MISSING_CNPJ',
			'SUBINVOICE_NOT_FOUND',
			'UNKNOWN_CNPJ',
		].includes(code);
	}

	private isUncomparedLineIssueCode(code: string): boolean {
		return [
			'INVALID_BIRTH_DAY',
			'INVALID_CPF',
			'INVALID_VALIDITY_END_DATE',
			'MISSING_BIRTH_DAY',
			'MISSING_CARRIER',
			'MISSING_CPF',
			'MISSING_NAME',
			'TITULARITY_NOT_IDENTIFIED',
		].includes(code);
	}

	private isUncomparedLineRow(
		row: GenericBeneficiaryReconciliationPreflightRow,
	): boolean {
		return row.issues.some((issue) =>
			this.isUncomparedLineIssueCode(issue.code),
		);
	}

	private isUnidentifiedOrganizationRow(
		row: GenericBeneficiaryReconciliationPreflightRow,
	): boolean {
		return [...row.issues, ...row.warnings].some((issue) =>
			this.isOrganizationIssueCode(issue.code),
		);
	}

	private filterPreflightRows(
		rows: GenericBeneficiaryReconciliationPreflightRow[],
	): GenericBeneficiaryReconciliationPreflightRow[] {
		const status = this.normalizeStatusFilter(
			this.getStringValue(this.filterForm.get('status')?.value),
		);
		const carrier = this.getStringValue(
			this.filterForm.get('carrier')?.value,
		).toLowerCase();
		const search = this.getStringValue(this.filterForm.get('search')?.value);
		const normalizedSearch = this.normalizeSearchText(search);
		const searchDigits = this.onlyDigits(search);

		return rows.filter((row) => {
			if (
				status === BeneficiaryReconciliationItemStatusFilter.APPLIED &&
				row.status !== 'ELIGIBLE'
			) {
				return false;
			}

			if (
				status === BeneficiaryReconciliationItemStatusFilter.IGNORED &&
				!this.isIgnoredPreflightRow(row)
			) {
				return false;
			}

			if (status === BeneficiaryReconciliationItemStatusFilter.FAILED) {
				return false;
			}

			if (
				status ===
					BeneficiaryReconciliationItemStatusFilter.MANUAL_REVIEW_REQUIRED &&
				(row.status !== 'PENDING' || this.pendingChanges.has(row.id))
			) {
				return false;
			}

			if (
				status === BeneficiaryReconciliationItemStatusFilter.PENDING &&
				(row.status !== 'ON_HOLD' || this.isIgnoredPreflightRow(row))
			) {
				return false;
			}

			if (
				status === BeneficiaryReconciliationItemStatusFilter.READY_TO_APPLY &&
				!this.pendingChanges.has(row.id)
			) {
				return false;
			}

			if (
				carrier &&
				!this.getStringValue(row.data?.carrier).toLowerCase().includes(carrier)
			) {
				return false;
			}

			if (normalizedSearch) {
				const textHaystack = this.normalizeSearchText(
					[
						row.data?.beneficiaryCpf ?? '',
						row.data?.beneficiaryName ?? '',
						row.data?.sourceOrganizationCnpj ?? '',
						this.getPreflightIssueLabels(row),
					].join(' '),
				);
				const digitHaystack = this.onlyDigits(
					[
						row.data?.beneficiaryCpf ?? '',
						row.data?.sourceOrganizationCnpj ?? '',
					].join(' '),
				);

				return (
					textHaystack.includes(normalizedSearch) ||
					Boolean(searchDigits && digitHaystack.includes(searchDigits))
				);
			}

			return true;
		});
	}

	private paginateLocalItems<T>(items: T[]): T[] {
		const limit = this.getLimit();
		const start = (this.itemsCurrentPage - 1) * limit;

		return items.slice(start, start + limit);
	}

	private isOperationalDivergenceExportField(
		field: BeneficiaryReconciliationField | null,
	): field is
		| 'CARD_MISMATCH'
		| 'INACTIVE_MISSING_IN_FILE'
		| 'ORGANIZATION_MISMATCH'
		| 'PLAN_MISMATCH'
		| 'TITULARITY_MISMATCH' {
		return (
			field === 'CARD_MISMATCH' ||
			field === 'INACTIVE_MISSING_IN_FILE' ||
			field === 'ORGANIZATION_MISMATCH' ||
			field === 'PLAN_MISMATCH' ||
			field === 'TITULARITY_MISMATCH'
		);
	}

	getOperationalDivergenceExportButtonLabel(): string {
		return this.currentApplyField === 'TITULARITY_MISMATCH'
			? 'Exportar pendentes de titularidade'
			: 'Exportar relatorio';
	}

	private getOperationalDivergenceExportFileName(
		field: BeneficiaryReconciliationField,
	): string {
		switch (field) {
			case 'CARD_MISMATCH':
				return 'relatorio-divergencia-carteirinha.xlsx';
			case 'INACTIVE_MISSING_IN_FILE':
				return 'relatorio-nao-encontrado-base-cliente.xlsx';
			case 'ORGANIZATION_MISMATCH':
				return 'relatorio-divergencia-cnpj-organizacao.xlsx';
			case 'PLAN_MISMATCH':
				return 'relatorio-divergencia-plano.xlsx';
			case 'TITULARITY_MISMATCH':
				return 'layout-pendencias-titularidade.xlsx';
			default:
				return 'relatorio-operacional-reconciliacao.xlsx';
		}
	}

	private isPreflightRowActionable(
		row: GenericBeneficiaryReconciliationPreflightRow,
	): boolean {
		return row.status === 'PENDING' || this.pendingChanges.has(row.id);
	}

	private isUnidentifiedOrganizationGroupActionable(
		group: GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup,
	): boolean {
		return (
			group.pendingCount > 0 ||
			Boolean(this.getActiveUnidentifiedOrganizationPendingChange(group))
		);
	}

	private getPendingChangesByPreflightKind(
		kind: GenericBeneficiaryReconciliationPreflightExportKind,
	): PendingItemChange[] {
		if (kind === 'UNIDENTIFIED_ORGANIZATIONS') {
			this.pruneStaleUnidentifiedOrganizationPendingChanges(
				this.unidentifiedOrganizationGroups,
			);
		}

		return Array.from(this.pendingChanges.values()).filter(
			(change) => change.preflightKind === kind,
		);
	}

	private getActiveUnidentifiedOrganizationPendingChange(
		group: GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup,
	): PendingItemChange | null {
		const pendingChange = this.pendingChanges.get(group.key) ?? null;

		if (!pendingChange) {
			return null;
		}

		if (pendingChange.action === 'CREATE_SUB') {
			return null;
		}

		if (pendingChange.action === 'RESUME_PREFLIGHT_ROW') {
			return group.onHoldCount > 0 ? pendingChange : null;
		}

		return group.pendingCount > 0 ? pendingChange : null;
	}

	private pruneStaleUnidentifiedOrganizationPendingChanges(
		groups: GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup[],
	): void {
		const groupsByKey = new Map(groups.map((group) => [group.key, group]));
		let changed = false;

		for (const change of Array.from(this.pendingChanges.values())) {
			if (change.preflightKind === 'UNIDENTIFIED_ORGANIZATIONS') {
				const group = groupsByKey.get(change.itemId);
				const isStillApplicable = group
					? Boolean(this.getActiveUnidentifiedOrganizationPendingChange(group))
					: false;

				if (!isStillApplicable) {
					this.pendingChanges.delete(change.itemId);
					changed = true;
				}
			}
		}

		if (changed) {
			this.persistPendingChanges();
		}
	}

	private queuePreflightRowIgnore(
		row: GenericBeneficiaryReconciliationPreflightRow,
	): void {
		this.setPendingChange({
			action: 'IGNORE_ITEM',
			actionLabel: 'Em espera',
			itemId: row.id,
			preflightKind: 'UNCOMPARED_LINES',
			targetValue: {},
		});
	}

	private queueUnidentifiedOrganizationGroupIgnore(
		group: GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup,
	): void {
		this.setPendingChange({
			action: 'IGNORE_ITEM',
			actionLabel: 'Em espera',
			itemId: group.key,
			preflightKind: 'UNIDENTIFIED_ORGANIZATIONS',
			targetValue: {
				cnpj: group.cnpj ?? undefined,
				rowIds: group.pendingRowIds,
			},
		});
	}

	private async runPreflightGroupAction(
		rowIds: string[],
		action: (
			rowId: string,
		) => ReturnType<
			| BeneficiaryReconciliationsService['holdGenericPreflightPendency']
			| BeneficiaryReconciliationsService['ignoreGenericPreflightPendency']
			| BeneficiaryReconciliationsService['resolveGenericPreflightPendency']
			| BeneficiaryReconciliationsService['resumeGenericPreflightPendency']
		>,
		successMessage: string,
		errorMessage: string,
	): Promise<void> {
		if (!this.organizationId || !this.comparisonId || rowIds.length === 0) {
			return;
		}

		try {
			await Promise.all(
				rowIds.map(async (rowId) => firstValueFrom(action(rowId))),
			);
			this.loadPreflightRows();
			this.loadItems({ refreshPendingTotal: true });
			this.snackBarService.showSnack(successMessage, 'success');
		} catch (error) {
			this.snackBarService.showSnack(
				this.getNormalizedBackendMessage(error, errorMessage),
				'error',
			);
		}
	}

	private async applyPreflightPendingChange(
		change: PendingItemChange,
	): Promise<boolean> {
		const rowIds = Array.isArray(change.targetValue.rowIds)
			? change.targetValue.rowIds
			: [change.itemId];

		switch (change.action) {
			case 'RESOLVE_PREFLIGHT_ROW':
				await firstValueFrom(
					this.beneficiaryReconciliationsService.resolveGenericPreflightPendency(
						{
							carrier:
								this.getStringValue(change.targetValue.carrier) || undefined,
							cpf: this.getStringValue(change.targetValue.cpf) || undefined,
							organizationId: this.organizationId,
							rowId: change.itemId,
							sessionId: this.comparisonId as string,
						},
					),
				);

				return true;
			case 'IGNORE_ITEM': {
				const request = this.isPendingChangeOnHold(change)
					? this.beneficiaryReconciliationsService.holdGenericPreflightPendency.bind(
							this.beneficiaryReconciliationsService,
						)
					: this.beneficiaryReconciliationsService.ignoreGenericPreflightPendency.bind(
							this.beneficiaryReconciliationsService,
						);

				await Promise.all(
					rowIds.map(async (rowId) =>
						firstValueFrom(
							request({
								organizationId: this.organizationId,
								rowId,
								sessionId: this.comparisonId as string,
							}),
						),
					),
				);

				return true;
			}
			case 'RESUME_PREFLIGHT_ROW':
				await Promise.all(
					rowIds.map(async (rowId) =>
						firstValueFrom(
							this.beneficiaryReconciliationsService.resumeGenericPreflightPendency(
								{
									organizationId: this.organizationId,
									rowId,
									sessionId: this.comparisonId as string,
								},
							),
						),
					),
				);

				return true;
			case 'TREAT_AS_MATRIX':
				await this.applyTreatAsMatrixChange(change, rowIds);

				return true;
			case 'CREATE_SUB':
				return true;
			default:
				return true;
		}
	}

	private async applyTreatAsMatrixChange(
		change: PendingItemChange,
		rowIds: string[],
	): Promise<void> {
		const cnpj = this.getStringValue(change.targetValue.cnpj);
		const group = this.unidentifiedOrganizationGroups.find(
			(item) => item.key === change.itemId,
		);

		if (
			cnpj &&
			group?.issueCodes.some((code) =>
				['CNPJ_NOT_REGISTERED', 'UNKNOWN_CNPJ'].includes(code),
			)
		) {
			await firstValueFrom(
				this.beneficiaryReconciliationsService.treatUnknownCnpjAsMatrix({
					cnpj,
					organizationId: this.organizationId,
					sessionId: this.comparisonId as string,
				}),
			);
			this.onReprocessPreflightAfterSubCreation();

			return;
		}

		await Promise.all(
			rowIds.map(async (rowId) =>
				firstValueFrom(
					this.beneficiaryReconciliationsService.resolveGenericPreflightPendency(
						{
							organizationId: this.organizationId,
							rowId,
							sessionId: this.comparisonId as string,
							treatAsMatrix: true,
						},
					),
				),
			),
		);
	}

	private refreshReconciliationData(): void {
		this.refreshCurrentSessionSummary();
		this.loadPreflightRows();
		this.loadItems({ refreshPendingTotal: true });

		if (this.showRecentSessions) {
			this.loadSessions(this.sessionsCurrentPage);
		}
	}

	private refreshCurrentSessionSummary(): void {
		if (!this.comparisonId || !this.organizationId) {
			return;
		}

		this.beneficiaryReconciliationsService
			.listGenericSessions({
				limit: 1,
				organizationId: this.organizationId,
				page: 1,
				sessionId: this.comparisonId,
			})
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				next: (response) => {
					const session = response.sessions.find(
						(item) => item.id === this.comparisonId,
					);
					const preflight = session ? this.getSessionPreflight(session) : null;

					if (preflight) {
						this.updatePreflightAlerts(preflight);
					}
				},
			});
	}

	private loadPreflightRows(): void {
		if (!this.organizationId || !this.comparisonId || !this.preflightSummary) {
			return;
		}

		this.isLoadingPreflightRows = true;
		this.beneficiaryReconciliationsService
			.listGenericPreflightPendencies({
				kind: 'UNCOMPARED_LINES',
				limit: this.getLimit(),
				organizationId: this.organizationId,
				page: this.itemsCurrentPage,
				sessionId: this.comparisonId,
				status: this.getUncomparedPreflightStatusFilter(),
			})
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: (error) => {
					this.isLoadingPreflightRows = false;
					this.preflightPendingRows = [];
					this.preflightOnHoldRows = [];
					this.preflightEligibleRows = [];
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Erro ao carregar as pendências de comparação.',
						),
						'error',
					);
				},
				next: (response) => {
					this.isLoadingPreflightRows = false;
					const rows = response.items;

					this.preflightPendingRows = rows.filter(
						(row) => row.status === 'PENDING',
					);
					this.preflightOnHoldRows = rows.filter(
						(row) =>
							row.status === 'ON_HOLD' && !this.isIgnoredPreflightRow(row),
					);
					this.preflightEligibleRows = rows.filter((row) =>
						this.isPreflightRowCompleted(row),
					);
					this.preflightRowsLoadedSessionId = this.comparisonId;
					this.itemsCount = response.total;
					this.itemsCurrentPage = response.page;
					this.itemsTotalPages = response.totalPages;
					this.preflightRowCpfEdits = this.preflightPendingRows.reduce<
						Record<string, string>
					>((accumulator, row) => {
						accumulator[row.id] = row.data?.beneficiaryCpf ?? '';

						return accumulator;
					}, {});
					this.preflightRowCarrierEdits = this.preflightPendingRows.reduce<
						Record<string, string>
					>((accumulator, row) => {
						accumulator[row.id] = row.data?.carrier ?? '';

						return accumulator;
					}, {});
					this.loadUnidentifiedOrganizationGroups();
				},
			});
	}

	private loadUnidentifiedOrganizationGroups(): void {
		if (!this.organizationId || !this.comparisonId || !this.preflightSummary) {
			this.unidentifiedOrganizationGroups = [];

			return;
		}

		this.isLoadingUnidentifiedOrganizationGroups = true;
		this.beneficiaryReconciliationsService
			.listGenericUnidentifiedOrganizationGroups({
				organizationId: this.organizationId,
				sessionId: this.comparisonId,
			})
			.pipe(
				finalize(() => {
					this.isLoadingUnidentifiedOrganizationGroups = false;
				}),
				takeUntil(this.unsubscribe$),
			)
			.subscribe({
				error: (error) => {
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Erro ao carregar organizações não identificadas.',
						),
						'error',
					);
				},
				next: (response) => {
					this.pruneStaleUnidentifiedOrganizationPendingChanges(response.items);
					this.unidentifiedOrganizationGroups = response.items;
					this.unidentifiedOrganizationGroupsLoadedSessionId =
						this.comparisonId;
				},
			});
	}

	private updatePreflightRow(
		updatedRow: GenericBeneficiaryReconciliationPreflightRow,
	): void {
		this.updatePreflightRows([updatedRow]);
		this.loadPreflightRows();
	}

	private updatePreflightRows(
		updatedRows: GenericBeneficiaryReconciliationPreflightRow[],
	): void {
		if (!this.preflightSummary) {
			return;
		}

		const updatedRowsById = new Map(updatedRows.map((row) => [row.id, row]));
		const nextRows = (this.preflightSummary.rows ?? []).map((row) =>
			updatedRowsById.has(row.id)
				? { ...row, ...(updatedRowsById.get(row.id) ?? {}) }
				: row,
		);
		const hasCompletePreflightRows =
			nextRows.length === this.preflightSummary.counts.total;
		const uncomparedLines = hasCompletePreflightRows
			? nextRows.filter(
					(row) =>
						!this.isPreflightRowCompleted(row) && this.isUncomparedLineRow(row),
				).length
			: (this.preflightSummary.pendencyCounts?.uncomparedLines ?? 0);

		this.preflightSummary = {
			...this.preflightSummary,
			counts: hasCompletePreflightRows
				? this.getPreflightCounts(nextRows)
				: this.preflightSummary.counts,
			pendencyCounts: {
				...this.preflightSummary.pendencyCounts,
				uncomparedLines,
			},
			rows: nextRows,
		};
	}

	private hasCompletePreflightSummaryRows(): boolean {
		const rows = this.preflightSummary?.rows ?? [];
		const total = this.preflightSummary?.counts.total ?? 0;

		return total > 0 && rows.length === total;
	}

	private async buildPendingSessionAudit(
		comparisonId: string,
		fields?: BeneficiaryReconciliationField[],
	): Promise<PendingSessionAudit> {
		if (!this.organizationId) {
			return {
				autoSuggestedCount: 0,
				manualReviewedCount: 0,
				manualUnreviewedCount: 0,
				manualUnreviewedItemIds: [],
				summaries: [],
				total: 0,
			};
		}

		const fieldSet = fields?.length ? new Set(fields) : null;
		const manualItems = Array.from(this.pendingChanges.values()).reduce<
			NonNullable<BeneficiaryReconciliationApplyRequest['items']>
		>((requests, change) => {
			const isOutsideSelectedField =
				fieldSet && (!change.field || !fieldSet.has(change.field));
			const request = this.buildApplyRequestFromChange(change);

			if (!change.preflightKind && !isOutsideSelectedField && request) {
				requests.push(request);
			}

			return requests;
		}, []);
		const preview = await firstValueFrom(
			this.beneficiaryReconciliationsService.previewGenericSessionApply({
				fields,
				items: manualItems,
				organizationId: this.organizationId,
				...this.getSessionScopeModeRequest(),
				sessionId: comparisonId,
			}),
		);

		return {
			autoSuggestedCount: preview.totalReadyToApply,
			manualReviewedCount: preview.totalManualReviewed,
			manualUnreviewedCount: preview.totalManualReviewRequired,
			manualUnreviewedItemIds: preview.manualReviewSampleItemIds,
			summaries: this.buildPendingSessionAuditSummaries(preview),
			total: preview.totalReadyToApply + preview.totalManualReviewed,
		};
	}

	private buildPendingSessionAuditSummaries(
		preview: BeneficiaryReconciliationApplyPreviewResponse,
	): PendingActionSummary[] {
		const summaries = Object.entries(preview.bySuggestedAction).reduce<
			PendingActionSummary[]
		>((currentSummaries, [action, count]) => {
			if (!count) {
				return currentSummaries;
			}

			const parsedAction = this.parseApplyAction(action);

			if (
				!parsedAction ||
				(parsedAction === 'IGNORE_ITEM' && preview.ignoreItemBreakdown)
			) {
				return currentSummaries;
			}

			currentSummaries.push({
				action: parsedAction,
				count,
				label: this.getApplyActionLabel(parsedAction),
			});

			return currentSummaries;
		}, []);

		if (preview.ignoreItemBreakdown) {
			const ignoreSummaries: { count: number; label: string }[] = [
				{
					count: preview.ignoreItemBreakdown.onHold,
					label: 'Em espera',
				},
				{
					count: preview.ignoreItemBreakdown.keepPlatform,
					label: 'Plataforma mantida',
				},
				{
					count: preview.ignoreItemBreakdown.ignored,
					label: 'Ignorado',
				},
			];

			for (const summary of ignoreSummaries) {
				if (summary.count > 0) {
					summaries.push({
						action: 'IGNORE_ITEM',
						...summary,
					});
				}
			}
		}

		return summaries.sort((current, next) => next.count - current.count);
	}

	private buildQuerySignature(queryState: QueryState): string {
		return JSON.stringify(queryState);
	}

	private buildItemsRequestSignature(
		requestState: BeneficiaryReconciliationListItemsArgs & {
			organizationId: string;
		},
	): string {
		return JSON.stringify(requestState);
	}

	private getCurrentItemsRequestState():
		| (BeneficiaryReconciliationListItemsArgs & {
				organizationId: string;
		  })
		| null {
		if (!this.comparisonId || !this.organizationId) {
			return null;
		}

		const field = this.parseReconciliationField(
			this.filterForm.get('field')?.value,
		);
		const statusFilterParams = this.getStatusFilterParams(
			this.normalizeStatusFilter(
				this.getStringValue(this.filterForm.get('status')?.value),
			),
		);

		return {
			carrier: this.getStringValue(this.filterForm.get('carrier')?.value),
			comparisonId: this.comparisonId,
			field: field ?? '',
			limit: this.getLimit(),
			organizationId: this.organizationId,
			page: this.itemsCurrentPage,
			reviewState: statusFilterParams.reviewState,
			search: this.getSearchQueryValue(),
			status: statusFilterParams.status,
		};
	}

	private isCurrentItemsRequest(
		requestId: number,
		requestSignature: string,
	): boolean {
		if (requestId !== this.latestItemsRequestId) {
			return false;
		}

		const currentRequestState = this.getCurrentItemsRequestState();

		return Boolean(
			currentRequestState &&
				this.buildItemsRequestSignature(currentRequestState) ===
					requestSignature,
		);
	}

	private getStatusFilterParams(
		value: BeneficiaryReconciliationItemStatusFilter,
	): {
		reviewState?: BeneficiaryReconciliationReviewState;
		status?:
			| BeneficiaryReconciliationItemStatus
			| BeneficiaryReconciliationItemStatusFilter.IGNORED;
	} {
		switch (value) {
			case BeneficiaryReconciliationItemStatusFilter.APPLIED:
				return { status: BeneficiaryReconciliationItemStatus.APPLIED };
			case BeneficiaryReconciliationItemStatusFilter.FAILED:
				return { status: BeneficiaryReconciliationItemStatus.FAILED };
			case BeneficiaryReconciliationItemStatusFilter.IGNORED:
				return { status: BeneficiaryReconciliationItemStatusFilter.IGNORED };
			case BeneficiaryReconciliationItemStatusFilter.PENDING:
				return { status: BeneficiaryReconciliationItemStatus.PENDING };
			case BeneficiaryReconciliationItemStatusFilter.MANUAL_REVIEW_REQUIRED:
				return {
					reviewState:
						BeneficiaryReconciliationReviewState.MANUAL_REVIEW_REQUIRED,
				};
			case BeneficiaryReconciliationItemStatusFilter.READY_TO_APPLY:
				return {
					reviewState: BeneficiaryReconciliationReviewState.READY_TO_APPLY,
				};
			default:
				return {};
		}
	}

	private normalizeStatusFilter(
		value: string,
	): BeneficiaryReconciliationItemStatusFilter {
		switch (value) {
			case BeneficiaryReconciliationItemStatusFilter.APPLIED:
			case BeneficiaryReconciliationItemStatusFilter.FAILED:
			case BeneficiaryReconciliationItemStatusFilter.IGNORED:
			case BeneficiaryReconciliationItemStatusFilter.MANUAL_REVIEW_REQUIRED:
			case BeneficiaryReconciliationItemStatusFilter.PENDING:
			case BeneficiaryReconciliationItemStatusFilter.READY_TO_APPLY:
				return value;
			default:
				return BeneficiaryReconciliationItemStatusFilter.ALL;
		}
	}

	private ensureAllowedOrganization(): boolean {
		return Boolean(this.organizationId);
	}

	private loadSelectedOrganizationContext(organizationId: string): void {
		const requestId = ++this.selectedOrganizationContextRequestId;

		this.selectedOrganizationIsMatriz = null;
		this.scopeMode = 'ORGANIZATION_ONLY';
		this.organizationsService
			.getById(organizationId)
			.pipe(
				catchError((error) => {
					if (
						this.isCurrentSelectedOrganizationContextRequest(
							requestId,
							organizationId,
						)
					) {
						this.selectedOrganizationIsMatriz = null;
						this.snackBarService.showSnack(
							this.getNormalizedBackendMessage(
								error,
								'Erro ao carregar o contexto da organizacao selecionada.',
							),
							'error',
						);
					}

					return EMPTY;
				}),
				takeUntil(this.unsubscribe$),
			)
			.subscribe((organization) => {
				if (
					!this.isCurrentSelectedOrganizationContextRequest(
						requestId,
						organizationId,
					)
				) {
					return;
				}

				this.selectedOrganizationIsMatriz = organization.is_matriz;

				if (!organization.is_matriz) {
					this.scopeMode = 'ORGANIZATION_ONLY';
				}
			});
	}

	private isCurrentSelectedOrganizationContextRequest(
		requestId: number,
		organizationId: string,
	): boolean {
		return (
			requestId === this.selectedOrganizationContextRequestId &&
			this.organizationId === organizationId
		);
	}

	private extractManualReviewItemIdsFromError(error: unknown): string[] {
		const errorPayload =
			error && typeof error === 'object'
				? (error as { error?: unknown }).error
				: null;
		const root =
			errorPayload && typeof errorPayload === 'object'
				? (errorPayload as Record<string, unknown>)
				: {};
		const data =
			root.data && typeof root.data === 'object'
				? (root.data as Record<string, unknown>)
				: {};

		const possibleSources = [
			root.pendingItemIds,
			root.manualReviewItemIds,
			root.pendingManualReviewItemIds,
			root.itemIds,
			data.pendingItemIds,
			data.manualReviewItemIds,
			data.pendingManualReviewItemIds,
			data.itemIds,
		];

		for (const source of possibleSources) {
			if (Array.isArray(source)) {
				return source
					.map((itemId) => this.getStringValue(itemId))
					.filter((itemId) => Boolean(itemId));
			}
		}

		return [];
	}

	private focusManualReviewItems(itemIds: string[]): void {
		if (itemIds.length === 0) {
			return;
		}

		this.highlightedManualReviewItemIds = new Set(itemIds);
		const currentStatus = this.normalizeStatusFilter(
			this.getStringValue(this.filterForm.get('status')?.value),
		);

		if (
			currentStatus !==
			BeneficiaryReconciliationItemStatusFilter.MANUAL_REVIEW_REQUIRED
		) {
			this.filterForm.patchValue(
				{
					status:
						BeneficiaryReconciliationItemStatusFilter.MANUAL_REVIEW_REQUIRED,
				},
				{ emitEvent: false },
			);
			this.syncQueryParams({
				page: 1,
			});
		}

		setTimeout(() => {
			const firstItemId = itemIds[0];
			const rowElement = document.getElementById(
				`reconciliation-item-${firstItemId}`,
			);

			if (rowElement) {
				rowElement.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
				});
			}
		}, 50);
	}

	private getPendingActionLabel(action: PendingChangeAction): string {
		switch (action) {
			case 'CREATE_BENEFICIARY':
				return 'novos cadastros';
			case 'CREATE_SUB':
				return 'Criar nova sub';
			case 'IGNORE_ITEM':
				return 'Em espera';
			case 'INACTIVATE_BENEFICIARY':
				return 'Serão inativados';
			case 'LINK_EXISTING_BENEFICIARY':
				return 'Serão vinculados';
			case 'REACTIVATE_BENEFICIARY':
				return 'Serão reativados';
			case 'RESOLVE_PREFLIGHT_ROW':
				return 'Correções informadas';
			case 'RESUME_PREFLIGHT_ROW':
				return 'Retomar';
			case 'SELECT_DUPLICATE_ROW':
				return 'Linhas selecionadas';
			case 'TREAT_AS_MATRIX':
				return 'Tratar como matriz';
			case 'TRANSFER_ORGANIZATION':
				return 'Trocaram de sub';
			case 'UPDATE_CARD':
				return 'Trocaram de carteirinha';
			case 'UPDATE_PLAN':
				return 'Trocaram de plano';
			case 'UPDATE_PLAN_AND_CARD':
				return 'Trocaram de plano e carteirinha';
			case 'UPDATE_TITULARITY':
				return 'Trocaram de titularidade';
			default:
				return action;
		}
	}

	private getApplyActionLabel(
		action: BeneficiaryReconciliationApplyAction,
	): string {
		return this.getPendingActionLabel(action);
	}

	private getBooleanValue(value: unknown): boolean {
		return value === true;
	}

	private getLimit(): number {
		const value = Number(this.filterForm.get('limit')?.value);

		return this.limitOptions.includes(value)
			? value
			: AppConstants.pagination.itemsPerPage;
	}

	private getPendingStorageKey(comparisonId: string): string {
		return `${this.pendingChangesStoragePrefix}${comparisonId}`;
	}

	private getActiveApplyProcessingStorageKey(comparisonId: string): string {
		return `${this.activeApplyProcessingStoragePrefix}${comparisonId}`;
	}

	private getConsumedTerminalProcessingStorageKey(
		processingId: string,
	): string {
		return `${this.consumedTerminalProcessingStoragePrefix}${processingId}`;
	}

	private hasConsumedTerminalProcessing(processingId: string): boolean {
		return (
			sessionStorage.getItem(
				this.getConsumedTerminalProcessingStorageKey(processingId),
			) === 'true'
		);
	}

	private markTerminalProcessingAsConsumed(processingId: string): void {
		sessionStorage.setItem(
			this.getConsumedTerminalProcessingStorageKey(processingId),
			'true',
		);
	}

	private resolveActiveApplyProcessingContext(
		activeProcessing: Exclude<
			BeneficiaryReconciliationActiveProcessingResponse,
			null
		>,
		draft?: BeneficiaryReconciliationDraft | null,
	): ProcessingApplyContext | undefined {
		const draftContext = this.parseActiveApplyProcessingContext(
			draft?.activeApplyProcessing,
			activeProcessing.processingId,
		);

		if (draftContext) {
			return draftContext;
		}

		return (
			this.readActiveApplyProcessingContextFromSessionStorage(
				activeProcessing.sessionId,
				activeProcessing.processingId,
			) ?? undefined
		);
	}

	private readActiveApplyProcessingContextFromSessionStorage(
		sessionId: string,
		processingId: string,
	): ProcessingApplyContext | null {
		try {
			const rawValue = sessionStorage.getItem(
				this.getActiveApplyProcessingStorageKey(sessionId),
			);

			if (!rawValue) {
				return null;
			}

			return this.parseActiveApplyProcessingContext(
				JSON.parse(rawValue),
				processingId,
			);
		} catch (error) {
			sessionStorage.removeItem(
				this.getActiveApplyProcessingStorageKey(sessionId),
			);

			return null;
		}
	}

	private parseActiveApplyProcessingContext(
		value: unknown,
		processingId: string,
	): ProcessingApplyContext | null {
		if (!value || typeof value !== 'object' || Array.isArray(value)) {
			return null;
		}

		const rawContext = value as Record<string, unknown>;

		if (this.getStringValue(rawContext.processingId) !== processingId) {
			return null;
		}

		const itemIds = Array.isArray(rawContext.itemIds)
			? rawContext.itemIds
					.map((itemId) => this.getStringValue(itemId))
					.filter(Boolean)
			: [];
		const fields = Array.isArray(rawContext.fields)
			? rawContext.fields
					.map((field) => this.parseReconciliationField(field))
					.filter((field): field is BeneficiaryReconciliationField =>
						Boolean(field),
					)
			: [];

		if (itemIds.length === 0 && fields.length === 0) {
			return null;
		}

		return {
			...(fields.length > 0 ? { fields } : {}),
			...(itemIds.length > 0 ? { itemIds } : {}),
		};
	}

	private getStoredPendingChangesSummary(comparisonId: string): {
		actionableCount: number;
		onHoldCount: number;
	} {
		try {
			const rawValue = sessionStorage.getItem(
				this.getPendingStorageKey(comparisonId),
			);

			if (!rawValue) {
				return { actionableCount: 0, onHoldCount: 0 };
			}

			const parsedValue = JSON.parse(rawValue);

			if (!Array.isArray(parsedValue)) {
				return { actionableCount: 0, onHoldCount: 0 };
			}

			return parsedValue.reduce(
				(summary, entry) => {
					if (!entry || typeof entry !== 'object') {
						return summary;
					}

					const rawEntry = entry as {
						action?: unknown;
						targetValue?: BeneficiaryReconciliationApplyTargetValue;
					};
					const action = this.parsePendingChangeAction(rawEntry.action);

					if (!action) {
						return summary;
					}

					if (
						action === 'IGNORE_ITEM' &&
						this.getStringValue(rawEntry.targetValue?.resolutionMode) !==
							RECONCILIATION_RESOLUTION_MODE_KEEP_PLATFORM
					) {
						return {
							...summary,
							onHoldCount: summary.onHoldCount + 1,
						};
					}

					return {
						...summary,
						actionableCount: summary.actionableCount + 1,
					};
				},
				{ actionableCount: 0, onHoldCount: 0 },
			);
		} catch (error) {
			return { actionableCount: 0, onHoldCount: 0 };
		}
	}

	private getStringValue(value: unknown): string {
		return typeof value === 'string' ? value.trim() : '';
	}

	private getNormalizedBackendMessage(
		error: unknown,
		fallbackMessage: string,
	): string {
		if (!error || typeof error !== 'object') {
			return this.normalizeBackendText(fallbackMessage);
		}

		const typedError = error as {
			error?: unknown;
			message?: unknown;
			status?: unknown;
		};
		const status =
			typeof typedError.status === 'number' ? typedError.status : null;

		if (status !== null && TRANSIENT_TRANSPORT_STATUS_CODES.has(status)) {
			return this.normalizeBackendText(fallbackMessage);
		}

		const payload = this.getBackendErrorPayload(typedError.error);

		if (payload.code === 'BENEFICIARY_RECONCILIATION_SESSION_CHANGED') {
			this.refreshReconciliationData();

			return 'A reconciliação foi atualizada por outra operação. Os dados foram recarregados para uma nova decisão.';
		}

		const candidateText = this.getStringValue(
			payload.message ?? typedError.message,
		);

		return this.normalizeBackendText(
			candidateText && !this.isTechnicalTransportMessage(candidateText)
				? candidateText
				: fallbackMessage,
		);
	}

	private getBackendErrorPayload(value: unknown): Record<string, unknown> {
		return value && typeof value === 'object'
			? (value as Record<string, unknown>)
			: {};
	}

	private isTechnicalTransportMessage(message: string): boolean {
		return (
			/Http failure response|Unknown Error|https?:\/\//i.test(message) ||
			/<(?:!doctype|html|head|body)\b/i.test(message)
		);
	}

	private recoverUnknownApplyResult(
		sessionId: string,
		applyContext: ProcessingApplyContext,
	): void {
		if (!this.organizationId) {
			return;
		}

		this.unknownApplySessionId = sessionId;
		this.unknownApplyContext = applyContext;
		this.verifyUnknownApplyResult(sessionId, applyContext);
	}

	private verifyUnknownApplyResult(
		sessionId: string,
		applyContext: ProcessingApplyContext,
	): void {
		if (!this.organizationId || this.isVerifyingUnknownApplyResult) {
			return;
		}

		this.isVerifyingUnknownApplyResult = true;
		this.isApplying = true;
		this.processingErrorMessage =
			'O resultado da aplicação está sendo confirmado. Não envie a operação novamente.';
		this.beneficiaryReconciliationsService
			.getGenericActiveProcessing({ organizationId: this.organizationId })
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: () => {
					this.releaseUnknownApplyVerification(
						'Não foi possível confirmar o resultado da aplicação. Use “Verificar resultado” antes de tentar aplicar novamente.',
					);
				},
				next: (activeProcessing) => {
					this.resolveUnknownApplyActiveProcessing(
						activeProcessing,
						sessionId,
						applyContext,
					);
				},
			});
	}

	private resolveUnknownApplyActiveProcessing(
		activeProcessing: BeneficiaryReconciliationActiveProcessingResponse,
		sessionId: string,
		applyContext: ProcessingApplyContext,
	): void {
		if (
			!activeProcessing ||
			activeProcessing.type !== 'APPLY' ||
			activeProcessing.sessionId !== sessionId
		) {
			this.verifyUnknownApplySessionState(sessionId);

			return;
		}

		this.clearUnknownApplyResult();
		this.restoreProcessingSession(sessionId);

		if (activeProcessing.status === 'processing') {
			this.startProcessingPolling({
				applyContext,
				processingId: activeProcessing.processingId,
				sessionId,
				type: 'APPLY',
			});

			return;
		}

		this.resolveUnknownApplyTerminalProcessing(
			activeProcessing,
			sessionId,
			applyContext,
		);
	}

	private resolveUnknownApplyTerminalProcessing(
		activeProcessing: Exclude<
			BeneficiaryReconciliationActiveProcessingResponse,
			null
		>,
		sessionId: string,
		applyContext: ProcessingApplyContext,
	): void {
		this.activeProcessingType = 'APPLY';
		this.activeApplyContext = applyContext;

		if (activeProcessing.status === 'finished') {
			this.handleProcessingFinished('APPLY', sessionId, activeProcessing.data);
		} else {
			this.handleProcessingFailed(
				activeProcessing.error ??
					'A aplicação falhou. Revise o estado atualizado antes de tentar novamente.',
			);
		}

		this.markTerminalProcessingAsConsumed(activeProcessing.processingId);
	}

	private verifyUnknownApplySessionState(sessionId: string): void {
		if (!this.organizationId) {
			this.releaseUnknownApplyVerification(
				'Não foi possível confirmar o resultado da aplicação. Use “Verificar resultado” antes de tentar aplicar novamente.',
			);

			return;
		}

		this.beneficiaryReconciliationsService
			.listGenericSessions({
				limit: 1,
				organizationId: this.organizationId,
				page: 1,
				sessionId,
			})
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: () => {
					this.releaseUnknownApplyVerification(
						'Não foi possível reler o estado da reconciliação. Use “Verificar resultado” antes de tentar aplicar novamente.',
					);
				},
				next: (response) => {
					if (!response.sessions.some((session) => session.id === sessionId)) {
						this.releaseUnknownApplyVerification(
							'Não foi possível localizar a sessão para confirmar a aplicação. Use “Verificar resultado” antes de tentar novamente.',
						);

						return;
					}

					this.clearUnknownApplyResult();
					this.processingErrorMessage =
						'O estado da reconciliação foi atualizado. Revise os itens antes de aplicar novamente.';
					this.refreshReconciliationData();
					this.snackBarService.showSnack(this.processingErrorMessage, 'info');
				},
			});
	}

	private releaseUnknownApplyVerification(message: string): void {
		this.isApplying = false;
		this.isVerifyingUnknownApplyResult = false;
		this.processingErrorMessage = message;
		this.refreshReconciliationData();
		this.snackBarService.showSnack(message, 'info');
	}

	private clearUnknownApplyResult(): void {
		this.unknownApplySessionId = null;
		this.unknownApplyContext = null;
		this.isVerifyingUnknownApplyResult = false;
		this.isApplying = false;
	}

	private analyzeFlexibleMapping(): void {
		if (
			!this.canCompare ||
			!this.organizationId ||
			this.isAnalyzingMapping ||
			this.isConfirmingMapping ||
			this.isProcessingReconciliation
		) {
			return;
		}

		this.isAnalyzingMapping = true;
		this.confirmedMapping = null;
		this.mappingAnalysis = null;

		this.beneficiaryReconciliationsService
			.analyzeGenericMapping({
				files: this.flexibleFiles,
				organizationId: this.organizationId,
			})
			.pipe(
				finalize(() => {
					this.isAnalyzingMapping = false;
				}),
				takeUntil(this.unsubscribe$),
			)
			.subscribe({
				error: (error) => {
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Erro ao analisar as colunas das bases.',
						),
						'error',
					);
				},
				next: (response) => {
					this.mappingAnalysis = response;
					this.openMappingModal(response);
				},
			});
	}

	private clearFlexibleUploadState(): void {
		this.flexibleFiles = [];
		this.confirmedMapping = null;
		this.mappingAnalysis = null;
	}

	private clearStandardUploadState(): void {
		this.healthFile = null;
		this.dentalFile = null;
	}

	private createStandardPreflight(): Observable<GenericPreflightCreationResponse> {
		const args = {
			dentalFile: this.dentalFile,
			healthFile: this.healthFile,
			organizationId: this.organizationId,
			scopeMode: this.effectiveScopeMode,
		};

		if (this.isAmilStandardImport) {
			return this.beneficiaryReconciliationsService.createAmilPreflight(args);
		}

		if (this.isBradescoProviderStandardImport) {
			return this.beneficiaryReconciliationsService.createBradescoPreflight(
				args,
			);
		}

		if (this.isBradescoSpgStandardImport) {
			return this.beneficiaryReconciliationsService.createBradescoSpgPreflight(
				args,
			);
		}

		return this.beneficiaryReconciliationsService.createGenericPreflight(args);
	}

	private resetGenericPreflightStateForNewSession(): void {
		this.createdSessionScopeModeFallback = null;
		this.hiddenPersistedOriginalFileKeys.clear();
		this.pendingChanges.clear();
		this.lastReportUrl = null;
		this.loadedSessionAppliedCount = 0;
		this.preflightSummary = null;
		this.preflightEligibleRows = [];
		this.preflightPendingRows = [];
		this.preflightOnHoldRows = [];
		this.preflightRowsLoadedSessionId = null;
		this.unidentifiedOrganizationGroups = [];
		this.unidentifiedOrganizationGroupsLoadedSessionId = null;
		this.preflightUnknownCnpjs = [];
		this.activeCardKey = 'DUPLICATE_IN_FILE';
	}

	private getOriginalFileKey(file: ReconciliationOriginalFile): string {
		return (
			file.id ??
			[
				this.preflightSummary?.importModel ?? 'UNKNOWN_MODEL',
				file.walletKind ?? 'UNKNOWN_WALLET',
				file.version ?? 'UNKNOWN_VERSION',
				file.fileName,
			].join(':')
		);
	}

	private getAcceptedStandardFileExtensions(): string[] {
		return this.isBradescoStandardImport
			? this.bradescoReconciliationFileExtensions
			: this.sanusStandardReconciliationFileExtensions;
	}

	private getAcceptedStandardReconciliationFiles(files: FileList): File[] {
		return this.getAcceptedReconciliationFiles(
			files,
			this.getAcceptedStandardFileExtensions(),
		);
	}

	private getDownloadFileName(
		response: HttpResponse<Blob>,
		fallbackFileName: string,
	): string {
		const contentDisposition = response.headers.get('Content-Disposition');
		const encodedFileName = contentDisposition?.match(
			/filename\*=UTF-8''([^;]+)/,
		);
		const quotedFileName = contentDisposition?.match(/filename="([^"]+)"/);

		if (encodedFileName?.[1]) {
			try {
				return decodeURIComponent(encodedFileName[1]);
			} catch {
				return fallbackFileName;
			}
		}

		return quotedFileName?.[1] ?? fallbackFileName;
	}

	private getPersistedOriginalFileByWalletKind(
		walletKind: GenericBeneficiaryReconciliationWalletKind,
	): ReconciliationOriginalFile | null {
		return (
			this.persistedOriginalFiles.find(
				(file) => file.walletKind === walletKind,
			) ?? null
		);
	}

	private hasUploadFileForField(fieldName: GenericUploadFileField): boolean {
		return fieldName === 'healthFile'
			? Boolean(this.healthFile || this.persistedHealthFile)
			: Boolean(this.dentalFile || this.persistedDentalFile);
	}

	private getRemovedPersistedWalletKinds(): GenericBeneficiaryReconciliationWalletKind[] {
		return [
			...new Set(
				(this.preflightSummary?.files ?? [])
					.filter((file) =>
						this.hiddenPersistedOriginalFileKeys.has(
							this.getOriginalFileKey(file),
						),
					)
					.map((file) => file.walletKind)
					.filter(
						(
							walletKind,
						): walletKind is GenericBeneficiaryReconciliationWalletKind =>
							walletKind === 'HEALTH' || walletKind === 'DENTAL',
					),
			),
		];
	}

	private saveBlobLocally(blob: Blob | null, fileName: string): void {
		if (!blob) {
			this.snackBarService.showSnack(
				'Não foi possível baixar o arquivo original.',
				'error',
			);

			return;
		}

		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');

		link.href = url;
		link.download = fileName;
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(url);
	}

	private static isAcceptedReconciliationFile(
		file: File,
		acceptedExtensions: string[],
	): boolean {
		return acceptedExtensions.some((extension) =>
			file.name.toLowerCase().endsWith(extension),
		);
	}

	private getAcceptedReconciliationFiles(
		files: FileList,
		acceptedExtensions: string[],
	): File[] {
		const selectedFiles = Array.from(files).filter((file) =>
			BeneficiaryReconciliationComponent.isAcceptedReconciliationFile(
				file,
				acceptedExtensions,
			),
		);

		if (selectedFiles.length === 0 || selectedFiles.length !== files.length) {
			this.snackBarService.showSnack(
				BeneficiaryReconciliationComponent.getRejectedFileExtensionMessage(
					acceptedExtensions,
				),
				'error',
			);
		}

		return selectedFiles;
	}

	private removeInvalidStandardFilesForSelectedModel(): void {
		const acceptedExtensions = this.getAcceptedStandardFileExtensions();

		if (
			this.healthFile &&
			!BeneficiaryReconciliationComponent.isAcceptedReconciliationFile(
				this.healthFile,
				acceptedExtensions,
			)
		) {
			this.healthFile = null;
		}

		if (
			this.dentalFile &&
			!BeneficiaryReconciliationComponent.isAcceptedReconciliationFile(
				this.dentalFile,
				acceptedExtensions,
			)
		) {
			this.dentalFile = null;
		}
	}

	private static getRejectedFileExtensionMessage(
		acceptedExtensions: string[],
	): string {
		const extensionList = acceptedExtensions.join(' ou ');

		return `Apenas bases do cliente em ${extensionList} podem ser enviadas na reconciliação.`;
	}

	private openMappingModal(
		analysis: BeneficiaryReconciliationMappingAnalysisResponse,
	): void {
		const dialogRef = this.dialog.open(
			BeneficiaryReconciliationMappingModalComponent,
			{
				data: { analysis },
				maxWidth: '96vw',
				width: '1040px',
			},
		);

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(
				(result: BeneficiaryReconciliationMappingConfirmation | undefined) => {
					if (!result) {
						return;
					}

					this.confirmFlexibleMapping(result);
				},
			);
	}

	private confirmFlexibleMapping(
		mapping: BeneficiaryReconciliationMappingConfirmation,
	): void {
		if (
			!this.organizationId ||
			this.flexibleFiles.length === 0 ||
			this.isConfirmingMapping ||
			this.isComparing ||
			this.isProcessingReconciliation
		) {
			return;
		}

		this.isConfirmingMapping = true;

		this.beneficiaryReconciliationsService
			.confirmGenericMapping({
				files: this.flexibleFiles,
				mapping,
				organizationId: this.organizationId,
				scopeMode: this.effectiveScopeMode,
			})
			.pipe(
				finalize(() => {
					this.isConfirmingMapping = false;
				}),
				takeUntil(this.unsubscribe$),
			)
			.subscribe({
				error: (error) => {
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Erro ao confirmar o mapeamento das bases.',
						),
						'error',
					);
				},
				next: (response) => {
					this.confirmedMapping = mapping;
					this.handleGenericPreflightCreated(response);
					this.confirmedMapping = null;
					this.mappingAnalysis = null;
				},
			});
	}

	private normalizeBackendText(value: unknown): string {
		const rawValue = this.getStringValue(value);

		if (!rawValue) {
			return '';
		}

		return rawValue
			.replace(/eleg(?:\u00EF\u00BF\u00BD|\uFFFD)veis/gi, 'elegíveis')
			.replace(/altera(?:\u00EF\u00BF\u00BD|\uFFFD|\?\?)es/gi, 'alterações')
			.replace(/s(?:\u00EF\u00BF\u00BD|\uFFFD|\?)o/gi, 'são')
			.replace(/n\u00C3\u00A3o/gi, 'n\u00E3o')
			.replace(/organiza\u00C3\u00A7\u00C3\u00A3o/gi, 'organiza\u00E7\u00E3o')
			.replace(/revis\u00C3\u00A3o/gi, 'revis\u00E3o')
			.replace(/obrigat\u00C3\u00B3rio/gi, 'obrigat\u00F3rio')
			.replace(/benefici\u00C3\u00A1rio/gi, 'benefici\u00E1rio')
			.replace(/altera\u00C3\u00A7\u00C3\u00A3o/gi, 'altera\u00E7\u00E3o')
			.replace(/compara\u00C3\u00A7\u00C3\u00A3o/gi, 'compara\u00E7\u00E3o')
			.replace(/sess\u00C3\u00A3o/gi, 'sess\u00E3o')
			.replace(/\u00C3\u00A3/g, '\u00E3')
			.replace(/\u00C3\u00A1/g, '\u00E1')
			.replace(/\u00C3\u00A2/g, '\u00E2')
			.replace(/\u00C3\u00A9/g, '\u00E9')
			.replace(/\u00C3\u00AA/g, '\u00EA')
			.replace(/\u00C3\u00AD/g, '\u00ED')
			.replace(/\u00C3\u00B3/g, '\u00F3')
			.replace(/\u00C3\u00B4/g, '\u00F4')
			.replace(/\u00C3\u00BA/g, '\u00FA')
			.replace(/\u00C3\u00A7/g, '\u00E7')
			.replace(/\u00E2\u20AC\u00A2/g, '\u2022');
	}

	private normalizeSearchText(value: unknown): string {
		return this.getStringValue(value)
			.toLowerCase()
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '');
	}

	private onlyDigits(value: unknown): string {
		return this.getStringValue(value).replace(/\D/g, '');
	}

	private getSearchQueryValue(): string {
		const search = this.getStringValue(this.filterForm.get('search')?.value);
		const digits = this.onlyDigits(search);

		return digits.length >= 3 ? digits : search;
	}

	private shouldFocusTitularityHolderCpf(
		item: BeneficiaryReconciliationItem,
	): boolean {
		if (
			item.field !== 'TITULARITY_MISMATCH' ||
			item.status !== BeneficiaryReconciliationItemStatus.FAILED
		) {
			return false;
		}

		const normalizedError = this.normalizeBackendText(item.errorMessage)
			.toLowerCase()
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '');

		return (
			normalizedError.includes('cpf do titular') ||
			(normalizedError.includes('titular') &&
				normalizedError.includes('beneficiario')) ||
			normalizedError.includes('mesma organização') ||
			normalizedError.includes('obrigatorio')
		);
	}

	private getSubjectBeneficiary(
		item: BeneficiaryReconciliationItem,
	): Record<string, unknown> {
		const context = (item.context ?? {}) as Record<string, unknown>;
		const subject = context.subjectBeneficiary;

		return subject && typeof subject === 'object'
			? (subject as Record<string, unknown>)
			: {};
	}

	private getSuggestedPendingChange(
		item: BeneficiaryReconciliationItem,
	): PendingItemChange | null {
		if (item.status !== BeneficiaryReconciliationItemStatus.PENDING) {
			return null;
		}

		const context = (item.context ?? {}) as Record<string, unknown>;

		if (this.resolveManualReviewRequired(context)) {
			return null;
		}

		const action = this.parseApplyAction(
			context.suggestedAction ?? item.suggestedAction,
		);

		if (!action) {
			return null;
		}

		const suggestedTargetValue =
			(context.suggestedTargetValue as
				| BeneficiaryReconciliationApplyTargetValue
				| null
				| undefined) ?? {};
		const actionLabel = this.getStringValue(context.actionLabel) || undefined;
		const targetValue =
			action === 'IGNORE_ITEM' && actionLabel?.toLowerCase() === 'em espera'
				? {}
				: suggestedTargetValue;

		return {
			action,
			actionLabel,
			field: item.field,
			itemId: item.id,
			targetValue: this.sanitizeApplyTargetValue(targetValue ?? {}),
		};
	}

	private hasMissingRequiredFields(
		item: BeneficiaryReconciliationItem,
	): boolean {
		const context = (item.context ?? {}) as Record<string, unknown>;
		const missingRequiredFields = Array.isArray(context.missingRequiredFields)
			? context.missingRequiredFields
			: [];

		return missingRequiredFields.length > 0;
	}

	private isActiveMissingOnHold(item: BeneficiaryReconciliationItem): boolean {
		if (item.field !== 'ACTIVE_MISSING_IN_DB') {
			return false;
		}

		const pendingChange = this.pendingChanges.get(item.id);

		if (pendingChange) {
			return this.isPendingChangeOnHold(pendingChange);
		}

		const context = (item.context ?? {}) as Record<string, unknown>;
		const holdState = this.getStringValue(context.holdState);

		return this.getBooleanValue(context.onHold) || holdState === 'ON_HOLD';
	}

	private isQueuedApplyEligibleItem(
		item: BeneficiaryReconciliationItem,
	): boolean {
		if (item.status !== BeneficiaryReconciliationItemStatus.PENDING) {
			return false;
		}

		const pendingChange = this.pendingChanges.get(item.id);

		if (pendingChange) {
			if (this.isPendingChangeOnHold(pendingChange)) {
				return false;
			}

			const action = this.parseApplyAction(pendingChange.action);

			return Boolean(
				action &&
					(action !== 'IGNORE_ITEM' ||
						this.isKeepPlatformPendingChange(pendingChange)) &&
					this.buildApplyRequestFromChange(pendingChange),
			);
		}

		if (this.requiresManualReview(item) || this.isItemOnHold(item)) {
			return false;
		}

		const effectiveChange = this.getSuggestedPendingChange(item);

		if (!effectiveChange) {
			return false;
		}

		const action = this.parseApplyAction(effectiveChange.action);

		return Boolean(
			action &&
				(action !== 'IGNORE_ITEM' ||
					this.isKeepPlatformPendingChange(effectiveChange)) &&
				this.buildApplyRequestFromChange(effectiveChange),
		);
	}

	private buildBulkCardActionRequest(
		field: BeneficiaryReconciliationField,
	): BeneficiaryReconciliationBulkCardActionRequest {
		const pendingChanges = Array.from(this.pendingChanges.values()).reduce<
			NonNullable<
				BeneficiaryReconciliationBulkCardActionRequest['pendingChanges']
			>
		>((changes, change) => {
			const action = this.parseApplyAction(change.action);

			if (
				!action ||
				change.preflightKind ||
				(change.field && change.field !== field)
			) {
				return changes;
			}

			changes.push({
				action,
				actionLabel: change.actionLabel,
				field: change.field,
				itemId: change.itemId,
				targetValue: this.sanitizeApplyTargetValue(change.targetValue),
			});

			return changes;
		}, []);

		return {
			field,
			pendingChanges,
			...this.getSessionScopeModeRequest(),
		};
	}

	private refreshBulkActionAvailability(): void {
		const comparisonId = this.comparisonId;
		const field = this.currentApplyField;
		const organizationId = this.organizationId;

		if (
			!comparisonId ||
			!organizationId ||
			!field ||
			(this.isTopCardActive && this.activeCardKey !== 'DUPLICATE_IN_FILE')
		) {
			this.resetBulkActionAvailabilityRequest();

			return;
		}

		const payload = this.buildBulkCardActionRequest(field);
		const payloadSignature = JSON.stringify(payload);
		const requestSignature = JSON.stringify({
			activeCardKey: this.activeCardKey,
			comparisonId,
			organizationId,
			page: this.itemsCurrentPage,
			payload,
			search: this.getStringValue(this.filterForm.get('search')?.value),
			status: this.getStringValue(this.filterForm.get('status')?.value),
		});

		if (
			this.bulkActionAvailabilityRequestSubscription &&
			this.bulkActionAvailabilityRequestSignature === requestSignature
		) {
			return;
		}

		const requestId = ++this.latestBulkActionAvailabilityRequestId;

		this.bulkActionAvailabilityRequestSubscription?.unsubscribe();
		this.bulkActionAvailabilityRequestSignature = requestSignature;
		this.bulkActionAvailabilityRequestSubscription =
			this.beneficiaryReconciliationsService
				.getGenericBulkCardActionAvailability({
					organizationId,
					payload,
					sessionId: comparisonId,
				})
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe({
					error: () => {
						this.handleBulkActionAvailabilityError(requestId, requestSignature);
					},
					next: (availability) => {
						this.handleBulkActionAvailabilityResponse(availability, {
							comparisonId,
							field,
							organizationId,
							payloadSignature,
							requestId,
						});
					},
				});
	}

	private resetBulkActionAvailabilityRequest(): void {
		this.latestBulkActionAvailabilityRequestId += 1;
		this.bulkActionAvailabilityRequestSubscription?.unsubscribe();
		this.bulkActionAvailabilityRequestSubscription = null;
		this.bulkActionAvailabilityRequestSignature = null;
		this.bulkCardActionAvailability = {
			failedCount: 0,
			pendingHoldCount: 0,
			queuedCount: 0,
		};
	}

	private handleBulkActionAvailabilityError(
		requestId: number,
		requestSignature: string,
	): void {
		if (
			requestId !== this.latestBulkActionAvailabilityRequestId ||
			this.bulkActionAvailabilityRequestSignature !== requestSignature
		) {
			return;
		}

		this.bulkActionAvailabilityRequestSignature = null;
		this.bulkActionAvailabilityRequestSubscription = null;
		this.bulkCardActionAvailability = {
			failedCount: 0,
			pendingHoldCount: 0,
			queuedCount: 0,
		};
	}

	private handleBulkActionAvailabilityResponse(
		availability: BulkCardActionAvailability,
		request: {
			comparisonId: string;
			field: BeneficiaryReconciliationField;
			organizationId: string;
			payloadSignature: string;
			requestId: number;
		},
	): void {
		if (!this.isBulkActionAvailabilityRequestCurrent(request)) {
			return;
		}

		this.bulkActionAvailabilityRequestSignature = null;
		this.bulkActionAvailabilityRequestSubscription = null;
		this.bulkCardActionAvailability = availability;
	}

	private isBulkActionAvailabilityRequestCurrent(request: {
		comparisonId: string;
		field: BeneficiaryReconciliationField;
		organizationId: string;
		payloadSignature: string;
		requestId: number;
	}): boolean {
		return (
			request.requestId === this.latestBulkActionAvailabilityRequestId &&
			this.comparisonId === request.comparisonId &&
			this.currentApplyField === request.field &&
			this.organizationId === request.organizationId &&
			JSON.stringify(this.buildBulkCardActionRequest(request.field)) ===
				request.payloadSignature
		);
	}

	private getUncomparedPreflightStatusFilter(): GenericBeneficiaryReconciliationPreflightListStatus {
		const status = this.normalizeStatusFilter(
			this.getStringValue(this.filterForm.get('status')?.value),
		);

		switch (status) {
			case BeneficiaryReconciliationItemStatusFilter.APPLIED:
				return 'APPLIED';
			case BeneficiaryReconciliationItemStatusFilter.MANUAL_REVIEW_REQUIRED:
				return 'PENDING';
			case BeneficiaryReconciliationItemStatusFilter.PENDING:
				return 'ON_HOLD';
			case BeneficiaryReconciliationItemStatusFilter.IGNORED:
				return 'IGNORED';
			default:
				return 'ALL';
		}
	}

	private hydratePendingChanges(comparisonId: string | null): void {
		this.pendingChanges.clear();

		if (!comparisonId) {
			return;
		}

		if (!this.organizationId) {
			this.hydratePendingChangesFromSessionStorage(comparisonId);

			return;
		}

		this.beneficiaryReconciliationsService
			.getGenericDraft({
				organizationId: this.organizationId,
				sessionId: comparisonId,
			})
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: () => {
					if (this.comparisonId !== comparisonId) {
						return;
					}

					this.hydratePendingChangesFromSessionStorage(comparisonId);
				},
				next: (draft) => {
					if (this.comparisonId !== comparisonId) {
						return;
					}

					if (!this.hydratePendingChangesFromDraft(draft)) {
						this.hydratePendingChangesFromSessionStorage(comparisonId);
					}
				},
			});
	}

	private hydratePendingChangesForRestoredSession(
		sessionId: string,
		draft?: BeneficiaryReconciliationDraft | null,
	): void {
		if (draft === undefined) {
			this.hydratePendingChanges(sessionId);

			return;
		}

		this.pendingChanges.clear();

		if (!this.hydratePendingChangesFromDraft(draft)) {
			this.hydratePendingChangesFromSessionStorage(sessionId);
		}
	}

	private hydratePendingChangesFromDraft(
		draft: BeneficiaryReconciliationDraft | null,
	): boolean {
		if (!draft || !Array.isArray(draft.pendingChanges)) {
			return false;
		}

		this.pendingChanges.clear();

		return this.hydratePendingChangesFromEntries(draft.pendingChanges);
	}

	private hydratePendingChangesFromSessionStorage(comparisonId: string): void {
		try {
			const rawValue = sessionStorage.getItem(
				this.getPendingStorageKey(comparisonId),
			);

			if (!rawValue) {
				return;
			}

			const parsedValue = JSON.parse(rawValue);

			if (!this.hydratePendingChangesFromEntries(parsedValue)) {
				sessionStorage.removeItem(this.getPendingStorageKey(comparisonId));
			}
		} catch (error) {
			sessionStorage.removeItem(this.getPendingStorageKey(comparisonId));
		}
	}

	private hydratePendingChangesFromEntries(entries: unknown): boolean {
		if (!Array.isArray(entries)) {
			return false;
		}

		let hasHydratedEntry = false;

		for (const entry of entries) {
			if (entry && typeof entry === 'object') {
				const itemId = this.getStringValue(
					(entry as { itemId?: unknown }).itemId,
				);
				const action = this.parsePendingChangeAction(
					(entry as { action?: unknown }).action,
				);
				const field = this.parseReconciliationField(
					(entry as { field?: unknown }).field,
				);
				const preflightKind = this.parsePreflightKind(
					(entry as { preflightKind?: unknown }).preflightKind,
				);

				if (itemId && action) {
					this.pendingChanges.set(itemId, {
						action,
						actionLabel:
							this.getStringValue(
								(entry as { actionLabel?: unknown }).actionLabel,
							) || undefined,
						field: field ?? undefined,
						itemId,
						preflightKind: preflightKind ?? undefined,
						targetValue: this.sanitizeApplyTargetValue(
							(
								entry as {
									targetValue?: BeneficiaryReconciliationApplyTargetValue;
								}
							).targetValue,
						),
					});
					hasHydratedEntry = true;
				}
			}
		}

		return hasHydratedEntry;
	}

	private loadItems(options?: { refreshPendingTotal?: boolean }): void {
		const requestState = this.getCurrentItemsRequestState();

		if (!requestState) {
			return;
		}

		const requestId = this.latestItemsRequestId + 1;
		const requestSignature = this.buildItemsRequestSignature(requestState);

		if (
			this.itemsRequestSubscription &&
			this.itemsRequestSignature === requestSignature
		) {
			return;
		}

		this.itemsRequestSubscription?.unsubscribe();
		this.latestItemsRequestId = requestId;
		this.itemsRequestSignature = requestSignature;
		this.isLoadingItems = true;
		this.itemsRequestSubscription = this.beneficiaryReconciliationsService
			.listGenericItems(requestState)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: (error) => {
					if (!this.isCurrentItemsRequest(requestId, requestSignature)) {
						return;
					}

					this.isLoadingItems = false;
					this.itemsRequestSignature = null;
					this.itemsRequestSubscription = null;
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Erro ao carregar os itens da reconciliação.',
						),
						'error',
					);
				},
				next: (response) => {
					if (!this.isCurrentItemsRequest(requestId, requestSignature)) {
						return;
					}

					this.isLoadingItems = false;
					this.itemsRequestSignature = null;
					this.itemsRequestSubscription = null;
					this.items = response.items;
					this.pruneManualReviewHighlights(response.items);
					this.primePendingChangesFromItems(response.items);
					this.itemsCount = response.total;
					this.itemsCurrentPage = response.page;
					this.itemsTotalPages = response.totalPages;
					this.summary = response.summary;
					this.refreshBulkActionAvailability();
					if (options?.refreshPendingTotal) {
						void this.refreshSessionPendingTotal();
					}
				},
			});
	}

	private loadSessionStateOrItems(
		selectedSession?: BeneficiaryReconciliationSessionListItem | null,
	): void {
		if (!this.comparisonId || !this.organizationId) {
			return;
		}

		if (selectedSession?.id === this.comparisonId) {
			this.loadSessionStateFromSession(selectedSession);

			return;
		}

		this.beneficiaryReconciliationsService
			.listGenericSessions({
				limit: 1,
				organizationId: this.organizationId,
				page: 1,
				sessionId: this.comparisonId,
			})
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: () => {
					this.loadItems();
				},
				next: (response) => {
					const session = response.sessions.find(
						(item) => item.id === this.comparisonId,
					);

					if (session) {
						this.loadSessionStateFromSession(session);

						return;
					}

					this.loadPreflightRows();
					this.loadItems();
				},
			});
	}

	private loadSessionStateFromSession(
		session: BeneficiaryReconciliationSessionListItem,
	): void {
		this.loadedSessionAppliedCount = session.appliedCount ?? 0;
		const preflight = this.getSessionPreflight(session);

		if (preflight && this.isPreflightOnlySession(session)) {
			this.items = [];
			this.itemsCount = 0;
			this.itemsTotalPages = 0;
			this.summary = EMPTY_BENEFICIARY_RECONCILIATION_SUMMARY;
			this.updatePreflightAlerts(preflight);
			this.loadPreflightRows();

			return;
		}

		if (preflight) {
			this.updatePreflightAlerts(preflight);
			this.loadPreflightRows();
		}

		this.loadItems();
	}

	private loadSessions(page: number): void {
		if (!this.organizationId) {
			return;
		}

		this.isLoadingSessions = true;
		this.sessionsCurrentPage = page;
		this.beneficiaryReconciliationsService
			.listGenericSessions({
				limit: this.sessionLimit,
				organizationId: this.organizationId,
				page,
			})
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				error: (error) => {
					this.isLoadingSessions = false;
					this.snackBarService.showSnack(
						this.getNormalizedBackendMessage(
							error,
							'Erro ao carregar as comparações recentes.',
						),
						'error',
					);
				},
				next: (response) => {
					this.isLoadingSessions = false;
					this.recentSessions = response.sessions;
					this.sessionsCount = response.total;
					this.sessionsCurrentPage = response.page;
					this.sessionsTotalPages = response.totalPages;
				},
			});
	}

	private async openApplyPreviewWithAudit(
		comparisonId: string,
		fields?: BeneficiaryReconciliationField[],
	): Promise<void> {
		this.isApplying = true;

		try {
			const audit = await this.buildPendingSessionAudit(comparisonId, fields);

			this.isApplying = false;
			this.sessionPendingTotal = audit.total;

			if (audit.manualUnreviewedCount > 0) {
				this.focusManualReviewItems(audit.manualUnreviewedItemIds);
				this.snackBarService.showSnack(
					`Revise ${audit.manualUnreviewedCount} caso(s) sensível(is) antes de confirmar tudo.`,
					'info',
				);

				return;
			}

			const confirmableTotal =
				audit.autoSuggestedCount + audit.manualReviewedCount;

			if (confirmableTotal === 0) {
				this.snackBarService.showSnack(
					'Não há alterações prontas para confirmar nesta sessão.',
					'info',
				);

				return;
			}

			const dialogRef = this.dialog.open(
				BeneficiaryReconciliationApplyPreviewModalComponent,
				{
					data: {
						autoSuggestedCount: audit.autoSuggestedCount,
						manualReviewedCount: audit.manualReviewedCount,
						summaries: audit.summaries,
						total: confirmableTotal,
					},
					maxWidth: undefined,
					width: '560px',
				},
			);

			dialogRef
				.afterClosed()
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((confirmed: boolean | undefined) => {
					if (!confirmed) {
						return;
					}

					this.applyPendingChanges(fields);
				});
		} catch (error) {
			this.isApplying = false;
			this.snackBarService.showSnack(
				'Erro ao validar os itens pendentes da sessão.',
				'error',
			);
		}
	}

	private openInlineResolution(item: BeneficiaryReconciliationItem): void {
		const pendingChange = this.pendingChanges.get(item.id) ?? null;
		const isDuplicateInFile = item.field === 'DUPLICATE_IN_FILE';
		const dialogRef = this.dialog.open(
			BeneficiaryReconciliationInlineResolutionModalComponent,
			{
				data: {
					item,
					pendingChange,
				},
				maxWidth: '96vw',
				width: isDuplicateInFile ? '1040px' : '720px',
			},
		);

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(
				(
					result: BeneficiaryReconciliationInlineResolutionResult | undefined,
				) => {
					if (!result) {
						return;
					}

					if ('removePending' in result) {
						this.removePendingChange(item.id);

						return;
					}

					if (result.definitiveIgnore) {
						void this.confirmAndIgnoreItemDefinitively(item);

						return;
					}

					const action = this.parseApplyAction(result.action);

					if (!action) {
						this.snackBarService.showSnack(
							'Selecione uma ação válida antes de salvar na fila.',
							'error',
						);

						return;
					}

					const targetValue =
						action === 'IGNORE_ITEM' && !result.onHold
							? {
									...result.targetValue,
									resolutionMode: RECONCILIATION_RESOLUTION_MODE_KEEP_PLATFORM,
								}
							: result.targetValue;

					this.setPendingChange({
						action,
						actionLabel:
							action === 'IGNORE_ITEM' && !result.onHold
								? 'Plataforma mantida'
								: this.resolvePendingActionLabel(item, action),
						field: item.field,
						itemId: item.id,
						targetValue,
					});

					if (result.applyOnly && action !== 'IGNORE_ITEM') {
						this.onApplySingleItem(item);
					}
				},
			);
	}

	private openTitularityResolution(item: BeneficiaryReconciliationItem): void {
		const dialogRef = this.dialog.open(
			BeneficiaryReconciliationTitularityResolutionModalComponent,
			{
				data: {
					action: this.pendingChanges.get(item.id)?.action ?? null,
					focusHolderCpf: this.shouldFocusTitularityHolderCpf(item),
					item,
					targetValue: this.pendingChanges.get(item.id)?.targetValue ?? null,
				},
				maxWidth: undefined,
				width: '620px',
			},
		);

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(
				(
					result:
						| BeneficiaryReconciliationTitularityResolutionResult
						| undefined,
				) => {
					if (!result) {
						return;
					}

					if ('removePending' in result) {
						this.removePendingChange(item.id);

						return;
					}

					const targetValue =
						result.action === 'IGNORE_ITEM' && !result.onHold
							? {
									...result.targetValue,
									resolutionMode: RECONCILIATION_RESOLUTION_MODE_KEEP_PLATFORM,
								}
							: result.targetValue;

					this.setPendingChange({
						action: result.action,
						actionLabel:
							result.action === 'IGNORE_ITEM' && !result.onHold
								? 'Plataforma mantida'
								: this.resolvePendingActionLabel(item, result.action),
						field: item.field,
						itemId: item.id,
						targetValue,
					});

					if (result.applyOnly && result.action !== 'IGNORE_ITEM') {
						this.onApplySingleItem(item);
					}
				},
			);
	}

	private openValueResolution(item: BeneficiaryReconciliationItem): void {
		const dialogRef = this.dialog.open(
			BeneficiaryReconciliationValueResolutionModalComponent,
			{
				data: {
					action: this.pendingChanges.get(item.id)?.action ?? null,
					item,
					targetValue: this.pendingChanges.get(item.id)?.targetValue ?? null,
				},
				maxWidth: undefined,
				width: '620px',
			},
		);

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(
				(
					result: BeneficiaryReconciliationValueResolutionResult | undefined,
				) => {
					if (!result) {
						return;
					}

					if ('removePending' in result) {
						this.removePendingChange(item.id);

						return;
					}

					const targetValue =
						result.mode === 'DATABASE' && !result.onHold
							? {
									...result.targetValue,
									resolutionMode: RECONCILIATION_RESOLUTION_MODE_KEEP_PLATFORM,
								}
							: result.targetValue;
					const action: BeneficiaryReconciliationApplyAction =
						result.mode === 'DATABASE'
							? 'IGNORE_ITEM'
							: targetValue.dentalNotApplicable === true ||
								  item.field === 'PLAN_MISMATCH'
								? 'UPDATE_PLAN'
								: 'UPDATE_CARD';

					this.setPendingChange({
						action,
						actionLabel:
							result.mode === 'DATABASE' && !result.onHold
								? 'Plataforma mantida'
								: this.resolvePendingActionLabel(item, action),
						field: item.field,
						itemId: item.id,
						targetValue,
					});

					if (result.applyOnly && action !== 'IGNORE_ITEM') {
						this.onApplySingleItem(item);
					}
				},
			);
	}

	private parseApplyAction(
		value: unknown,
	): BeneficiaryReconciliationApplyAction | null {
		const action = this.getStringValue(value);

		switch (action) {
			case 'CREATE_BENEFICIARY':
			case 'IGNORE_ITEM':
			case 'INACTIVATE_BENEFICIARY':
			case 'LINK_EXISTING_BENEFICIARY':
			case 'REACTIVATE_BENEFICIARY':
			case 'SELECT_DUPLICATE_ROW':
			case 'TRANSFER_ORGANIZATION':
			case 'UPDATE_CARD':
			case 'UPDATE_PLAN':
			case 'UPDATE_PLAN_AND_CARD':
			case 'UPDATE_TITULARITY':
				return action;
			default:
				return null;
		}
	}

	private parsePendingChangeAction(value: unknown): PendingChangeAction | null {
		const applyAction = this.parseApplyAction(value);

		if (applyAction) {
			return applyAction;
		}

		const action = this.getStringValue(value);

		switch (action) {
			case 'CREATE_SUB':
			case 'RESOLVE_PREFLIGHT_ROW':
			case 'RESUME_PREFLIGHT_ROW':
			case 'TREAT_AS_MATRIX':
				return action;
			default:
				return null;
		}
	}

	private parsePreflightKind(
		value: unknown,
	): GenericBeneficiaryReconciliationPreflightExportKind | null {
		const kind = this.getStringValue(value);

		switch (kind) {
			case 'UNCOMPARED_LINES':
			case 'UNIDENTIFIED_ORGANIZATIONS':
				return kind;
			default:
				return null;
		}
	}

	private parseReconciliationCategory(
		value: unknown,
	): ReconciliationCardKey | null {
		return (
			this.parseReconciliationField(value) ?? this.parsePreflightKind(value)
		);
	}

	private parseReconciliationField(
		value: unknown,
	): BeneficiaryReconciliationField | null {
		const field = this.getStringValue(value);

		return field in BENEFICIARY_RECONCILIATION_FIELD_METADATA
			? (field as BeneficiaryReconciliationField)
			: null;
	}

	private parsePositiveNumber(value: unknown, fallback: number): number {
		const parsedValue = Number(value);

		return Number.isInteger(parsedValue) && parsedValue > 0
			? parsedValue
			: fallback;
	}

	private parseQueryState(params: Params): QueryState {
		return {
			carrier: this.getStringValue(params.carrier),
			comparisonId: this.getStringValue(params.comparisonId) || null,
			field: this.getStringValue(params.field),
			limit: this.parsePositiveNumber(
				params.limit,
				AppConstants.pagination.itemsPerPage,
			),
			page: this.parsePositiveNumber(params.page, 1),
			search: this.getStringValue(params.search),
			status: this.normalizeStatusFilter(
				this.getStringValue(params.reviewState) ||
					this.getStringValue(params.status),
			),
		};
	}

	private persistPendingChanges(): void {
		if (!this.comparisonId) {
			return;
		}

		const storageKey = this.getPendingStorageKey(this.comparisonId);

		if (this.pendingChanges.size === 0) {
			sessionStorage.removeItem(storageKey);

			if (this.getCurrentActiveApplyProcessingDraft()) {
				this.remoteDraftSave$.next();

				return;
			}

			this.deletePendingChangesDraftRemote(this.comparisonId);

			return;
		}

		const serializedValue = this.serializePendingChanges();

		sessionStorage.setItem(storageKey, JSON.stringify(serializedValue));
		this.remoteDraftSave$.next();
	}

	private primePendingChangesFromItems(
		items: BeneficiaryReconciliationItem[],
	): void {
		let hasChanges = false;

		for (const item of items) {
			const existingChange = this.pendingChanges.get(item.id);

			if (
				existingChange &&
				(this.hasTerminalReconciliationStatus(item) ||
					this.isDuplicateResolvedByClientReturn(item))
			) {
				this.pendingChanges.delete(item.id);
				hasChanges = true;
			}

			const normalizedExistingChange = this.pendingChanges.get(item.id);

			if (normalizedExistingChange && !normalizedExistingChange.field) {
				this.pendingChanges.set(item.id, {
					...normalizedExistingChange,
					field: item.field,
				});
				hasChanges = true;
			}

			if (!this.pendingChanges.has(item.id)) {
				const suggestedChange = this.getSuggestedPendingChange(item);

				if (suggestedChange) {
					this.pendingChanges.set(item.id, suggestedChange);
					hasChanges = true;
				}
			}
		}

		if (hasChanges) {
			this.persistPendingChanges();
		}
	}

	private pruneManualReviewHighlights(
		loadedItems: BeneficiaryReconciliationItem[],
	): void {
		if (this.highlightedManualReviewItemIds.size === 0) {
			return;
		}

		const loadedItemIds = new Set(loadedItems.map((item) => item.id));

		this.highlightedManualReviewItemIds = new Set(
			Array.from(this.highlightedManualReviewItemIds).filter((itemId) =>
				loadedItemIds.has(itemId),
			),
		);
	}

	private async refreshSessionPendingTotal(): Promise<void> {
		if (!this.comparisonId || !this.organizationId) {
			this.sessionPendingTotal = 0;

			return;
		}

		try {
			const response = await firstValueFrom(
				this.beneficiaryReconciliationsService.listGenericItems({
					comparisonId: this.comparisonId,
					limit: 1,
					organizationId: this.organizationId,
					page: 1,
					status: BeneficiaryReconciliationItemStatus.PENDING,
				}),
			);
			this.sessionPendingTotal = response.total;
		} catch (error) {
			this.sessionPendingTotal = 0;
		}
	}

	private removePendingChange(itemId: string): void {
		if (this.isPendingChangeMutationBlocked) {
			return;
		}

		if (this.pendingChanges.delete(itemId)) {
			this.persistPendingChanges();
		}
	}

	private resetStatusFilterToAll(): void {
		this.itemsCurrentPage = 1;

		if (
			this.normalizeStatusFilter(
				this.getStringValue(this.filterForm.get('status')?.value),
			) === BeneficiaryReconciliationItemStatusFilter.ALL
		) {
			return;
		}

		this.filterForm.patchValue(
			{
				status: BeneficiaryReconciliationItemStatusFilter.ALL,
			},
			{ emitEvent: false },
		);
		this.syncQueryParams({
			page: 1,
		});
	}

	private requiresManualReview(item: BeneficiaryReconciliationItem): boolean {
		if (item.status !== BeneficiaryReconciliationItemStatus.PENDING) {
			return false;
		}

		const context = (item.context ?? {}) as Record<string, unknown>;

		return this.resolveManualReviewRequired(context);
	}

	private resolveManualReviewRequired(
		context: Record<string, unknown>,
	): boolean {
		return (
			this.getBooleanValue(context.manualReviewRequired) ||
			this.getBooleanValue(context.requiresManualReview) ||
			this.getBooleanValue(context.requires_manual_review)
		);
	}

	private resolveResolved(context: Record<string, unknown>): boolean {
		return this.getBooleanValue(context.resolved);
	}

	private resolvePendingActionLabel(
		item: BeneficiaryReconciliationItem,
		action: BeneficiaryReconciliationApplyAction,
	): string {
		if (action === 'IGNORE_ITEM') {
			return 'Em espera';
		}

		if (
			item.field === 'DUPLICATE_IN_FILE' &&
			action === 'SELECT_DUPLICATE_ROW'
		) {
			return 'Linha selecionada';
		}

		return this.getApplyActionLabel(action);
	}

	private isPendingChangeOnHold(
		pendingChange: PendingItemChange | undefined,
	): boolean {
		if (!pendingChange || pendingChange.action !== 'IGNORE_ITEM') {
			return false;
		}

		return (
			this.getStringValue(pendingChange.targetValue?.resolutionMode) !==
			RECONCILIATION_RESOLUTION_MODE_KEEP_PLATFORM
		);
	}

	private isKeepPlatformPendingChange(
		pendingChange: PendingItemChange | undefined,
	): boolean {
		return (
			pendingChange?.action === 'IGNORE_ITEM' &&
			this.getStringValue(pendingChange.targetValue?.resolutionMode) ===
				RECONCILIATION_RESOLUTION_MODE_KEEP_PLATFORM
		);
	}

	isItemOnHold(item: BeneficiaryReconciliationItem): boolean {
		if (this.isItemDefinitivelyIgnored(item)) {
			return false;
		}

		const pendingChange = this.pendingChanges.get(item.id);

		if (pendingChange) {
			return this.isPendingChangeOnHold(pendingChange);
		}

		if (this.isDuplicateOnHold(item) || this.isActiveMissingOnHold(item)) {
			return true;
		}

		const context = (item.context ?? {}) as Record<string, unknown>;
		const holdState = this.getStringValue(context.holdState);

		const suggestedChange = this.getSuggestedPendingChange(item);

		return (
			this.getBooleanValue(context.onHold) ||
			holdState === 'ON_HOLD' ||
			this.isPendingChangeOnHold(suggestedChange ?? undefined)
		);
	}

	isDuplicateOnHold(item: BeneficiaryReconciliationItem): boolean {
		if (item.field !== 'DUPLICATE_IN_FILE') {
			return false;
		}

		if (this.isItemDefinitivelyIgnored(item)) {
			return false;
		}

		if (this.isDuplicateResolvedByClientReturn(item)) {
			return false;
		}

		const pendingChange = this.pendingChanges.get(item.id);

		if (pendingChange) {
			return this.isPendingChangeOnHold(pendingChange);
		}

		const context = (item.context ?? {}) as Record<string, unknown>;
		const holdState = this.getStringValue(context.holdState);

		return this.getBooleanValue(context.onHold) || holdState === 'ON_HOLD';
	}

	private isItemDefinitivelyIgnored(
		item: BeneficiaryReconciliationItem,
	): boolean {
		const context = (item.context ?? {}) as Record<string, unknown>;
		const resolution =
			context.resolution && typeof context.resolution === 'object'
				? (context.resolution as Record<string, unknown>)
				: null;
		const action = this.parseApplyAction(resolution?.action);

		return (
			item.status === BeneficiaryReconciliationItemStatus.APPLIED &&
			action === 'IGNORE_ITEM' &&
			(this.getStringValue(context.actionLabel) === 'Ignorado' ||
				this.getStringValue(
					(resolution?.payload as BeneficiaryReconciliationApplyTargetValue)
						?.operationalAction,
				) === RECONCILIATION_OPERATIONAL_ACTION_IGNORE)
		);
	}

	private isDuplicateResolvedByClientReturn(
		item: BeneficiaryReconciliationItem,
	): boolean {
		if (item.field !== 'DUPLICATE_IN_FILE') {
			return false;
		}

		const context = (item.context ?? {}) as Record<string, unknown>;
		const resolution =
			context.resolution && typeof context.resolution === 'object'
				? (context.resolution as Record<string, unknown>)
				: null;

		return (
			this.getBooleanValue(context.clientReturnResolved) &&
			(this.parseApplyAction(context.suggestedAction) ===
				'SELECT_DUPLICATE_ROW' ||
				this.parseApplyAction(resolution?.action) === 'SELECT_DUPLICATE_ROW')
		);
	}

	private sanitizeApplyTargetValue(
		value: BeneficiaryReconciliationApplyTargetValue | null | undefined,
	): BeneficiaryReconciliationApplyTargetValue {
		if (!value || typeof value !== 'object') {
			return {};
		}

		const sanitizedValue: BeneficiaryReconciliationApplyTargetValue = {};

		if (this.getStringValue(value.beneficiaryId)) {
			sanitizedValue.beneficiaryId = this.getStringValue(value.beneficiaryId);
		}

		if (this.getStringValue(value.birthDay)) {
			sanitizedValue.birthDay = this.getStringValue(value.birthDay);
		}

		if (this.getStringValue(value.cardNumber)) {
			sanitizedValue.cardNumber = this.getStringValue(value.cardNumber);
		}

		if (typeof value.cascadeDependents === 'boolean') {
			sanitizedValue.cascadeDependents = value.cascadeDependents;
		}

		if (this.getStringValue(value.carrier)) {
			sanitizedValue.carrier = this.getStringValue(value.carrier);
		}

		if (this.getStringValue(value.cnpj)) {
			sanitizedValue.cnpj = this.getStringValue(value.cnpj);
		}

		if (this.getStringValue(value.cpf)) {
			sanitizedValue.cpf = this.getStringValue(value.cpf);
		}

		if (this.getStringValue(value.email)) {
			sanitizedValue.email = this.getStringValue(value.email);
		}

		if (this.getStringValue(value.holderCpf)) {
			sanitizedValue.holderCpf = this.getStringValue(value.holderCpf);
		}

		if (this.getStringValue(value.name)) {
			sanitizedValue.name = this.getStringValue(value.name);
		}

		if (this.getStringValue(value.operationalAction)) {
			sanitizedValue.operationalAction = this.getStringValue(
				value.operationalAction,
			);
		}

		if (this.getStringValue(value.organizationId)) {
			sanitizedValue.organizationId = this.getStringValue(value.organizationId);
		}

		if (this.getStringValue(value.planName)) {
			sanitizedValue.planName = this.getStringValue(value.planName);
		}

		if (this.getStringValue(value.resolutionMode)) {
			sanitizedValue.resolutionMode = this.getStringValue(value.resolutionMode);
		}

		if (Array.isArray(value.rowIds)) {
			const rowIds = value.rowIds
				.map((rowId) => this.getStringValue(rowId))
				.filter((rowId) => Boolean(rowId));

			if (rowIds.length > 0) {
				sanitizedValue.rowIds = rowIds;
			}
		}

		if (
			typeof value.rowNumber === 'number' &&
			Number.isFinite(value.rowNumber)
		) {
			sanitizedValue.rowNumber = value.rowNumber;
		}

		if (this.getStringValue(value.sheetName)) {
			sanitizedValue.sheetName = this.getStringValue(value.sheetName);
		}

		if (this.getStringValue(value.sourceFileName)) {
			sanitizedValue.sourceFileName = this.getStringValue(value.sourceFileName);
		}

		if (value.typeKinship) {
			sanitizedValue.typeKinship = value.typeKinship;
		}

		if (typeof value.dentalNotApplicable === 'boolean') {
			sanitizedValue.dentalNotApplicable = value.dentalNotApplicable;
		}

		return sanitizedValue;
	}

	private setPendingChange(change: PendingItemChange): void {
		if (this.isPendingChangeMutationBlocked) {
			return;
		}

		this.pendingChanges.set(change.itemId, {
			...change,
			targetValue: this.sanitizeApplyTargetValue(change.targetValue),
		});
		this.persistPendingChanges();
	}

	private syncQueryParams(args: {
		comparisonId?: string | null;
		page?: number;
	}): void {
		const comparisonId =
			args.comparisonId === undefined ? this.comparisonId : args.comparisonId;
		const page = args.page ?? this.itemsCurrentPage;
		const queryParams: Params = {};

		if (comparisonId) {
			queryParams.comparisonId = comparisonId;
		}

		if (page > 1) {
			queryParams.page = page;
		}

		if (this.getLimit() !== AppConstants.pagination.itemsPerPage) {
			queryParams.limit = this.getLimit();
		}

		const field = this.getStringValue(this.filterForm.get('field')?.value);
		const status = this.normalizeStatusFilter(
			this.getStringValue(this.filterForm.get('status')?.value),
		);
		const statusFilterParams = this.getStatusFilterParams(status);
		const carrier = this.getStringValue(this.filterForm.get('carrier')?.value);
		const search = this.getSearchQueryValue();

		if (field) {
			queryParams.field = field;
		}

		if (statusFilterParams.status) {
			queryParams.status = statusFilterParams.status;
		}

		if (statusFilterParams.reviewState) {
			queryParams.reviewState = statusFilterParams.reviewState;
		}

		if (carrier) {
			queryParams.carrier = carrier;
		}

		if (search) {
			queryParams.search = search;
		}

		void this.router.navigate([], {
			queryParams,
			queryParamsHandling: 'replace',
			relativeTo: this.activatedRoute,
			replaceUrl: true,
		});
	}
}
